import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import '@tensorflow/tfjs';
import './App.css';
import { createTheme } from 'react-data-table-component';
import { v4 as uuidv4 } from 'uuid';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Power, SkipBack, SkipForward, LogOut, Volume2, VolumeX, Play, Pause, Shuffle, ThumbsUp, ThumbsDown } from "lucide-react";
import { supabase, isSupabaseConfigured } from './supabaseClient';
import TrackFeedback from './components/TrackFeedback';
import SessionFeedback from './components/SessionFeedback';
import EmailGate from './components/EmailGate';
import { dispatchAction } from './lib/actionDispatcher';
import { isFrameTooDark } from './lib/frameAnalysis';
import { ensurePlaybackStarted } from './lib/ensurePlaybackStarted';
import { useSpotifyCredentials } from './hooks/useSpotifyCredentials';

// DJ-133 — record an "error" event row in users_actions so a session that
// never produced a successful classification (camera failure, classifier
// failure, network failure) still shows up in the dj-stats timeline. Mirrors
// the playback_events heartbeat pattern: frontend writes directly through the
// publishable Supabase key. Best-effort; failures only log.
async function recordSessionError({ userEmail, sessionId, errorType, errorMessage, cameraLabel }) {
  if (!supabase || !userEmail) return;
  try {
    await supabase.from('users_actions').insert({
      user_email: userEmail,
      timestamp: new Date().toISOString(),
      event_type: 'error',
      error_type: errorType,
      error_message: errorMessage ? String(errorMessage).slice(0, 500) : null,
      session_id: sessionId ?? null,
      camera_label: cameraLabel ?? null,
    });
  } catch (err) {
    console.warn('[recordSessionError] insert failed:', err?.message || err);
  }
}

// DJ-133 — translate an axios-style error into an error_type + error_message
// suitable for users_actions. Used by every /process-image-to-songs catch.
function classifyClientError(error) {
  if (!error) {
    return { errorType: 'request_failed_network', errorMessage: 'unknown' };
  }
  if (error.code === 'ECONNABORTED') {
    return { errorType: 'request_failed_network', errorMessage: 'timeout' };
  }
  if (error.response) {
    const status = error.response.status;
    const detail = error.response.data?.error || error.response.statusText || '';
    return { errorType: 'request_failed_status', errorMessage: `${status} ${detail}`.trim() };
  }
  if (error.request) {
    return { errorType: 'request_failed_network', errorMessage: 'no response received' };
  }
  return { errorType: 'request_failed_network', errorMessage: error.message || 'unknown' };
}

// Add this near the top of your file, after your imports
createTheme('customDark', {
  text: {
    primary: '#fff',
    secondary: '#aaa',
  },
  background: {
    default: '#181818', // Spotify-like dark background
  },
  context: {
    background: '#232323',
    text: '#FFFFFF',
  },
  divider: {
    default: '#333',
  },
  button: {
    default: '#232323',
    hover: '#333',
    focus: '#444',
    disabled: '#555',
  },
  sortFocus: {
    default: '#1db954', // Spotify green
  },
  highlightOnHover: {
    default: '#222',
    text: '#fff',
  },
  striped: {
    default: '#202020',
    text: '#fff',
  },
});

// Detect Android WebView — autoplay is allowed there, no activation overlay needed
const isAndroidWebView = typeof window !== 'undefined' && (
  window.AndroidInterface !== undefined ||
  (navigator.userAgent && navigator.userAgent.includes('DJApp'))
);

// Mobile-browser guard: the DJ is meant to run on laptops and inside the Android
// APK WebView (which identifies itself with DJApp in the UA). If the user opens
// the DJ URL on their personal phone Chrome by accident, bounce them to the
// companion remote. Skipped on localhost so dev mode still works on a phone.
if (typeof window !== 'undefined' && !isAndroidWebView) {
  const host = window.location.hostname;
  const isLocalhost = host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local');
  const isMobileBrowser = /Mobi|Android|iPhone|iPad|iPod/.test(navigator.userAgent);
  const allowOverride = window.location.search.includes('no-redirect');
  const REMOTE_URL = process.env.REACT_APP_REMOTE_URL || 'https://dj-remote.vercel.app';
  if (isMobileBrowser && !isLocalhost && !allowOverride) {
    window.location.replace(REMOTE_URL);
  }
}

// --- Ensure the global function always exists before SDK loads ---
if (typeof window !== 'undefined' && !window.onSpotifyWebPlaybackSDKReady) {
  window.onSpotifyWebPlaybackSDKReady = null;
}

// Add a helper function to check player state
const isPlayerReady = (player, playerReady, deviceId) => {
  if (!player) {
    console.warn('[Player] No player instance available');
    return false;
  }
  if (!playerReady) {
    console.warn('[Player] Player is not ready');
    return false;
  }
  if (!deviceId) {
    console.warn('[Player] No device ID available');
    return false;
  }
  return true;
};

// Human-readable labels for remote-initiated actions shown in the DJ's
// toast/badge when the companion app fires an event.
const REMOTE_ACTION_LABELS = {
  play: 'Resume',
  pause: 'Pause',
  skip: 'Skip',
  previous: 'Previous',
  volume_up: 'Volume up',
  volume_down: 'Volume down',
  mute: 'Mute',
  unmute: 'Unmute',
  skip_playlist: 'New playlist',
  like_track: 'Thumbs up',
  dislike_track: 'Thumbs down',
  shutdown_dj: 'Shut down DJ',
  start_dj: 'Start DJ',
  no_action: "Didn't catch that",
};
const labelForAction = (name) => REMOTE_ACTION_LABELS[name] || name;

function App() {
  const [recommendedSongs, setRecommendedSongs] = useState([]);
  const [currentSongIndex, setCurrentSongIndex] = useState(0);
  // DJ-129: highest trackIndex seen in the current recommendedSongs array.
  // Used to detect "Spotify went off-script" — either the queue wrapped
  // back to the start of the same playlist (Spotify's auto-loop behavior
  // when no `repeat` mode is set) or autoplay kicked in with a track not
  // in our playlist at all. Both cases are a signal the apply paths
  // gated on `state.paused` won't fire, so we apply pending continuation
  // immediately instead of letting the user keep hearing wrong music.
  const lastSeenTrackIndexRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [player, setPlayer] = useState(null);
  const [deviceId, setDeviceId] = useState(null);
  const [scene, setScene] = useState({ 
    scene: '', 
    sceneMatches: false,
    action: '',
    confidence: 0,
    explanation: ''
  });
  const [videoStream, setVideoStream] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [playerReady, setPlayerReady] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [isMuted, setIsMuted] = useState(false);
  const preMuteVolume = useRef(0.8);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const tokenRef = useRef(localStorage.getItem('token'));
  // Timestamp when the access_token was minted via OAuth code exchange.
  // Spotify's /api edge nodes need a few seconds to recognize a freshly
  // minted token — first calls within ~3s of mint can return 403. We use
  // this to insert a settle delay before fetchAllLikedSongs.
  const tokenMintedAtRef = useRef(0);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [trackProgress, setTrackProgress] = useState(0);
  const [trackDuration, setTrackDuration] = useState(0);
  const progressInterval = useRef(null);
  const initializationTimeout = useRef(null);
  const isInitializing = useRef(false);
  const codeExchangeInProgress = useRef(
    new URLSearchParams(window.location.search).has('code')
  );
  const sdkScriptLoaded = useRef(false);
  const playerInstance = useRef(null);
  const shouldCleanup = useRef(false);
  const initializationLock = useRef(false);
  const volumeDebounceTimeout = useRef(null);
  const pendingVolumeChange = useRef(null);
  const loggedPlayedTrackId = useRef(null);
  const skipInitiatedByApp = useRef(false);
  const backwardSkipInitiatedByApp = useRef(false);
  const lastPausedState = useRef(false); // Initialize to false (not-paused) so first pause transition is detected
  const initialDataFetchPerformed = useRef(false);
  const lastPlayedSongsRef = useRef([]);
  
  // Device transfer detection — DJ yields when another device takes over Spotify
  const [djYielded, setDjYielded] = useState(false);
  const djYieldedRef = useRef(false);
  const yieldedPlaylistContext = useRef(null);

  // Device lifecycle tracking for debugging
  const deviceLifecycleRef = useRef({
    deviceSetAt: null,
    lastSuccessfulPlayback: null,
    lastDeviceCheck: null,
    deviceCheckInterval: null,
    deviceHealthHistory: [],
    errorHistory: []
  });
  
  // Add refs to prevent duplicate state processing
  const lastProcessedState = useRef({
    trackId: null,
    position: null,
    timestamp: null,
    lastNewTrackLog: null
  });
  const stateProcessingLock = useRef(false);
  const newTrackDebounceTimer = useRef(null);
  
  // Use ref for session ID to ensure it persists across renders
  // Session ID is generated:
  // 1. On initial app load (component mount)
  // 2. On page refresh (component remount)  
  // 3. When master toggle is turned ON from OFF state
  const sessionIdRef = useRef(null);
  // Initialize session ID only once on mount
  if (!sessionIdRef.current) {
    sessionIdRef.current = uuidv4();
    console.log('[Session] Initial session ID generated:', sessionIdRef.current);
  }

  // Origin of the current action being dispatched. Flips to 'remote_button' /
  // 'remote_voice' inside the broadcast listener for the duration of a remote-
  // triggered action, then reverts to 'dj_app'. Read at POST time to stamp
  // playback_events / track_feedback rows.
  const actionOriginRef = useRef('dj_app');

  // Timer for rotating session ID after prolonged "No Music" state
  const noneStateTimerRef = useRef(null);

  // Add pending scene and songs state
  const [pendingScene, setPendingScene] = useState(null);
  const [pendingSongs, setPendingSongs] = useState(null);
  const hasPendingUpdate = useRef(false);
  const pendingUpdateApplied = useRef(false);
  
  // Add ref to track playlist playback handling
  const isHandlingPlaylistPlayback = useRef(false);
  const lastPlaybackStartTime = useRef(0);
  
  // Add ref to track if we need fresh SDK after toggle
  const needsFreshSDK = useRef(false);

  // Add a state ref to track volume changes
  const volumeStateRef = useRef({
    current: 0.8,
    pending: null,
    timeout: null
  });
  const volumeApiTimeout = useRef(null);
  const lastTrackedVolume = useRef(null);
  const lastSignificantVolume = useRef(null);
  const volumePollInterval = useRef(null);

  // Add master toggle state
  // Android WebView doesn't need activation — autoplay is allowed
  // On web browsers, require one-time activation click to satisfy autoplay policy
  const [isMasterEnabled, setIsMasterEnabled] = useState(() => {
    if (isAndroidWebView) {
      // Mark as activated so the activation overlay never shows on Android
      sessionStorage.setItem('dj_audio_activated', 'true');
      return true;
    }
    return sessionStorage.getItem('dj_audio_activated') === 'true';
  });
  const isMasterEnabledRef = useRef(isMasterEnabled);

  // Epoch bumped every time we rotate sessionIdRef.current so that React
  // effects keyed off the session (e.g. the remote-broadcast listener) can
  // re-subscribe. Refs by themselves don't trigger re-renders, so we need an
  // explicit state handle.
  const [sessionEpoch, setSessionEpoch] = useState(0);
  const rotateSessionId = useCallback(() => {
    sessionIdRef.current = uuidv4();
    setSessionEpoch((n) => n + 1);
    return sessionIdRef.current;
  }, []);

  // Add state for async playlist handling
  const [pendingPlaylistId, setPendingPlaylistId] = useState(null);
  const [playlistSubscription, setPlaylistSubscription] = useState(null);
  const [isGeneratingPlaylist, setIsGeneratingPlaylist] = useState(false);
  const [playlistPollingInterval, setPlaylistPollingInterval] = useState(null);
  const [skippedPlaylistIds, setSkippedPlaylistIds] = useState([]);
  const [currentPlaylistId, setCurrentPlaylistId] = useState(null);
  const currentPlaylistIdRef = useRef(null);
  // Holds the playlist ID for tracks queued in pendingSongs so applyPendingUpdates
  // can promote it to currentPlaylistId at the exact moment the new tracks start
  // playing — not when the ID is first received from the backend.
  const queuedPlaylistIdRef = useRef(null);
  const [currentPlaylistMode, setCurrentPlaylistMode] = useState(null); // 'smart_picker' (the only mode after AI Generator retirement)
  // Ref mirror so the remote-feedback broadcast listener (DJ-139), whose
  // useEffect deps don't include this state, can still read the current
  // value when forwarding a session_feedback row.
  const currentPlaylistModeRef = useRef(null);
  useEffect(() => { currentPlaylistModeRef.current = currentPlaylistMode; }, [currentPlaylistMode]);
  const modesUsedInSessionRef = useRef(new Set());
  // Tracks the sub-vibe for the currently-playing playlist so every
  // playback_events / track_feedback row can be stamped with it.
  const currentSubVibeRef = useRef(null);
  // Holds the sub-vibe for tracks queued in pendingSongs, mirroring
  // queuedPlaylistIdRef so applyPendingUpdates can promote it when
  // the new tracks actually start playing.
  const queuedSubVibeRef = useRef(null);
  const [lastFileUrl, setLastFileUrl] = useState(null);

  // Session feedback state
  const [showSessionFeedback, setShowSessionFeedback] = useState(false);
  const [lastRemoteAction, setLastRemoteAction] = useState(null);  // {tool, heard, ts}
  const lastRemoteActionClearRef = useRef(null);
  const sessionStartTimeRef = useRef(Date.now());
  const feedbackSessionIdRef = useRef(null);
  const feedbackListeningMinutesRef = useRef(0);

  // Add refs to track playlist generation state for interval access
  const isGeneratingPlaylistRef = useRef(false);
  const pendingPlaylistIdRef = useRef(null);
  const playlistPollingIntervalRef = useRef(null);
  const playlistSubscriptionRef = useRef(null);
  const isContinuationInProgressRef = useRef(false);
  const requestContinuationPlaylistRef = useRef(null);

  // "Preparing your DJ" state — shown while god_mode builds the user's profile
  const [isPreparingDj, setIsPreparingDj] = useState(false);
  const [preparingStage, setPreparingStage] = useState(null);
  const isPreparingDjRef = useRef(false);

  // Gate: don't send snapshots until liked-songs have been synced to the backend
  // so god_mode always has the user's library available.
  const likedSongsSyncedRef = useRef(false);
  // Tracks how many times fetchAllLikedSongs has been retried after a hard
  // failure (Spotify auth/network down). Bounded by MAX_ATTEMPTS inside the
  // function. Reset to 0 on a successful sync.
  const likedSongsRetryCountRef = useRef(0);

  // Diagnostics: track audio routing flaps and SDK position stalls so we can
  // diagnose mid-track audio gaps that don't pause the SDK (Android USB audio
  // hijack, headphone-jack flap, etc.). All emit console.warn → BetterStack.
  const lastHeartbeatPositionRef = useRef({ position: null, trackId: null, sampledAt: 0, stallLoggedAt: 0 });
  const lastPausedFlagRef = useRef(null);
  const lastDeviceFingerprintRef = useRef(null);

  // Spotify App Pool — dynamic credentials (resolved via hook)
  const {
    clientId: resolvedClientId,
    clientSecret: resolvedClientSecret,
    credentialsResolved,
    credentialsLoading,
    userNotRegistered,
    userEmail,
    setUserEmail,
    setUserNotRegistered,
    resolveCredentials: resolveSpotifyCredentials,
    clearCredentials: clearSpotifyCredentials,
  } = useSpotifyCredentials();

  const CLIENT_ID = resolvedClientId;
  const CLIENT_SECRET = resolvedClientSecret;
  
  // Log component mount/unmount for session tracking
  useEffect(() => {
    console.log('[Session] App component mounted with session ID:', sessionIdRef.current);
    return () => {
      console.log('[Session] App component unmounting with session ID:', sessionIdRef.current);
    };
  }, []); // Empty dependency array means this only runs on mount/unmount

  // Diagnostics helpers: snapshot audio output devices and detect routing flaps.
  // The progress bar can advance while audio drops on a Sonos line-in if Android
  // re-routes STREAM_MUSIC away from the wired headphone jack (USB audio class
  // device, headphone-jack flap). The browser's enumerateDevices() is a usable
  // proxy for this since it reflects OS-level audio device availability.
  const fingerprintAudioOutputs = useCallback((devices) => {
    return devices
      .filter(d => d.kind === 'audiooutput')
      .map(d => `${d.deviceId}|${d.label || ''}|${d.groupId || ''}`)
      .sort()
      .join(',');
  }, []);

  const snapshotDevicesAndLog = useCallback(async (reason) => {
    if (!navigator?.mediaDevices?.enumerateDevices) return;
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const outputs = devices.filter(d => d.kind === 'audiooutput');
      const fingerprint = fingerprintAudioOutputs(devices);
      const changed = lastDeviceFingerprintRef.current !== null && lastDeviceFingerprintRef.current !== fingerprint;
      lastDeviceFingerprintRef.current = fingerprint;
      console.warn('[Audio Routing]', {
        reason,
        count: outputs.length,
        outputs: outputs.map(d => ({
          deviceId: (d.deviceId || '').slice(0, 16),
          label: d.label || '',
          groupId: (d.groupId || '').slice(0, 16),
        })),
        changed,
      });
    } catch (e) {
      console.warn('[Audio Routing] enumerateDevices failed:', e?.message);
    }
  }, [fingerprintAudioOutputs]);

  // Diagnostics: mount-once listeners for audio routing, page lifecycle,
  // network online/offline, and AudioContext state. All graceful-degrade if
  // the WebView is missing the API. See plan-greedy-pancake.md.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleDeviceChange = () => snapshotDevicesAndLog('devicechange');
    const handleVisibility = () => console.warn('[Page Visibility]', {
      state: typeof document !== 'undefined' ? document.visibilityState : null,
      hidden: typeof document !== 'undefined' ? document.hidden : null,
    });
    const handleOnline = () => console.warn('[Network]', {
      online: true,
      effectiveType: navigator.connection?.effectiveType,
    });
    const handleOffline = () => console.warn('[Network]', {
      online: false,
      effectiveType: navigator.connection?.effectiveType,
    });
    if (navigator?.mediaDevices?.addEventListener) {
      navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    }
    if (typeof document !== 'undefined' && document.addEventListener) {
      document.addEventListener('visibilitychange', handleVisibility);
    }
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial snapshots so we have baseline data correlated by mount time
    snapshotDevicesAndLog('mount');
    if (typeof document !== 'undefined') {
      console.warn('[Page Visibility]', {
        state: document.visibilityState,
        hidden: document.hidden,
        mount: true,
      });
    }
    console.warn('[Network]', {
      online: navigator.onLine,
      effectiveType: navigator.connection?.effectiveType,
      mount: true,
    });

    return () => {
      try {
        if (navigator?.mediaDevices?.removeEventListener) {
          navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
        }
        if (typeof document !== 'undefined' && document.removeEventListener) {
          document.removeEventListener('visibilitychange', handleVisibility);
        }
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
        lastDeviceFingerprintRef.current = null;
      } catch (e) {
        console.warn('[Diagnostics] cleanup error:', e?.message);
      }
    };
  }, [snapshotDevicesAndLog]);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const REDIRECT_URI = window.location.hostname === 'localhost'
    ? 'https://tayna-schizophyceous-deedra.ngrok-free.dev'
    : window.location.origin;
  //const REDIRECT_URI = 'https://dev.d364589rcdl5vf.amplifyapp.com';
  //staging URL 'https://staging.d364589rcdl5vf.amplifyapp.com'; // This will be updated once we have the new Amplify domain

  const AUTH_ENDPOINT = 'https://accounts.spotify.com/authorize';
  const TOKEN_ENDPOINT = 'https://accounts.spotify.com/api/token';
  const RESPONSE_TYPE = 'code';
  const SCOPE = 'user-library-read user-read-private user-read-email streaming user-modify-playback-state user-read-playback-state user-top-read';

  const MAX_PLAYER_INIT_RETRIES = 3;
  const PLAYER_INIT_RETRY_DELAY = 2000;

  // Add these refs near the top of the component
  const playerStateLock = useRef(false);
  const playerHeartbeatInterval = useRef(null);
  const lastValidState = useRef(null);
  const stateTransitionTimeout = useRef(null);
  const tokenRefreshLock = useRef(false);

  const redirectToSpotifyLogin = useCallback(() => {
    // Don't redirect if credentials haven't been resolved yet
    if (!resolvedClientId) {
      console.log('[Auth] Skipping Spotify redirect — credentials not resolved yet');
      return;
    }
    // Don't redirect while a code exchange is in progress (prevents infinite loop)
    if (codeExchangeInProgress.current) {
      console.log('[Auth] Skipping Spotify redirect — code exchange in progress');
      return;
    }
    window.location.href = getAuthUrl();
  }, [resolvedClientId]);

  // Helper function to check and log all available devices
  const checkAvailableDevices = useCallback(async (context = '') => {
    const token = localStorage.getItem('token');
    if (!token) return null;

    try {
      const response = await axios.get(
        'https://api.spotify.com/v1/me/player/devices',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      const devices = response.data.devices || [];
      const ourDevice = devices.find(d => d.id === deviceId);
      
      const deviceInfo = {
        context,
        timestamp: new Date().toISOString(),
        count: devices.length,
        devices: devices.map(d => ({
          id: d.id,
          name: d.name,
          type: d.type,
          is_active: d.is_active,
          is_our_device: d.id === deviceId
        })),
        ourDeviceId: deviceId,
        ourDeviceFound: !!ourDevice,
        ourDeviceActive: ourDevice?.is_active || false
      };
      
      console.log(`[Device Check - ${context}]`, deviceInfo);
      
      // Add to device health history
      deviceLifecycleRef.current.deviceHealthHistory.push({
        ...deviceInfo,
        success: true
      });
      
      // Keep only last 20 health checks
      if (deviceLifecycleRef.current.deviceHealthHistory.length > 20) {
        deviceLifecycleRef.current.deviceHealthHistory.shift();
      }
      
      deviceLifecycleRef.current.lastDeviceCheck = Date.now();
      return devices;
    } catch (error) {
      const errorInfo = {
        context,
        timestamp: new Date().toISOString(),
        error: error?.response?.data || error.message,
        statusCode: error?.response?.status
      };
      
      console.error(`[Device Check Failed - ${context}]`, errorInfo);
      
      deviceLifecycleRef.current.errorHistory.push(errorInfo);
      if (deviceLifecycleRef.current.errorHistory.length > 10) {
        deviceLifecycleRef.current.errorHistory.shift();
      }
      
      return null;
    }
  }, [deviceId]);

  // Helper function to display device health summary (can be called from console)
  const getDeviceHealthSummary = useCallback(() => {
    const now = Date.now();
    const lifecycle = deviceLifecycleRef.current;
    
    const summary = {
      currentStatus: {
        hasDevice: !!deviceId,
        deviceId: deviceId || 'None',
        playerReady,
        deviceUptime: lifecycle.deviceSetAt 
          ? `${Math.floor((now - lifecycle.deviceSetAt) / 1000)}s`
          : 'Never set',
        lastSuccessfulPlayback: lifecycle.lastSuccessfulPlayback
          ? `${Math.floor((now - lifecycle.lastSuccessfulPlayback) / 1000)}s ago`
          : 'Never',
        lastDeviceCheck: lifecycle.lastDeviceCheck
          ? `${Math.floor((now - lifecycle.lastDeviceCheck) / 1000)}s ago`
          : 'Never'
      },
      recentHealthChecks: lifecycle.deviceHealthHistory.slice(-5).map(h => ({
        time: h.timestamp,
        deviceCount: h.count,
        ourDeviceFound: h.ourDeviceFound,
        context: h.context
      })),
      recentErrors: lifecycle.errorHistory.slice(-5),
      recommendation: !deviceId ? 'No device connected - try refreshing the page' :
                      lifecycle.deviceHealthHistory.length > 0 && 
                      !lifecycle.deviceHealthHistory[lifecycle.deviceHealthHistory.length - 1]?.ourDeviceFound ?
                      'Device lost - needs reconnection' :
                      lifecycle.lastSuccessfulPlayback && (now - lifecycle.lastSuccessfulPlayback) > 300000 ?
                      'No successful playback in 5+ minutes - device may be stale' :
                      'Device appears healthy'
    };
    
    console.log('[Device Health Summary]', summary);
    return summary;
  }, [deviceId, playerReady]);
  
  // Expose to window for debugging
  useEffect(() => {
    window.getDeviceHealthSummary = getDeviceHealthSummary;
    return () => {
      delete window.getDeviceHealthSummary;
    };
  }, [getDeviceHealthSummary]);

  const refreshAccessToken = useCallback(async () => {
    if (tokenRefreshLock.current) {
      console.log('[Auth] Token refresh is already in progress. Skipping.');
      return localStorage.getItem('token');
    }
    tokenRefreshLock.current = true;

    try {
      const clientId = CLIENT_ID;
      const clientSecret = CLIENT_SECRET;
      const refreshToken = localStorage.getItem('refresh_token');

      if (!refreshToken) {
        return null;
      }

      const authHeader = `Basic ${btoa(`${clientId}:${clientSecret}`)}`;
      const data = new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
      }).toString();

      const response = await axios({
        method: 'post',
        url: TOKEN_ENDPOINT,
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        data: data,
      });

      const { access_token, expires_in } = response.data;
      
      // Store new access token and update expiry
      localStorage.setItem('token', access_token);
      const expiryTime = Date.now() + (expires_in * 1000);
      localStorage.setItem('token_expiry', expiryTime.toString());
      
      // Update only the ref, not the state, to avoid cascading effects
      tokenRef.current = access_token;
      console.log('[Auth] Token refreshed successfully without updating state');
      return access_token;
    } catch (error) {
      console.error('Error refreshing token:', error);
      // Clear tokens on refresh error
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('token_expiry');
      tokenRef.current = null;
      setToken(null);
      return null;
    } finally {
      tokenRefreshLock.current = false;
    }
  }, [CLIENT_ID, CLIENT_SECRET, TOKEN_ENDPOINT]);

  // Fetch currently playing track from Spotify API
  const fetchCurrentlyPlaying = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const response = await axios.get('https://api.spotify.com/v1/me/player/currently-playing', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data && response.data.item) {
        setCurrentlyPlaying({
          title: response.data.item.name,
          artist: response.data.item.artists.map(a => a.name).join(', '),
          albumArt: response.data.item.album.images[0]?.url,
          spotifyTrackId: response.data.item.id,
        });
      } else {
        setCurrentlyPlaying(null);
      }
    } catch (error) {
      setCurrentlyPlaying(null);
    }
  }, []);

  // --- Play Song using Web Playback SDK ---
  const playAllSongs = useCallback(async () => {
    if (!recommendedSongs || recommendedSongs.length === 0) {
      console.warn('[playAllSongs] No songs to play');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token || !deviceId) {
      console.warn('[playAllSongs] No token or deviceId:', {
        hasToken: !!token,
        deviceId,
        playerReady,
        deviceUptime: deviceLifecycleRef.current.deviceSetAt 
          ? `${Math.floor((Date.now() - deviceLifecycleRef.current.deviceSetAt) / 1000)}s`
          : 'N/A'
      });
      return;
    }

    // Check current player state BEFORE attempting playback
    if (player) {
      try {
        const state = await player.getCurrentState();
        console.log('[playAllSongs] Current player state before playback:', {
          hasState: !!state,
          isPaused: state?.paused,
          isBuffering: state?.loading,
          currentTrack: state?.track_window?.current_track?.name,
          position: state?.position,
          duration: state?.duration,
          deviceId,
          deviceUptime: deviceLifecycleRef.current.deviceSetAt 
            ? `${Math.floor((Date.now() - deviceLifecycleRef.current.deviceSetAt) / 1000)}s`
            : 'N/A'
        });
      } catch (stateError) {
        console.error('[playAllSongs] Error getting player state:', stateError);
      }
    }
    
    // Check available devices before attempting playback
    console.log('[playAllSongs] Checking available devices before playback...');
    await checkAvailableDevices('Before Playback');

    // Check if we've recently started playback (within last 2 seconds)
    const now = Date.now();
    if (now - lastPlaybackStartTime.current < 2000) {
      console.log('[playAllSongs] Skipping - playback recently started', now - lastPlaybackStartTime.current, 'ms ago');
      return;
    }
    lastPlaybackStartTime.current = now;

    // Get all track URIs
    const uris = recommendedSongs
      .map(song => song.spotifyTrackId)
      .filter(Boolean)
      .map(id => id.startsWith('spotify:track:') ? id : `spotify:track:${id}`);

    console.log('[playAllSongs] Attempting playback:', {
      trackCount: uris.length,
      deviceId,
      playerReady,
      deviceUptime: deviceLifecycleRef.current.deviceSetAt 
        ? `${Math.floor((Date.now() - deviceLifecycleRef.current.deviceSetAt) / 1000)}s`
        : 'N/A',
      timeSinceLastSuccess: deviceLifecycleRef.current.lastSuccessfulPlayback
        ? `${Math.floor((Date.now() - deviceLifecycleRef.current.lastSuccessfulPlayback) / 1000)}s`
        : 'N/A'
    });

    try {
      // Play directly on the device without separate transfer
      console.log('[playAllSongs] Playing', uris.length, 'tracks directly on device:', deviceId);
      await axios.put(
        `https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`,
        { 
          uris,
          position_ms: 0,
          offset: { position: 0 }
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      // Success! Update tracking
      deviceLifecycleRef.current.lastSuccessfulPlayback = Date.now();
      console.log('[playAllSongs] ✅ Playback started successfully');
      setIsPlaying(true);
      
    } catch (error) {
      console.error('[playAllSongs] ❌ Error playing songs:', {
        error: error?.response?.data || error.message || error,
        statusCode: error?.response?.status,
        deviceId,
        deviceUptime: deviceLifecycleRef.current.deviceSetAt 
          ? `${Math.floor((Date.now() - deviceLifecycleRef.current.deviceSetAt) / 1000)}s`
          : 'N/A',
        timeSinceLastSuccess: deviceLifecycleRef.current.lastSuccessfulPlayback
          ? `${Math.floor((Date.now() - deviceLifecycleRef.current.lastSuccessfulPlayback) / 1000)}s`
          : 'N/A'
      });
      
      // Check available devices when error occurs
      console.log('[playAllSongs] Checking devices after error...');
      const availableDevices = await checkAvailableDevices('After Error');
      
      // If device not found, try playing on any active device
      if (error?.response?.status === 404) {
        console.log('[playAllSongs] Device not found (404), attempting to play on any active device');
        console.log('[playAllSongs] Available devices for fallback:', availableDevices);
        
        try {
          await axios.put(
            'https://api.spotify.com/v1/me/player/play',
            { uris },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          console.log('[playAllSongs] ✅ Successfully started playing on active device');
          setIsPlaying(true);
        } catch (fallbackError) {
          console.error('[playAllSongs] ❌ Fallback play failed:', {
            error: fallbackError?.response?.data || fallbackError.message || fallbackError,
            statusCode: fallbackError?.response?.status,
            reason: fallbackError?.response?.data?.error?.reason
          });
          
          // One more device check after fallback failure
          console.log('[playAllSongs] Final device check after fallback failure:');
          await checkAvailableDevices('After Fallback Failure');
        }
      }
    }
  }, [deviceId, recommendedSongs, player, checkAvailableDevices]);

  const getAuthUrl = () => {
    const state = Math.random().toString(36).substring(7);
    localStorage.setItem('spotify_auth_state', state);

    const encodedRedirectUri = encodeURIComponent(REDIRECT_URI);
    const authUrl = `${AUTH_ENDPOINT}?` +
      `client_id=${CLIENT_ID}&` +
      `redirect_uri=${encodedRedirectUri}&` +
      `response_type=${RESPONSE_TYPE}&` +
      `scope=${encodeURIComponent(SCOPE)}&` +
      `state=${state}`;
    return authUrl;
  };

  // Update the token refresh effect to properly handle token refresh
  useEffect(() => {
    // Don't run token refresh until credentials are resolved
    if (!credentialsResolved || !CLIENT_ID || !CLIENT_SECRET) return;

    const refreshToken = async () => {
      const refreshTokenValue = localStorage.getItem('refresh_token');
      if (!refreshTokenValue) {
        console.log('[Auth] No refresh token available');
        redirectToSpotifyLogin();
        return;
      }

      try {
        console.log('[Auth] Attempting to refresh token');
        const authHeader = `Basic ${btoa(`${CLIENT_ID}:${CLIENT_SECRET}`)}`;
        const data = new URLSearchParams({
          grant_type: 'refresh_token',
          refresh_token: refreshTokenValue,
        }).toString();

        const response = await axios({
          method: 'post',
          url: TOKEN_ENDPOINT,
          headers: {
            'Authorization': authHeader,
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          data: data,
        });

                      const { access_token, expires_in } = response.data;
        console.log('[Auth] Token refreshed successfully');
        console.log('[Token Refresh] Token successfully refreshed, new expiry in 60 minutes');
        
        localStorage.setItem('token', access_token);
        const expiryTime = Date.now() + (expires_in * 1000);
        localStorage.setItem('token_expiry', expiryTime.toString());
        
        // Update only the ref, not the state, to avoid cascading effects
        tokenRef.current = access_token;
        console.log('[Auth] Auto-refresh completed without updating state');
      } catch (error) {
        console.error('[Auth] Error refreshing token:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('token_expiry');
        redirectToSpotifyLogin();
      }
    };

    const checkTokenExpiry = () => {
      const currentToken = localStorage.getItem('token');
      const tokenExpiry = localStorage.getItem('token_expiry');

      if (!currentToken || !tokenExpiry) {
        console.log('[Auth] No token or expiry found');
        refreshToken();
        return;
      }

      const timeUntilExpiry = parseInt(tokenExpiry) - Date.now();
      const minutesUntilExpiry = Math.floor(timeUntilExpiry / 60000);
      if (timeUntilExpiry < 300000) { // Refresh if less than 5 minutes until expiry
        console.log(`[Token Expiry] Token will expire in ${minutesUntilExpiry} minutes, initiating refresh`);
        console.log('[Auth] Token expiring soon, refreshing');
        refreshToken();
      }
    };

    // Check token on mount and every 4 minutes
    checkTokenExpiry();
    const intervalId = setInterval(checkTokenExpiry, 240000);

    return () => clearInterval(intervalId);
  }, [CLIENT_ID, CLIENT_SECRET, TOKEN_ENDPOINT, redirectToSpotifyLogin, recommendedSongs, credentialsResolved]);

  // Use boolean so effect re-runs when token first arrives (null→value),
  // but NOT on every token refresh (value→value) which would interrupt playback
  const hasToken = !!token;

  // Update the player initialization effect
  useEffect(() => {

    let scriptTag = null;
    let initializationAttempts = 0;
    let timeoutId = null;
    let isCleaningUp = false;

    if (!isMasterEnabled) {
      console.log('[Spotify SDK] Master toggle is disabled. Skipping player initialization.');
      return;
    }

    // Check and refresh token before initialization
    const ensureValidToken = async () => {
      const currentToken = localStorage.getItem('token');
      const tokenExpiry = localStorage.getItem('token_expiry');
      const refreshToken = localStorage.getItem('refresh_token');
      
      if (!currentToken && !refreshToken) {
        console.log('[Spotify SDK] No token or refresh token available, skipping initialization');
        return false;
      }
      
      // Check if token is expired or near expiry (within 5 minutes)
      if (currentToken && tokenExpiry) {
              const timeUntilExpiry = parseInt(tokenExpiry) - Date.now();
      const minutesUntilExpiry = Math.floor(timeUntilExpiry / 60000);
      if (timeUntilExpiry < 300000) { // Less than 5 minutes
        console.log(`[Token Expiry] Token will expire in ${minutesUntilExpiry} minutes, initiating refresh`);
        console.log('[Spotify SDK] Token expired or near expiry, refreshing before initialization');
        const newToken = await refreshAccessToken();
        if (!newToken) {
          console.log('[Spotify SDK] Failed to refresh token');
          return false;
        }
        console.log('[Token Refresh] Token successfully refreshed, new expiry in 60 minutes');
        console.log('[Spotify SDK] Token refreshed successfully before initialization');
        }
      } else if (!currentToken && refreshToken) {
        console.log('[Spotify SDK] No access token but refresh token exists, refreshing');
        const newToken = await refreshAccessToken();
        if (!newToken) {
          console.log('[Spotify SDK] Failed to refresh token');
          return false;
        }
        console.log('[Spotify SDK] Token obtained successfully before initialization');
      }
      
      return true;
    };

    // Don't re-initialize if player already exists and is ready
    if (playerInstance.current && playerReady) {
      console.log('[Spotify SDK] Player already initialized and ready, skipping re-initialization');
      return;
    }

    const cleanupExistingPlayer = async () => {
      if (playerInstance.current) {
        console.log('[Spotify SDK] Cleaning up existing player instance');
        try {
          await playerInstance.current.disconnect();
          playerInstance.current = null;
          setPlayer(null);
          setPlayerReady(false);
          setDeviceId(null);
        } catch (error) {
          console.error('[Spotify SDK] Error disconnecting existing player:', error);
        }
      }
    };

    async function initializePlayer() {
      if (isCleaningUp || initializationAttempts >= MAX_PLAYER_INIT_RETRIES) {
        console.log('[Spotify SDK] Initialization skipped - cleanup in progress or max attempts reached');
        return;
      }

      if (initializationLock.current) {
        console.log('[Spotify SDK] Initialization already in progress');
        return;
      }

      // Ensure we have a valid token before proceeding
      const hasValidToken = await ensureValidToken();
      if (!hasValidToken) {
        console.log('[Spotify SDK] No valid token available, skipping initialization');
        return;
      }

      initializationLock.current = true;
      initializationAttempts++;
      console.log(`[Spotify SDK] Initialization attempt ${initializationAttempts}`);

      try {
        await cleanupExistingPlayer();

        if (isCleaningUp) {
          console.log('[Spotify SDK] Cleanup triggered during initialization, aborting');
          return;
        }

        setPlayerReady(false);
        setDeviceId(null);
        setPlayer(null);

        window.onSpotifyWebPlaybackSDKReady = () => {
          if (isCleaningUp) {
            console.log('[Spotify SDK] Cleanup in progress, skipping SDK initialization');
            return;
          }

          console.log('[Spotify SDK] SDK Ready callback triggered');
          
          try {
            const spotifyPlayer = new window.Spotify.Player({
              name: 'DJ Web Player',
              getOAuthToken: cb => { 
                console.log('[Spotify SDK] getOAuthToken called, refreshing token proactively.');
                refreshAccessToken().then(newToken => {
                  if (newToken) {
                    console.log('[Spotify SDK] Token refreshed successfully in getOAuthToken');
                    cb(newToken);
                  } else {
                    console.error('[Spotify SDK] Failed to refresh token in getOAuthToken');
                    redirectToSpotifyLogin();
                    cb(null);
                  }
                }).catch(error => {
                  console.error('[Spotify SDK] Error refreshing token in getOAuthToken:', error);
                  redirectToSpotifyLogin();
                  cb(null);
                });
              },
              volume: volumeStateRef.current.current
            });

            // Enhanced error handling for player events
            spotifyPlayer.addListener('ready', async ({ device_id }) => {
              console.log('[Spotify SDK] Ready with Device ID', device_id);
              if (!isCleaningUp) {
                playerInstance.current = spotifyPlayer;
                
                // Wait for device to be actually registered with Spotify
                const waitForDevice = async (retries = 10) => {
                  const token = localStorage.getItem('token');
                  for (let i = 0; i < retries; i++) {
                    try {
                      const response = await axios.get('https://api.spotify.com/v1/me/player/devices', {
                        headers: {
                          Authorization: `Bearer ${token}`,
                        },
                      });
                      
                      const ourDevice = response.data.devices.find(d => d.id === device_id);
                      if (ourDevice) {
                        console.log('[Spotify SDK] Device confirmed available in Spotify after', i + 1, 'attempts');
                        return true;
                      }
                      
                      console.log('[Spotify SDK] Device not yet available, attempt', i + 1, 'of', retries);
                      // Wait 500ms before next check
                      await new Promise(resolve => setTimeout(resolve, 500));
                    } catch (error) {
                      console.error('[Spotify SDK] Error checking device availability:', error);
                    }
                  }
                  return false;
                };
                
                const deviceAvailable = await waitForDevice();
                
                if (deviceAvailable && !isCleaningUp) {
                  // Now we can safely set the device as ready
                  setDeviceId(device_id);
                  setPlayerReady(true);
                  setPlayer(spotifyPlayer);
                  initializationLock.current = false;
                  
                  if (volumeStateRef.current.pending !== null) {
                    spotifyPlayer.setVolume(volumeStateRef.current.pending)
                      .then(() => {
                        volumeStateRef.current.current = volumeStateRef.current.pending;
                        volumeStateRef.current.pending = null;
                      })
                      .catch(error => console.warn('[Spotify SDK] Error setting initial volume:', error));
                  }
                  // Initialize baseline for significant volume change detection
                  spotifyPlayer.getVolume().then(vol => {
                    lastSignificantVolume.current = Math.round(vol * 100) / 100;
                  }).catch(() => {});

                } else {
                  console.error('[Spotify SDK] Device never became available, disconnecting player');
                  playerInstance.current = null;
                  initializationLock.current = false;
                  spotifyPlayer.disconnect();
                }
              }
            });

            spotifyPlayer.addListener('not_ready', ({ device_id }) => {
              console.log('[Spotify SDK] Device ID has gone offline:', device_id);
              if (!isCleaningUp) {
                setPlayerReady(false);
                setDeviceId(null);
                initializationLock.current = false;
                
                if (initializationAttempts < MAX_PLAYER_INIT_RETRIES) {
                  timeoutId = setTimeout(() => {
                    console.log('[Spotify SDK] Attempting to reconnect...');
                    initializePlayer();
                  }, PLAYER_INIT_RETRY_DELAY);
                }
              }
            });

            spotifyPlayer.addListener('initialization_error', ({ message }) => {
              console.error('[Spotify SDK] Initialization Error:', message);
              initializationLock.current = false;
              if (!isCleaningUp && initializationAttempts < MAX_PLAYER_INIT_RETRIES) {
                timeoutId = setTimeout(initializePlayer, PLAYER_INIT_RETRY_DELAY);
              }
            });

            spotifyPlayer.addListener('authentication_error', ({ message }) => {
              console.error('[Spotify SDK] Authentication Error:', message);
              initializationLock.current = false;
              if (!isCleaningUp) {
                // Try to refresh the token on auth error
                refreshAccessToken().then(newToken => {
                  if (newToken) {
                    console.log('[Spotify SDK] Token refreshed after auth error, reconnecting player');
                    
                    // Disconnect and reconnect the player with new token
                    if (spotifyPlayer && typeof spotifyPlayer.disconnect === 'function') {
                      spotifyPlayer.disconnect().then(() => {
                        console.log('[Spotify SDK] Player disconnected, waiting before reinitializing');
                        // Wait a bit before trying to reinitialize
                        if (initializationAttempts < MAX_PLAYER_INIT_RETRIES) {
                          timeoutId = setTimeout(initializePlayer, PLAYER_INIT_RETRY_DELAY);
                        }
                      }).catch(disconnectError => {
                        console.error('[Spotify SDK] Error disconnecting after auth error:', disconnectError);
                        // Still try to reinitialize even if disconnect failed
                        if (initializationAttempts < MAX_PLAYER_INIT_RETRIES) {
                          timeoutId = setTimeout(initializePlayer, PLAYER_INIT_RETRY_DELAY);
                        }
                      });
                    }
                  } else {
                    localStorage.removeItem('token');
                    localStorage.removeItem('token_expiry');
                    tokenRef.current = null;
                    setToken(null);
                  }
                }).catch(refreshError => {
                  console.error('[Spotify SDK] Error refreshing token after auth error:', refreshError);
                  localStorage.removeItem('token');
                  localStorage.removeItem('token_expiry');
                  tokenRef.current = null;
                  setToken(null);
                });
              }
            });

            spotifyPlayer.addListener('account_error', ({ message }) => {
              console.error('[Spotify SDK] Account Error:', message);
              initializationLock.current = false;
            });

            // Add playback error handler
            spotifyPlayer.addListener('playback_error', ({ message }) => {
              console.error('[Spotify SDK] Playback Error:', message);
              if (!isCleaningUp && playerReady) {
                // Attempt to reconnect on playback errors
                spotifyPlayer.connect().catch(error => {
                  console.error('[Spotify SDK] Reconnection failed:', error);
                });
              }
            });

            if (!isCleaningUp) {
              spotifyPlayer.connect()
                .then(success => {
                  if (success) {
                    console.log('[Spotify SDK] Player connected successfully');
                  } else {
                    console.error('[Spotify SDK] Player failed to connect');
                    initializationLock.current = false;
                    if (!isCleaningUp && initializationAttempts < MAX_PLAYER_INIT_RETRIES) {
                      timeoutId = setTimeout(initializePlayer, PLAYER_INIT_RETRY_DELAY);
                    }
                  }
                })
                .catch(error => {
                  console.error('[Spotify SDK] Connection error:', error);
                  initializationLock.current = false;
                  if (!isCleaningUp && initializationAttempts < MAX_PLAYER_INIT_RETRIES) {
                    timeoutId = setTimeout(initializePlayer, PLAYER_INIT_RETRY_DELAY);
                  }
                });
            }
          } catch (error) {
            console.error('[Spotify SDK] Error creating player:', error);
            initializationLock.current = false;
            if (!isCleaningUp && initializationAttempts < MAX_PLAYER_INIT_RETRIES) {
              timeoutId = setTimeout(initializePlayer, PLAYER_INIT_RETRY_DELAY);
            }
          }
        };

        // Always load fresh SDK after master toggle to avoid stale devices
        console.log('[DEBUG] Checking SDK state - window.Spotify:', !!window.Spotify, 'needsFreshSDK:', needsFreshSDK.current);
        
        // If SDK exists but no player instance, it's likely stale from a toggle
        const isStaleSDK = window.Spotify && !playerInstance.current;
        
        if (!window.Spotify || needsFreshSDK.current || isStaleSDK) {
          console.log('[DEBUG] Loading fresh SDK - stale:', isStaleSDK, 'needsFresh:', needsFreshSDK.current);
          
          // Remove any existing script tags
          const existingScripts = document.querySelectorAll('script[src*="spotify-player.js"]');
          existingScripts.forEach(script => {
            console.log('[DEBUG] Removing existing Spotify SDK script');
            script.remove();
          });
          
          // Clear window.Spotify
          if (window.Spotify) {
            window.Spotify = undefined;
            console.log('[DEBUG] Cleared window.Spotify');
          }
          
          // Reset the flag
          needsFreshSDK.current = false;
          
          // Add fresh script tag with cache buster
          scriptTag = document.createElement('script');
          scriptTag.src = `https://sdk.scdn.co/spotify-player.js?cb=${Date.now()}`;
          scriptTag.async = true;
          document.body.appendChild(scriptTag);
          console.log('[DEBUG] Fresh script tag added to DOM with cache buster');
        } else {
          console.log('[DEBUG] Using existing SDK - playerInstance exists');
          window.onSpotifyWebPlaybackSDKReady();
        }
      } catch (error) {
        console.error('[Spotify SDK] Error during initialization:', error);
        initializationLock.current = false;
        if (!isCleaningUp && initializationAttempts < MAX_PLAYER_INIT_RETRIES) {
          timeoutId = setTimeout(initializePlayer, PLAYER_INIT_RETRY_DELAY);
        }
      }
    }

    initializePlayer();

    return () => {
      
      isCleaningUp = true;
      initializationLock.current = false;
      
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      if (volumeStateRef.current.timeout) {
        clearTimeout(volumeStateRef.current.timeout);
      }

      if (volumeApiTimeout.current) {
        clearTimeout(volumeApiTimeout.current);
      }

      if (volumePollInterval.current) {
        clearInterval(volumePollInterval.current);
      }

      if (playerInstance.current) {
        console.log('[Spotify SDK] Cleaning up player');
        
        // Give the SDK a moment to finish any pending operations
        const playerToClean = playerInstance.current;
        playerInstance.current = null;
        
        // Disconnect after a small delay to allow pending events to complete
        setTimeout(() => {
          playerToClean.disconnect().catch(error => {
            console.error('[Spotify SDK] Error disconnecting player:', error);
          });
        }, 100);
      }
      
      // Clean up all SDK scripts immediately
      const allScripts = document.querySelectorAll('script[src*="spotify-player.js"]');
      allScripts.forEach(script => {
        console.log('[DEBUG] Removing SDK script from cleanup');
        script.remove();
      });
      
      // Force clean the SDK
      if (window.Spotify) {
        console.log('[DEBUG] Force cleaning window.Spotify');
        try {
          // Try to clean up any existing player instances in the SDK
          if (window.Spotify._instances) {
            window.Spotify._instances = [];
          }
        } catch (e) {
          console.log('[DEBUG] Could not clean SDK instances:', e);
        }
        window.Spotify = undefined;
      }
      
      window.onSpotifyWebPlaybackSDKReady = null;
      
      setPlayer(null);
      setPlayerReady(false);
      setDeviceId(null);
    };
  }, [isMasterEnabled, refreshAccessToken, redirectToSpotifyLogin, hasToken]);

  // Sync token ref with token state (ref is read by getOAuthToken callback)
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  // Sync playlist generation state with refs for interval access
  useEffect(() => {
    isGeneratingPlaylistRef.current = isGeneratingPlaylist;
  }, [isGeneratingPlaylist]);

  useEffect(() => {
    pendingPlaylistIdRef.current = pendingPlaylistId;
  }, [pendingPlaylistId]);

  useEffect(() => {
    currentPlaylistIdRef.current = currentPlaylistId;
  }, [currentPlaylistId]);

  // Update the token validation effect
  useEffect(() => {
    const checkTokenValidity = async () => {
      const token = localStorage.getItem('token');
      const tokenExpiry = localStorage.getItem('token_expiry');
      const refreshToken = localStorage.getItem('refresh_token');

      // If no tokens exist, redirect to login
      if (!token && !refreshToken) {
        redirectToSpotifyLogin();
        return;
      }

      // If token exists and is not expired, do nothing
      if (token && tokenExpiry && Date.now() < parseInt(tokenExpiry)) {
        return;
      }

      // If token is expired or missing but we have refresh token, trigger token refresh by clearing the token
      if (refreshToken) {
        console.log('[Token Validation] Token expired or missing, triggering refresh');
        // Don't set token to null here to prevent cascading effects
        // Just trigger the refresh
        refreshAccessToken();
      } else {
        // If no refresh token, redirect to login
        redirectToSpotifyLogin();
      }
    };

    // Check immediately on mount
    checkTokenValidity();

    // Check every 30 minutes
    const intervalId = setInterval(checkTokenValidity, 10 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [redirectToSpotifyLogin, refreshAccessToken]);

  // Function to apply pending scene and songs
  const applyPendingUpdates = useCallback(async () => {
    console.log('[Pending Updates] ====== APPLY PENDING UPDATES START ======');
    console.log('[Pending Updates] Current state:', {
      hasPendingScene: !!pendingScene,
      hasPendingSongs: !!pendingSongs?.length,
      pendingSongsCount: pendingSongs?.length || 0,
      playerReady,
      deviceId,
      pendingUpdateApplied: pendingUpdateApplied.current,
      hasPendingUpdate: hasPendingUpdate.current,
      timestamp: new Date().toISOString()
    });

    // Double check we haven't already applied this update
    if (pendingUpdateApplied.current) {
      console.log('[Pending Updates] WARNING: Updates already applied, skipping to prevent double execution');
      return;
    }

    // Set the flag before starting the update process
    console.log('[Pending Updates] Setting pendingUpdateApplied flag to true');
    pendingUpdateApplied.current = true;

    try {
      if (pendingScene) {
        console.log('[Pending Updates] Applying pending scene:', {
          scene: pendingScene.scene,
          sceneMatches: pendingScene.sceneMatches,
          action: pendingScene.action
        });
        setScene(pendingScene);
        setPendingScene(null);
      }

      if (pendingSongs && pendingSongs.length > 0) {
        console.log('[Pending Updates] Applying pending songs:', {
          count: pendingSongs.length,
          firstSong: pendingSongs[0]?.title,
          lastSong: pendingSongs[pendingSongs.length - 1]?.title
        });
        setRecommendedSongs(pendingSongs);
        // DJ-129: reset the off-script highwater so the next playlist's
        // first track isn't compared against the old playlist's deep index.
        lastSeenTrackIndexRef.current = null;

        // Play all songs in the queue

        const uris = pendingSongs
          .map(song => song.spotifyTrackId)
          .filter(Boolean)
          .map(id => id.startsWith('spotify:track:') ? id : `spotify:track:${id}`);
        
        console.log('[Pending Updates] Preparing to play songs:', {
          urisCount: uris.length,
          firstUri: uris[0],
          lastUri: uris[uris.length - 1]
        });
        
        const token = localStorage.getItem('token');
        if (token && playerReady && deviceId) {
          console.log('[Pending Updates] All requirements met, initiating playback');
          try {
            // First pause to prevent auto-advance to next track in old playlist
            // (P1 #8) — use playerInstance.current (the live ref) instead of
            // the `player` closure value. After a toggle cycle, `player` in the
            // useCallback closure is the OLD (disconnected) instance; the ref
            // always points to the current live player.
            const livePlayer = playerInstance.current;
            if (livePlayer && typeof livePlayer.pause === 'function') {
              try {
                await livePlayer.pause();
                console.log('[Pending Updates] Paused current playback to prevent auto-advance');
                // Small delay to ensure pause takes effect
                await new Promise(resolve => setTimeout(resolve, 200));
              } catch (pauseError) {
                console.log('[Pending Updates] Could not pause via SDK:', pauseError.message);
              }
            }
            
            // Play directly on the device
            console.log('[Pending Updates] Playing new playlist directly on device:', deviceId);
            await axios.put(
              `https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`,
              { 
                uris,
                position_ms: 0,
                offset: { position: 0 }
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );
            // Promote the queued playlist ID — the new tracks are now
            // starting on the SDK, so playback_events from this point forward
            // belong to the new playlist. Update the ref synchronously to
            // avoid the one-render lag on the useEffect ref-syncer.
            if (queuedPlaylistIdRef.current) {
              currentPlaylistIdRef.current = queuedPlaylistIdRef.current;
              setCurrentPlaylistId(queuedPlaylistIdRef.current);
              queuedPlaylistIdRef.current = null;
            }
            currentSubVibeRef.current = queuedSubVibeRef.current;
            queuedSubVibeRef.current = null;
            setIsPlaying(true);
            console.log('[Pending Updates] SUCCESS - New playlist started playing');
          } catch (error) {
            console.error('[Pending Updates] ERROR during playback:', {
              status: error?.response?.status,
              data: error?.response?.data,
              message: error?.message,
              deviceId,
              urisCount: uris?.length
            });
            
            // Handle 401 Unauthorized errors
            if (error?.response?.status === 401) {
              console.log('[Pending Updates] Got 401 error, attempting to refresh token and retry');
              const newToken = await refreshAccessToken();
              if (newToken) {
                // Retry with fresh token
                try {
                  await axios.put(
                    `https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`,
                    { uris },
                    {
                      headers: {
                        Authorization: `Bearer ${newToken}`,
                      },
                    }
                  );
                  if (queuedPlaylistIdRef.current) {
                    currentPlaylistIdRef.current = queuedPlaylistIdRef.current;
                    setCurrentPlaylistId(queuedPlaylistIdRef.current);
                    queuedPlaylistIdRef.current = null;
                  }
                  currentSubVibeRef.current = queuedSubVibeRef.current;
                  queuedSubVibeRef.current = null;
                  setIsPlaying(true);
                  console.log('[Pending Updates] Successfully started playing new songs after token refresh');
                  return; // Success, don't clear pending state
                } catch (retryError) {
                  console.error('[Pending Updates] Error playing songs after token refresh:', retryError?.response?.data || retryError.message || retryError);
                }
              }
            } else if (error?.response?.status === 404) {
              // Device not found, try playing on any active device
              console.log('[Pending Updates] Device not found, attempting to play on any active device');
              try {
                await axios.put(
                  'https://api.spotify.com/v1/me/player/play',
                  { uris },
                  {
                    headers: {
                      Authorization: `Bearer ${token}`,
                    },
                  }
                );
                console.log('[Pending Updates] Successfully started playing on active device');
                if (queuedPlaylistIdRef.current) {
                  currentPlaylistIdRef.current = queuedPlaylistIdRef.current;
                  setCurrentPlaylistId(queuedPlaylistIdRef.current);
                  queuedPlaylistIdRef.current = null;
                }
                currentSubVibeRef.current = queuedSubVibeRef.current;
                queuedSubVibeRef.current = null;
                setIsPlaying(true);
                return; // Success, don't clear pending state
              } catch (fallbackError) {
                console.error('[Pending Updates] Fallback play failed:', fallbackError?.response?.data || fallbackError.message || fallbackError);
              }
            }

            // Even if playing fails, we should still clear the pending songs
            setPendingSongs(null);
            queuedPlaylistIdRef.current = null;
            queuedSubVibeRef.current = null;
            hasPendingUpdate.current = false;
            pendingUpdateApplied.current = false; // Reset flag on error
          }
        } else {
          console.warn('[Pending Updates] Cannot play songs - missing requirements:', {
            hasToken: !!token,
            playerReady,
            deviceId
          });
          // Clear pending state even if we couldn't play
          setPendingSongs(null);
          queuedPlaylistIdRef.current = null;
          queuedSubVibeRef.current = null;
          hasPendingUpdate.current = false;
          pendingUpdateApplied.current = false; // Reset flag on error
        }
      }

      // Only clear hasPendingUpdate if everything succeeded
      console.log('[Pending Updates] Clearing hasPendingUpdate flag');
      hasPendingUpdate.current = false;
      console.log('[Pending Updates] ====== APPLY PENDING UPDATES END - SUCCESS ======');
    } catch (error) {
      console.error('[Pending Updates] CRITICAL ERROR:', error);
      // Reset pending update flags on error to allow retry
      console.log('[Pending Updates] Resetting flags due to error');
      hasPendingUpdate.current = false;
      pendingUpdateApplied.current = false;
      // Clear pending state on error
      setPendingScene(null);
      setPendingSongs(null);
      queuedPlaylistIdRef.current = null;
      queuedSubVibeRef.current = null;
      console.log('[Pending Updates] ====== APPLY PENDING UPDATES END - ERROR ======');
    }
  }, [pendingScene, pendingSongs, playerReady, deviceId, setIsPlaying]);

  // Handle player state changes in a separate effect
  useEffect(() => {
    if (!player) return;

    let reconnectTimeout = null;
    let consecutiveNullStates = 0;
    const MAX_NULL_STATES = 3;
    const INITIAL_RECONNECT_DELAY = 1000;
    const MAX_RECONNECT_DELAY = 10000;
    let currentReconnectDelay = INITIAL_RECONNECT_DELAY;
    let reconnectAttempts = 0;
    const MAX_RECONNECT_ATTEMPTS = 5;
    let forceRefreshTimeout = null;
    let isCleaningUp = false; // Add cleanup flag

    // Add heartbeat check
    const startHeartbeat = () => {
      if (playerHeartbeatInterval.current) {
        clearInterval(playerHeartbeatInterval.current);
      }
      
      playerHeartbeatInterval.current = setInterval(async () => {
        if (isCleaningUp) return; // Skip if cleaning up
        try {
          // Check if player still exists before calling methods
          if (!player || typeof player.getCurrentState !== 'function') {
            console.log('[Spotify SDK] Player no longer available, stopping heartbeat');
            clearInterval(playerHeartbeatInterval.current);
            return;
          }
          const state = await player.getCurrentState();
          // [Audio Heartbeat] diagnostic: log every 5s while we have a valid SDK state.
          // Also detects position stalls (>=10s same position while not paused) and
          // pause flips. Piggybacks on the existing 5s tick — no new interval.
          if (state) {
            const trackId = state.track_window?.current_track?.id || null;
            const trackUri = state.track_window?.current_track?.uri || null;
            const nowTs = Date.now();
            const prev = lastHeartbeatPositionRef.current;

            console.warn('[Audio Heartbeat]', {
              position: state.position,
              paused: state.paused,
              loading: state.loading,
              trackId,
              trackUri,
              durationLeft: (state.duration || 0) - (state.position || 0),
              ts: nowTs,
            });

            // Stall detect: position not advancing while not paused, same track, ≥10s.
            // Rate-limited to one log every 10s while stuck.
            if (!state.paused && prev.position !== null && state.position === prev.position && prev.trackId === trackId) {
              const elapsed = nowTs - prev.sampledAt;
              const sinceLastStallLog = nowTs - prev.stallLoggedAt;
              if (elapsed >= 10000 && sinceLastStallLog >= 10000) {
                console.warn('[Audio Stall]', {
                  position: state.position,
                  trackId,
                  stuckForMs: elapsed,
                  duration: state.duration,
                  loading: state.loading,
                });
                lastHeartbeatPositionRef.current = { ...prev, stallLoggedAt: nowTs };
              }
            } else {
              lastHeartbeatPositionRef.current = {
                position: state.position,
                trackId,
                sampledAt: nowTs,
                stallLoggedAt: prev.stallLoggedAt,
              };
            }

            // Pause-flip detection (heartbeat-driven; pairs with sdk_event detection in handlePlayerStateChanged)
            if (lastPausedFlagRef.current !== null && lastPausedFlagRef.current !== state.paused) {
              console.warn('[Pause Flip]', {
                from: lastPausedFlagRef.current,
                to: state.paused,
                position: state.position,
                trackId,
                trigger: 'heartbeat',
              });
            }
            lastPausedFlagRef.current = state.paused;
          }
          if (!state && !playerStateLock.current && lastValidState.current) {
            // Before recovering, check if another device took over
            const devices = await checkAvailableDevices('Heartbeat - Null State');
            if (devices) {
              const anotherActive = devices.some(d => d.is_active && d.id !== deviceId);
              if (anotherActive && !djYieldedRef.current) {
                enterYieldedState();
                return;
              }
            }
            console.log('[Spotify SDK] Heartbeat detected invalid state, attempting recovery');
            await recoverPlayerState();
          }
        } catch (error) {
          console.error('[Spotify SDK] Heartbeat check failed:', error);
          // Stop heartbeat if player is no longer available
          if (error.message && error.message.includes('is not a function')) {
            clearInterval(playerHeartbeatInterval.current);
          }
        }
      }, 5000);
    };

    const recoverPlayerState = async () => {
      if (playerStateLock.current || isCleaningUp) {
        console.log('[Spotify SDK] State recovery blocked by lock or cleanup');
        return;
      }

      if (!deviceId) {
        console.warn('[Spotify SDK] Recovery skipped: no device ID.');
        return;
      }

      // Verify player still exists
      if (!player || typeof player.connect !== 'function') {
        console.warn('[Spotify SDK] Player no longer available for recovery');
        return;
      }

      playerStateLock.current = true;
      console.log('[Spotify SDK] Attempting state recovery');

      try {
        await player.connect();
        
        if (lastValidState.current && lastValidState.current.track_window?.current_track && recommendedSongs.length > 0) {
          const { uri } = lastValidState.current.track_window.current_track;
          const { position, paused } = lastValidState.current;
          
          const uris = recommendedSongs
            .map(song => song.spotifyTrackId)
            .filter(Boolean)
            .map(id => id.startsWith('spotify:track:') ? id : `spotify:track:${id}`);

          if (uris.includes(uri)) {
            console.log(`[Spotify SDK] Recovering playback for track ${uri} at ${position}ms. Was paused: ${paused}`);
            
            const token = localStorage.getItem('token');
            await axios.put(
              `https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`,
              {
                uris,
                offset: { uri },
                position_ms: position,
              },
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            );

            if (paused && player && typeof player.pause === 'function') {
              setTimeout(() => {
                if (!isCleaningUp && player && typeof player.pause === 'function') {
                  player.pause().catch(e => console.error('[Spotify SDK] Error pausing after recovery:', e));
                }
              }, 500);
            }
          } else {
             console.warn('[Spotify SDK] Last played track not in current recommended list. Attempting to play from start of list.');
             await playAllSongs();
          }
        } else {
          console.warn('[Spotify SDK] No valid last state or recommendations to recover with. Will not start playback.');
        }
      } catch (error) {
        console.error('[Spotify SDK] State recovery failed:', error.response ? error.response.data : error.message);
      } finally {
        playerStateLock.current = false;
      }
    };

    const enterYieldedState = () => {
      if (djYieldedRef.current) return; // Already yielded

      console.log('[Device Transfer] Another device is active — DJ yielding control');

      // Save playlist context for resume
      yieldedPlaylistContext.current = {
        recommendedSongs: [...recommendedSongs],
        currentSongIndex: lastProcessedState.current.trackId,
        lastValidState: lastValidState.current,
      };

      djYieldedRef.current = true;
      setDjYielded(true);
      setIsPlaying(false);

      // Stop heartbeat
      if (playerHeartbeatInterval.current) {
        clearInterval(playerHeartbeatInterval.current);
        playerHeartbeatInterval.current = null;
      }

      // Stop progress interval
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
        progressInterval.current = null;
      }

      toast.info('Music is playing on another device. Tap Resume DJ to take back control.', {
        autoClose: 8000,
      });
    };

    const forcePlayerRefresh = async () => {
      if (playerStateLock.current) {
        console.log('[Spotify SDK] Force refresh blocked by lock');
        return;
      }

      playerStateLock.current = true;
      console.log('[Spotify SDK] Attempting to force refresh player');
      
      try {
        await player.disconnect();
        await new Promise(resolve => setTimeout(resolve, 1000));
        await player.connect();
        
        // Try to restore last known good state
        if (lastValidState.current && !lastValidState.current.paused) {
          await player.resume();
          if (lastValidState.current.position) {
            await player.seek(lastValidState.current.position);
          }
        }
        
        console.log('[Spotify SDK] Force refresh completed');
      } catch (error) {
        console.error('[Spotify SDK] Force refresh failed:', error);
      } finally {
        playerStateLock.current = false;
      }
    };

    const handlePlayerStateChanged = async (state) => {
      // Check if we're cleaning up
      if (isCleaningUp) {
        console.log('[Spotify SDK] Ignoring state change during cleanup');
        return;
      }

      // Debounce rapid state changes
      const now = Date.now();
      const currentTrackId = state?.track_window?.current_track?.id;
      const currentPosition = state?.position;
      
      // Check if this is a duplicate state within 50ms
      if (lastProcessedState.current.trackId === currentTrackId &&
          lastProcessedState.current.position === currentPosition &&
          lastProcessedState.current.timestamp &&
          (now - lastProcessedState.current.timestamp) < 50) {
        console.log('[Player State] Ignoring duplicate state change');
        return;
      }

      // Pause-flip detection (event-driven; complements heartbeat-driven detection
      // in startHeartbeat). Pure observability — does not gate downstream logic.
      if (state && lastPausedFlagRef.current !== null && lastPausedFlagRef.current !== state.paused) {
        console.warn('[Pause Flip]', {
          from: lastPausedFlagRef.current,
          to: state.paused,
          position: state.position,
          trackId: state.track_window?.current_track?.id || null,
          trigger: 'sdk_event',
        });
      }
      if (state) {
        lastPausedFlagRef.current = state.paused;
      }

      // Clear any pending state transition timeout
      if (stateTransitionTimeout.current) {
        clearTimeout(stateTransitionTimeout.current);
      }

      // Check for invalid state conditions
      const isInvalidState = state && (
        state.duration === 0 ||
        (state.track_window && !state.track_window.current_track) ||
        (state.paused && state.position === 0 && state.duration === 0)
      );

      if (!state || isInvalidState) {
        console.log('[Spotify SDK] Received invalid state:', {
          isNull: !state,
          duration: state?.duration,
          hasCurrentTrack: state?.track_window?.current_track ? true : false,
          position: state?.position,
          paused: state?.paused,
          locked: playerStateLock.current
        });
        
        // If we're locked, wait for lock to clear
        if (playerStateLock.current) {
          console.log('[Spotify SDK] State change blocked by lock, will retry');
          stateTransitionTimeout.current = setTimeout(() => {
            if (!isCleaningUp) {
              handlePlayerStateChanged(state);
            }
          }, 500);
          return;
        }

        consecutiveNullStates++;
        
        if (consecutiveNullStates >= MAX_NULL_STATES) {
          if (lastValidState.current) {
            // Check if another device took over before attempting recovery
            const devices = await checkAvailableDevices('State Handler - Consecutive Nulls');
            if (devices) {
              const anotherActive = devices.some(d => d.is_active && d.id !== deviceId);
              if (anotherActive && !djYieldedRef.current) {
                enterYieldedState();
                return;
              }
            }
            console.log('[Spotify SDK] Attempting recovery using last valid state');
            await recoverPlayerState();
            return;
          }

          // Continue with existing reconnection logic...
          // ... (keep existing reconnection code here)
        }
        return;
      }

      // We have a valid state
      consecutiveNullStates = 0;
      reconnectAttempts = 0;
      currentReconnectDelay = INITIAL_RECONNECT_DELAY;

      // Save previous state BEFORE overwriting (needed for external skip detection)
      // Only done here (after null/invalid check) so bad states don't destroy saved position
      const prevProcessedPosition = lastProcessedState.current.position;
      const prevDuration = lastValidState.current?.duration;
      const prevTimestamp = lastProcessedState.current.timestamp;
      const prevPaused = lastValidState.current?.paused;

      // Now safe to update
      lastProcessedState.current = {
        trackId: currentTrackId,
        position: currentPosition,
        timestamp: now
      };
      lastValidState.current = state;

      // Update playing state and track information
      setIsPlaying(!state.paused);

      // Pause/resume tracking is handled directly in handlePlayPause() —
      // the SDK state listener was removed here because it fired duplicates on mobile.
      lastPausedState.current = state.paused;

      // Update current track information
      if (state.track_window?.current_track) {
        const currentTrack = state.track_window.current_track;
        const previousTrack = currentlyPlaying;
        
        // Log track transition
        if (!currentlyPlaying || currentlyPlaying.title !== currentTrack.name) {
          console.log('[Player State] TRACK CHANGE DETECTED:', {
            previous: previousTrack ? `${previousTrack.title} - ${previousTrack.artist}` : 'None',
            current: `${currentTrack.name} - ${currentTrack.artists.map(a => a.name).join(', ')}`,
            position: state.position,
            paused: state.paused,
            hasPendingUpdate: hasPendingUpdate.current,
            pendingSongsCount: pendingSongs?.length || 0,
            timestamp: new Date().toISOString()
          });
        }
        
        // Spotify Track Relinking (DJ-116): for users in markets where the
        // track was relinked, current_track.id is the relinked id with
        // potentially different metadata. linked_from.id is the original
        // un-relinked id that matches what our backend stored in playlists.
        const canonicalTrackId = currentTrack.linked_from?.id || currentTrack.id;

        setCurrentlyPlaying({
          title: currentTrack.name,
          artist: currentTrack.artists.map(a => a.name).join(', '),
          albumArt: currentTrack.album?.images[0]?.url,
          albumName: currentTrack.album?.name,
          spotifyTrackId: canonicalTrackId
        });

        // Track when a new song starts playing (once per unique song)
        const trackId = canonicalTrackId;
        const songName = currentTrack.name;
        const firstArtistName = currentTrack.artists && currentTrack.artists.length > 0 ? currentTrack.artists[0].name : null;

        if (trackId && songName && firstArtistName) {
          if (trackId !== loggedPlayedTrackId.current) {
            console.log(`[Player] New unique track detected. ID: ${trackId}, Previously Logged ID: ${loggedPlayedTrackId.current}`);

            // External skip detection
            if (skipInitiatedByApp.current) {
              // App-initiated forward skip — handlePlayNextSong already posted to /track-skip
              console.log('[External Skip] Track change was app-initiated (forward), skipping external skip detection');
              skipInitiatedByApp.current = false;
            } else if (backwardSkipInitiatedByApp.current) {
              // App-initiated backward skip — handlePlayPreviousSong already posted to /track-skip
              console.log('[External Skip] Track change was app-initiated (backward), skipping external skip detection');
              backwardSkipInitiatedByApp.current = false;
            } else if (loggedPlayedTrackId.current !== null) {
              // Track changed without app initiation — could be external skip or natural completion
              // Use saved values from BEFORE lastProcessedState was overwritten
              // Get previous track info from SDK state (more reliable than closure state)
              const prevTracks = state.track_window?.previous_tracks;
              const sdkPrevTrack = prevTracks && prevTracks.length > 0 ? prevTracks[0] : null;
              const prevTrackName = sdkPrevTrack?.name || currentlyPlaying?.title;
              const prevTrackArtist = sdkPrevTrack?.artists?.[0]?.name || currentlyPlaying?.artist?.split(', ')[0];
              const prevTrackId = sdkPrevTrack?.linked_from?.id || sdkPrevTrack?.id || currentlyPlaying?.spotifyTrackId || null;

              if (prevProcessedPosition != null && prevDuration != null && prevDuration > 0) {
                // Estimate actual position by compensating for time since last SDK state update
                let estimatedPosition = prevProcessedPosition;
                if (!prevPaused && prevTimestamp) {
                  const elapsed = now - prevTimestamp;
                  estimatedPosition = Math.min(prevProcessedPosition + elapsed, prevDuration);
                }
                const wasNearEnd = estimatedPosition >= (prevDuration - 5000);

                if (!wasNearEnd) {
                  console.log('[External Skip] Detected external skip:', {
                    previousTrack: prevTrackName ? `${prevTrackName} - ${prevTrackArtist}` : 'Unknown',
                    rawPosition: prevProcessedPosition,
                    estimatedPosition: estimatedPosition,
                    duration: prevDuration,
                    remainingMs: prevDuration - estimatedPosition,
                    elapsed: now - prevTimestamp
                  });

                  const skipEmail = localStorage.getItem('user_email');
                  if (skipEmail && prevTrackName && prevTrackArtist) {
                    axios.post(`${API_BASE_URL}/track-skip`, {
                      email: skipEmail,
                      songName: prevTrackName,
                      artistName: prevTrackArtist,
                      spotifyTrackId: prevTrackId,
                      source: 'external',
                      origin: actionOriginRef.current,
                      action: 'skip_forward',
                      position: prevProcessedPosition,
                      duration: prevDuration,
                      sessionId: sessionIdRef.current,
                      playlistId: currentPlaylistIdRef.current,
                      subVibe: currentSubVibeRef.current ?? queuedSubVibeRef.current ?? null
                    }).then((response) => {
                      handleFeedbackRegenRef.current?.(response?.data);
                    }).catch((err) => {
                      console.error('[External Skip] Error sending external skip data:', err.message);
                    });
                  }
                } else {
                  console.log('[External Skip] Track completed naturally (was near end)', {
                    rawPosition: prevProcessedPosition,
                    estimatedPosition: estimatedPosition,
                    duration: prevDuration,
                    elapsed: now - prevTimestamp
                  });
                }
              }
            }

            loggedPlayedTrackId.current = trackId;

            const userEmail = localStorage.getItem('user_email');
            try {
              console.log(`[Player] Sending 'track-playing' data to API for: ${songName} - ${firstArtistName} (ID: ${trackId})`);
              await axios.post(`${API_BASE_URL}/track-playing`, {
                email: userEmail,
                songName: songName,
                artistName: firstArtistName,
                spotifyTrackId: trackId,
                sessionId: sessionIdRef.current,
                playlistId: currentPlaylistIdRef.current,
                subVibe: currentSubVibeRef.current ?? queuedSubVibeRef.current ?? null,
                origin: actionOriginRef.current
              });
              console.log(`[Player] Successfully sent 'track-playing' data for ID: ${trackId}`);
            } catch (apiError) {
              console.error(`[Player] Error sending 'track-playing' data for ID: ${trackId}:`, apiError.response ? apiError.response.data : apiError.message);
            }
          }
        }

        // Find the index of current track in recommendedSongs
        const trackIndex = recommendedSongs.findIndex(
          song => song.spotifyTrackId === currentTrack.id
        );
        if (trackIndex !== -1) {
          setCurrentSongIndex(trackIndex);
          // DJ-129: snapshot the prior highwater BEFORE we update it, so
          // the wrap-around comparison below can see "we were at track N,
          // now we're at track 0". Update is max-only so a Previous press
          // doesn't drop the highwater and mask a real wrap-around.
          const priorHighwater = lastSeenTrackIndexRef.current;
          if (priorHighwater === null || trackIndex > priorHighwater) {
            lastSeenTrackIndexRef.current = trackIndex;
          }

          // Pre-fetch continuation playlist when near end of playlist
          // Check both SDK next_tracks AND our own trackIndex to avoid false triggers on playlist load
          if (!state.paused
              && state.track_window?.next_tracks?.length <= 1
              && trackIndex >= recommendedSongs.length - 2
              && recommendedSongs.length > 2
              && !hasPendingUpdate.current
              && !isGeneratingPlaylistRef.current
              && !pendingPlaylistIdRef.current
              && !isContinuationInProgressRef.current) {
            console.log('[Continuation] Near end of playlist (<=1 song remaining, track ' + (trackIndex + 1) + '/' + recommendedSongs.length + '), pre-fetching next playlist');
            if (requestContinuationPlaylistRef.current) {
              requestContinuationPlaylistRef.current();
            }
          }

          // Apply pending update if last song ended/paused and a continuation playlist is waiting
          if (state.paused
              && state.track_window?.next_tracks?.length === 0
              && trackIndex >= recommendedSongs.length - 1
              && hasPendingUpdate.current
              && !pendingUpdateApplied.current) {
            console.log('[Continuation] Last song ended with pending update, applying now');
            applyPendingUpdates();
          }

          // Fallback: if last song finished and no continuation was triggered
          if (state.paused
              && state.track_window?.next_tracks?.length === 0
              && trackIndex >= recommendedSongs.length - 1
              && recommendedSongs.length > 2
              && state.duration > 0
              && state.position >= state.duration - 1000
              && !hasPendingUpdate.current
              && !isGeneratingPlaylistRef.current
              && !pendingPlaylistIdRef.current
              && !isContinuationInProgressRef.current) {
            console.log('[Continuation] Fallback: last song ended, requesting continuation');
            if (requestContinuationPlaylistRef.current) {
              requestContinuationPlaylistRef.current();
            }
          }

          // DJ-129: wrap-around detector. Spotify often plays our queue
          // through, then loops back to track 1 of the SAME playlist
          // without pausing — meaning the apply paths above never fire.
          // If we observe a jump back to track 0 from near the end while a
          // continuation is pending, swap into the new playlist.
          if (hasPendingUpdate.current
              && !pendingUpdateApplied.current
              && priorHighwater !== null
              && priorHighwater >= recommendedSongs.length - 2
              && trackIndex === 0
              && priorHighwater !== 0) {
            console.log(
              '[Continuation] Wrap-around detected — Spotify looped from track ' +
              (priorHighwater + 1) + ' to track 1/' + recommendedSongs.length +
              '. Applying pending continuation now.'
            );
            applyPendingUpdates();
          }
        }
      }

      // Safety net: if playlist ended and Spotify looped back but a pending continuation exists,
      // apply the pending update to load the correct continuation playlist
      if (state.paused
          && state.position < 1000
          && hasPendingUpdate.current
          && !pendingUpdateApplied.current
          && prevDuration > 0
          && prevProcessedPosition >= prevDuration - 3000) {
        console.log('[Continuation] Playlist ended with pending continuation, applying pending update');
        applyPendingUpdates();
      }

      // DJ-129: off-script (autoplay) detector. The 3 apply paths above
      // gate on state.paused — but Spotify often does NOT pause at end
      // of queue. The "wrap-around" variant (Spotify loops to track 1
      // of the same playlist) is handled inside the `trackIndex !== -1`
      // block above. This branch handles the "autoplay" variant
      // (Thriller-during-Deep-Focus): the SDK reports a track that is
      // NOT in our recommendedSongs at all.
      if (state.track_window?.current_track
          && hasPendingUpdate.current
          && !pendingUpdateApplied.current
          && lastSeenTrackIndexRef.current !== null) {
        const newId = state.track_window.current_track.id;
        const trackIsOffPlaylist = newId && !recommendedSongs.some(s => s.spotifyTrackId === newId);
        if (trackIsOffPlaylist) {
          console.log(
            '[Continuation] Off-script (autoplay) detected — current track ' + newId +
            ' is not in recommendedSongs (last seen index=' + lastSeenTrackIndexRef.current +
            '/' + recommendedSongs.length + '). Applying pending continuation now.'
          );
          applyPendingUpdates();
        }
      }

      // Update track duration and progress
      if (state && state.duration > 0) {
        setTrackDuration(state.duration);
        setTrackProgress(state.position);
        
        // Calculate time remaining
        const timeRemaining = state.duration - state.position;
        const percentagePlayed = (state.position / state.duration) * 100;
        
        // Apply pending updates when 0.5-1 seconds remain
        if (!state.paused && timeRemaining <= 1000 && timeRemaining > 500 && hasPendingUpdate.current && !pendingUpdateApplied.current) {
          console.log('[Player State] TRIGGERING PENDING UPDATE:', {
            timeRemaining,
            secondsRemaining: (timeRemaining / 1000).toFixed(1),
            position: state.position,
            duration: state.duration,
            currentTrack: state.track_window?.current_track?.name,
            pendingUpdateApplied: pendingUpdateApplied.current,
            hasPendingUpdate: hasPendingUpdate.current,
            pendingSongsCount: pendingSongs?.length || 0
          });
          applyPendingUpdates();
        }
        
        // Log when song is about to end (within last 10 seconds) while still playing
        if (!state.paused && timeRemaining <= 10000 && timeRemaining > 0 && state.track_window?.current_track) {
          const currentTrack = state.track_window.current_track;
          console.log('[Song Progress] Track approaching end:', {
            name: currentTrack.name,
            artist: currentTrack.artists.map(a => a.name).join(', '),
            time_remaining_ms: timeRemaining,
            time_remaining_seconds: (timeRemaining / 1000).toFixed(1),
            position_ms: state.position,
            duration_ms: state.duration,
            percentage_played: percentagePlayed.toFixed(2) + '%',
            is_playing: !state.paused
          });
        }

        // Reset the pending update applied flag when a new track starts
        if (state.position < 1000) {
          const trackName = state.track_window?.current_track?.name;
          const trackId = state.track_window?.current_track?.id;
          const now = Date.now();
          
          // Check if we've already logged this track start recently
          const lastLog = lastProcessedState.current.lastNewTrackLog;
          const shouldLog = trackId && 
            (!lastLog || 
             lastLog.trackId !== trackId ||
             (now - lastLog.timestamp) > 5000);
          
          if (shouldLog) {
            // Clear any existing timer for a different track
            if (newTrackDebounceTimer.current) {
              clearTimeout(newTrackDebounceTimer.current);
              newTrackDebounceTimer.current = null;
            }
            
            // Record immediately to prevent duplicate timers
            lastProcessedState.current.lastNewTrackLog = {
              trackId: trackId,
              timestamp: now,
              logged: false
            };
            
            // Set a single timer for this track
            newTrackDebounceTimer.current = setTimeout(() => {
              // Only log if we haven't already logged this track
              const currentLog = lastProcessedState.current.lastNewTrackLog;
              if (currentLog && currentLog.trackId === trackId && !currentLog.logged) {
                currentLog.logged = true;
                
                // Log once
                console.log('[Player State] NEW TRACK STARTED:', {
                  trackName: trackName,
                  trackId: trackId,
                  position: state.position,
                  pendingUpdateApplied: pendingUpdateApplied.current,
                  hasPendingUpdate: hasPendingUpdate.current,
                  isPlaying: !state.paused
                });
                if (!hasPendingUpdate.current) {
                  console.log('[Player State] Resetting pendingUpdateApplied flag');
                  pendingUpdateApplied.current = false;
                }
              }
              newTrackDebounceTimer.current = null;
            }, 200); // Wait 200ms to collect all rapid state changes
          }
        }
        
        // Clear existing interval
        if (progressInterval.current) {
          clearInterval(progressInterval.current);
        }
        
        // Start new interval if track is playing
        if (!state.paused) {
          progressInterval.current = setInterval(() => {
            setTrackProgress(prev => {
              const newProgress = prev + 1000;
              const timeRemaining = state.duration - newProgress;
              
              // Check for pending updates in the interval as well
              if (timeRemaining <= 1000 && timeRemaining > 500 && hasPendingUpdate.current && !pendingUpdateApplied.current) {
                console.log('[Progress Interval] Detected pending update window (0.5-1 seconds remaining)', {
                  timeRemaining,
                  secondsRemaining: (timeRemaining / 1000).toFixed(1),
                  hasPendingScene: !!pendingScene,
                  hasPendingSongs: !!pendingSongs
                });
                
                Promise.resolve().then(() => {
                  if (!pendingUpdateApplied.current) {
                    applyPendingUpdates();
                  }
                });
              }
              
              if (newProgress >= state.duration) {
                clearInterval(progressInterval.current);
                console.log('[Progress Interval] Track completed');
                return prev;
              }
              return newProgress;
            });
          }, 1000);
        }
      }

      // Rest of the existing player state change handler code...
    };

    // Start heartbeat when setting up effect (skip if DJ has yielded to another device)
    if (!djYieldedRef.current) {
      startHeartbeat();
    }

    // Create safe event handlers that check cleanup state
    const safePlayerStateChanged = (state) => {
      if (!isCleaningUp) {
        handlePlayerStateChanged(state);
      }
    };

    const safePlaybackError = async ({ message }) => {
      if (!isCleaningUp) {
        console.error('[Spotify SDK] Playback Error:', message);
        if (!playerStateLock.current && !djYieldedRef.current) {
          await recoverPlayerState();
        }
      }
    };

    player.addListener('player_state_changed', safePlayerStateChanged);
    player.addListener('playback_error', safePlaybackError);

    return () => {
      isCleaningUp = true; // Set cleanup flag first
      
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      if (forceRefreshTimeout) {
        clearTimeout(forceRefreshTimeout);
      }
      if (playerHeartbeatInterval.current) {
        clearInterval(playerHeartbeatInterval.current);
      }
      if (stateTransitionTimeout.current) {
        clearTimeout(stateTransitionTimeout.current);
      }
      if (newTrackDebounceTimer.current) {
        clearTimeout(newTrackDebounceTimer.current);
      }
      
      // Safely remove listeners
      if (player && typeof player.removeListener === 'function') {
        try {
          player.removeListener('player_state_changed', safePlayerStateChanged);
          player.removeListener('playback_error', safePlaybackError);
        } catch (error) {
          console.error('[Spotify SDK] Error removing listeners:', error);
        }
      }
    };
  }, [player, recommendedSongs, playAllSongs, applyPendingUpdates, isPlaying]);

  // DJ-129: catch the late-arrival case. If pre-fetch finishes AFTER
  // Spotify already wrapped to track 0 of the old playlist, no state
  // event will fire to trigger the wrap-around detector. Watch for the
  // moment pendingSongs lands and apply immediately if we're already
  // sitting in the wrap-around state.
  useEffect(() => {
    if (pendingSongs?.length > 0
        && hasPendingUpdate.current
        && !pendingUpdateApplied.current
        && lastSeenTrackIndexRef.current !== null
        && recommendedSongs.length > 2
        && lastSeenTrackIndexRef.current >= recommendedSongs.length - 2
        && currentSongIndex === 0) {
      console.log(
        '[Continuation] Pending continuation arrived AFTER wrap-around (highwater ' +
        (lastSeenTrackIndexRef.current + 1) + '/' + recommendedSongs.length +
        ', now on track 1). Applying immediately.'
      );
      applyPendingUpdates();
    }
  }, [pendingSongs, currentSongIndex, recommendedSongs.length, applyPendingUpdates]);

  useEffect(() => {
    console.log('[App] playerReady:', playerReady, 'deviceId:', deviceId);
    if (player && playerReady) {
      player.setVolume(volume);
      // Reset isLoading when player becomes ready to fix camera after token refresh
      if (isLoading) {
        console.log('[App] Player ready, resetting isLoading state');
        setIsLoading(false);
      }
    }
  }, [player, playerReady, volume, deviceId]); // Removed isLoading from dependencies

  // Track device lifecycle changes
  useEffect(() => {
    if (deviceId) {
      deviceLifecycleRef.current.deviceSetAt = Date.now();
      console.log('[Device Lifecycle] Device ID set:', {
        deviceId,
        timestamp: new Date().toISOString(),
        playerReady,
        timeSinceLastLoss: deviceLifecycleRef.current.lastDeviceCheck 
          ? `${Math.floor((Date.now() - deviceLifecycleRef.current.lastDeviceCheck) / 1000)}s`
          : 'N/A'
      });
      
      // Start periodic device health checks
      if (!deviceLifecycleRef.current.deviceCheckInterval) {
        console.log('[Device Lifecycle] Starting periodic device health checks');
        
        // Do immediate check
        checkAvailableDevices('Device Set - Initial Check');
        
        // Then check every 60 seconds — also detect device transfers as a safety net
        deviceLifecycleRef.current.deviceCheckInterval = setInterval(async () => {
          const devices = await checkAvailableDevices('Periodic Health Check');
          if (devices && !djYieldedRef.current && isMasterEnabledRef.current) {
            const anotherActive = devices.some(d => d.is_active && d.id !== deviceId);
            if (anotherActive) {
              console.log('[Device Transfer] Periodic check detected another active device — yielding');
              djYieldedRef.current = true;
              setDjYielded(true);
              setIsPlaying(false);
              if (playerHeartbeatInterval.current) {
                clearInterval(playerHeartbeatInterval.current);
                playerHeartbeatInterval.current = null;
              }
              if (progressInterval.current) {
                clearInterval(progressInterval.current);
                progressInterval.current = null;
              }
              toast.info('Music is playing on another device. Tap Resume DJ to take back control.', {
                autoClose: 8000,
              });
            }
          }
        }, 60000);
      }
    } else {
      if (deviceLifecycleRef.current.deviceSetAt) {
        const deviceUptime = Date.now() - deviceLifecycleRef.current.deviceSetAt;
        console.warn('[Device Lifecycle] Device ID lost:', {
          wasActiveFor: `${Math.floor(deviceUptime / 1000)}s`,
          lastSuccessfulPlayback: deviceLifecycleRef.current.lastSuccessfulPlayback
            ? `${Math.floor((Date.now() - deviceLifecycleRef.current.lastSuccessfulPlayback) / 1000)}s ago`
            : 'Never',
          playerReady,
          timestamp: new Date().toISOString()
        });
      }
      
      // Clear the health check interval
      if (deviceLifecycleRef.current.deviceCheckInterval) {
        console.log('[Device Lifecycle] Stopping device health checks');
        clearInterval(deviceLifecycleRef.current.deviceCheckInterval);
        deviceLifecycleRef.current.deviceCheckInterval = null;
      }
    }
    
    // Cleanup on unmount
    return () => {
      if (deviceLifecycleRef.current.deviceCheckInterval) {
        clearInterval(deviceLifecycleRef.current.deviceCheckInterval);
        deviceLifecycleRef.current.deviceCheckInterval = null;
      }
    };
  }, [deviceId, playerReady, checkAvailableDevices]);

  // Auto start webcam and take snapshot when player is ready, then every 1 minute
  useEffect(() => {
    let intervalId;
    let snapshotTimerId;

    if (playerReady && videoRef.current && isMasterEnabled && !djYielded) {
      // Only start the webcam if there isn't an active stream
      // We check the videoRef's srcObject to see if a stream is attached.
      if (!videoRef.current.srcObject) {
        console.log('[Webcam] Player ready and no stream active, attempting to start webcam');
        startWebcam().catch((err) => {
          console.log('[Webcam] Error starting webcam:', err);
          if (
            err &&
            (err.name === "NotAllowedError" ||
              err.message?.toLowerCase().includes("gesture"))
          ) {
            console.warn('[Webcam] Camera access requires user gesture');
          }
        });
      }

      // Wait for video to be ready, then take first snapshot and set up interval
      const video = videoRef.current;
      const trySnapshot = () => {
        console.log('[Webcam] Checking video readiness:', {
          readyState: video.readyState,
          width: video.videoWidth,
          height: video.videoHeight
        });
        
        if (
          video.srcObject && // Ensure the stream is attached
          video.readyState >= 2 &&
          video.videoWidth > 0 &&
          video.videoHeight > 0
        ) {
          // Check if playlist generation is in progress before taking first snapshot
          if (isGeneratingPlaylistRef.current || pendingPlaylistIdRef.current) {
            console.log('[Webcam] Skipping first snapshot - playlist generation in progress');
          } else {
            console.log('[Webcam] Taking first snapshot');
            takeSnapshot();
          }
          // Set up interval for subsequent snapshots
          intervalId = setInterval(() => {
            if (isMasterEnabled) {
              // Check if playlist generation is in progress before taking snapshot
              if (isGeneratingPlaylistRef.current || pendingPlaylistIdRef.current) {
                console.log('[Webcam] Skipping periodic snapshot - playlist generation in progress');
                return;
              }
              console.log('[Webcam] Taking periodic snapshot');
              takeSnapshot();
            }
          }, 60000); 
        } else {
          console.log('[Webcam] Video not ready, retrying in 200ms');
          snapshotTimerId = setTimeout(trySnapshot, 200);
        }
      };
      trySnapshot();
    } else {
      console.log('[Webcam] Not starting - playerReady:', playerReady, 'videoRef exists:', !!videoRef.current, 'isMasterEnabled:', isMasterEnabled, 'djYielded:', djYielded);
    }

    return () => {
      if (intervalId) {
        console.log('[Webcam] Cleaning up interval');
        clearInterval(intervalId);
      }
      if (snapshotTimerId) {
        console.log('[Webcam] Cleaning up snapshot timer');
        clearTimeout(snapshotTimerId);
      }
    };
  }, [playerReady, isMasterEnabled, djYielded]);

  // Activation handler — satisfies browser autoplay policy with a user gesture
  const handleActivateDJ = useCallback(() => {
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      if (audioCtx.state === 'suspended') {
        audioCtx.resume();
      }
    } catch (e) {
      console.log('[Activation] AudioContext resume failed:', e);
    }
    sessionStorage.setItem('dj_audio_activated', 'true');
    setIsMasterEnabled(true);
  }, []);

  // Resume DJ control after yielding to another device
  const resumeDJControl = useCallback(async () => {
    console.log('[Device Transfer] Resuming DJ control');

    const token = localStorage.getItem('token');
    let currentTrackUri = null;
    let currentPositionMs = 0;

    // Grab the current playback state from the other device before transferring
    try {
      if (token) {
        const playbackRes = await axios.get(
          'https://api.spotify.com/v1/me/player',
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (playbackRes.data && playbackRes.data.item) {
          currentTrackUri = playbackRes.data.item.uri;
          currentPositionMs = playbackRes.data.progress_ms || 0;
          console.log('[Device Transfer] Captured position from other device:', {
            track: playbackRes.data.item.name,
            position: currentPositionMs,
            uri: currentTrackUri
          });
        }
      }
    } catch (error) {
      console.warn('[Device Transfer] Could not get current playback state:', error.response?.status || error.message);
    }

    // Transfer playback back to our device
    try {
      if (token && deviceId) {
        await axios.put(
          'https://api.spotify.com/v1/me/player',
          { device_ids: [deviceId], play: false },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
    } catch (error) {
      console.error('[Device Transfer] Error transferring playback back:', error.response?.data || error.message);
    }

    // Clear yielded state
    djYieldedRef.current = false;
    setDjYielded(false);
    yieldedPlaylistContext.current = null;

    // Resume playback from the exact position
    if (recommendedSongs.length > 0 && token && deviceId) {
      const uris = recommendedSongs
        .map(song => song.spotifyTrackId)
        .filter(Boolean)
        .map(id => id.startsWith('spotify:track:') ? id : `spotify:track:${id}`);

      // If the track from the other device is in our playlist, resume there
      const resumeUri = currentTrackUri && uris.includes(currentTrackUri) ? currentTrackUri : null;

      try {
        await axios.put(
          `https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`,
          {
            uris,
            offset: resumeUri ? { uri: resumeUri } : { position: 0 },
            position_ms: resumeUri ? currentPositionMs : 0,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('[Device Transfer] Resumed playback', resumeUri
          ? `at ${Math.round(currentPositionMs / 1000)}s into ${resumeUri}`
          : 'from start of playlist');
      } catch (error) {
        console.error('[Device Transfer] Error resuming playback, falling back to playAllSongs:', error.response?.data || error.message);
        await playAllSongs();
      }
    } else if (recommendedSongs.length > 0) {
      await playAllSongs();
    }

    toast.success('DJ is back in control!', { autoClose: 3000 });
  }, [deviceId, recommendedSongs, playAllSongs]);

  const handleMuteToggle = useCallback(() => {
    if (isMuted) {
      // Unmute: restore previous volume
      setIsMuted(false);
      setVolume(preMuteVolume.current);
      lastTrackedVolume.current = Math.round(preMuteVolume.current * 100) / 100;
      if (player && playerReady && deviceId) {
        player.setVolume(preMuteVolume.current).catch(err => console.warn('[Player] Failed to unmute:', err));
      }
    } else {
      // Mute: save current volume and set to 0
      preMuteVolume.current = volume;
      setIsMuted(true);
      setVolume(0);
      lastTrackedVolume.current = 0;
      if (player && playerReady && deviceId) {
        player.setVolume(0).catch(err => console.warn('[Player] Failed to mute:', err));
      }
    }
  }, [isMuted, volume, player, playerReady, deviceId]);

  const handleVolumeChange = useCallback((e) => {
    const newVolume = parseFloat(e.target.value);

    // Moving the slider while muted auto-unmutes
    if (isMuted) {
      setIsMuted(false);
    }

    // Update the UI state and sync with poll tracker to avoid double-logging
    setVolume(newVolume);
    lastTrackedVolume.current = Math.round(newVolume * 100) / 100;
    
    // Clear any pending timeout
    if (volumeStateRef.current.timeout) {
      clearTimeout(volumeStateRef.current.timeout);
    }
    
    // Store the pending volume
    volumeStateRef.current.pending = newVolume;

    // Debounced API call for volume tracking (longer debounce to avoid flooding).
    // Snapshot the action origin at call time — the deferred POST fires at
    // +2500ms, which is longer than the broadcast listener's 1500ms revert
    // window, so reading actionOriginRef inside the timer would always see
    // 'dj_app' even when a remote action initiated the volume change.
    const originSnapshot = actionOriginRef.current;
    if (volumeApiTimeout.current) {
      clearTimeout(volumeApiTimeout.current);
    }
    volumeApiTimeout.current = setTimeout(() => {
      const userEmail = localStorage.getItem('user_email');
      if (userEmail) {
        axios.post(`${API_BASE_URL}/track-volume`, {
          email: userEmail,
          volume: newVolume,
          sessionId: sessionIdRef.current,
          origin: originSnapshot
        }).catch(err => {
          console.warn('[Volume Tracking] Error sending volume data:', err.message);
        });

        // Detect significant volume change (>10%) and log as intervention
        if (lastSignificantVolume.current !== null && Math.abs(newVolume - lastSignificantVolume.current) > 0.10) {
          const p = playerInstance.current;
          if (p) {
            p.getCurrentState().then(state => {
              if (state && state.track_window && state.track_window.current_track) {
                const track = state.track_window.current_track;
                axios.post(`${API_BASE_URL}/track-playback-event`, {
                  email: userEmail,
                  action: 'volume_adjusted',
                  songName: track.name,
                  artistName: track.artists[0]?.name,
                  spotifyTrackId: track.linked_from?.id || track.id,
                  position: state.position,
                  duration: state.duration,
                  sessionId: sessionIdRef.current,
                  playlistId: currentPlaylistIdRef.current,
                  subVibe: currentSubVibeRef.current ?? queuedSubVibeRef.current ?? null,
                  origin: originSnapshot
                }).catch(err => {
                  console.warn('[Volume Tracking] Error sending volume_adjusted event:', err.message);
                });
              }
            }).catch(() => {});
          }
          lastSignificantVolume.current = newVolume;
        }
      }
      volumeApiTimeout.current = null;
    }, 2500);

    // Debounce the actual volume change
    volumeStateRef.current.timeout = setTimeout(() => {
      if (player && playerReady && deviceId) {
        try {
          // Update the current volume state
          volumeStateRef.current.current = newVolume;
          
          // Call the SDK method
          player.setVolume(newVolume).catch(error => {
            console.warn('[Player] Failed to set volume:', error);
            // Revert UI on failure
            setVolume(volumeStateRef.current.current);
          });
        } catch (error) {
          console.warn('[Player] Error setting volume:', error);
          // Revert UI on error
          setVolume(volumeStateRef.current.current);
        }
      }
      volumeStateRef.current.pending = null;
    }, 300);
  }, [player, playerReady, deviceId, isMuted]);

  // Poll for volume changes from any source (hardware buttons, Spotify Connect, etc.)
  useEffect(() => {
    if (!player || !playerReady || !deviceId) {
      return;
    }

    const checkVolume = async () => {
      try {
        const currentVolume = await player.getVolume();
        const rounded = Math.round(currentVolume * 100) / 100;

        if (lastTrackedVolume.current !== null && rounded !== lastTrackedVolume.current) {
          // Volume changed externally — update UI and track it
          setVolume(rounded);
          volumeStateRef.current.current = rounded;

          const userEmail = localStorage.getItem('user_email');
          if (userEmail) {
            axios.post(`${API_BASE_URL}/track-volume`, {
              email: userEmail,
              volume: rounded,
              sessionId: sessionIdRef.current,
              origin: actionOriginRef.current
            }).catch(err => {
              console.warn('[Volume Tracking] Error sending volume data:', err.message);
            });

            // Detect significant external volume change (>10%) and log as intervention
            if (lastSignificantVolume.current !== null && Math.abs(rounded - lastSignificantVolume.current) > 0.10) {
              const p = playerInstance.current;
              if (p) {
                p.getCurrentState().then(state => {
                  if (state && state.track_window && state.track_window.current_track) {
                    const track = state.track_window.current_track;
                    axios.post(`${API_BASE_URL}/track-playback-event`, {
                      email: userEmail,
                      action: 'volume_adjusted',
                      songName: track.name,
                      artistName: track.artists[0]?.name,
                      spotifyTrackId: track.linked_from?.id || track.id,
                      position: state.position,
                      duration: state.duration,
                      sessionId: sessionIdRef.current,
                      playlistId: currentPlaylistIdRef.current,
                      subVibe: currentSubVibeRef.current ?? queuedSubVibeRef.current ?? null,
                      origin: actionOriginRef.current
                    }).catch(err => {
                      console.warn('[Volume Tracking] Error sending volume_adjusted event:', err.message);
                    });
                  }
                }).catch(() => {});
              }
              lastSignificantVolume.current = rounded;
            }
          }
        }
        lastTrackedVolume.current = rounded;
      } catch (err) {
        // Player might not be ready, ignore
      }
    };

    // Initial read
    checkVolume();

    // Poll every 5 seconds
    volumePollInterval.current = setInterval(checkVolume, 5000);

    return () => {
      if (volumePollInterval.current) {
        clearInterval(volumePollInterval.current);
        volumePollInterval.current = null;
      }
    };
  }, [player, playerReady, deviceId]);

  // Update the handleSeek function
  const handleSeek = useCallback(async (value) => {
    if (!isPlayerReady(player, playerReady, deviceId)) return;
    
    try {
      await player.seek(value);
      setTrackProgress(value);
    } catch (error) {
      console.error('[Player] Error seeking:', error);
    }
  }, [player, playerReady, deviceId, setTrackProgress]);

  // Add cleanup for volume debounce
  useEffect(() => {
    return () => {
      if (volumeDebounceTimeout.current) {
        clearTimeout(volumeDebounceTimeout.current);
      }
    };
  }, []);



  const fetchUserProfile = useCallback(async (token) => {
    try {
      console.log('Starting user profile fetch...');
      const response = await axios.get('https://api.spotify.com/v1/me', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      const userEmail = response.data.email;
      console.log('Fetched user email:', userEmail);
      
      if (!userEmail) {
        console.error('No email found in Spotify profile response');
        return null;
      }
      
      localStorage.setItem('user_email', userEmail);
      return userEmail;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  }, []);

  // Resolve the freshest available access token. The argument is a hint —
  // if it's expired or missing, refresh proactively. tokenRef.current is
  // updated synchronously by refreshAccessToken so it's always current.
  const resolveFreshToken = useCallback(async (hint) => {
    const expiry = parseInt(localStorage.getItem('token_expiry') || '0', 10);
    const isValid = expiry > Date.now() + 60_000; // 60s safety margin
    if (isValid) {
      return tokenRef.current || localStorage.getItem('token') || hint || null;
    }
    return await refreshAccessToken();
  }, [refreshAccessToken]);

  const fetchAllLikedSongs = useCallback(async (initialToken) => {
    // Auto-retry helper: on 401, refresh token and retry the call exactly once.
    // Resolves a fresh token before the first attempt so we never start a
    // request with an expired token.
    const spotifyGet = async (url, params) => {
      let activeToken = await resolveFreshToken(initialToken);
      const send = (t) => axios.get(url, {
        headers: { Authorization: `Bearer ${t}` },
        params,
      });
      const sleep = (ms) => new Promise(r => setTimeout(r, ms));
      const trySend = async (token) => {
        try { return await send(token); }
        catch (err) { return { __err: err }; }
      };
      let attempt = 0;
      // Spotify token-propagation can produce 403s on /me/* for ~1-2 minutes
      // after a fresh OAuth. 8 attempts with a 30s cap covers ~3 min total
      // (1+2+4+8+16+30+30+30) — enough to outlast the worst case we've seen.
      const MAX_RETRIES = 8;
      while (true) {
        const r = await trySend(activeToken);
        if (!r.__err) return r;
        const err = r.__err;
        const status = err.response?.status;
        if (status === 401) {
          console.warn('[LikedSongs] Spotify 401 → refreshing token + retry');
          activeToken = await refreshAccessToken();
          if (!activeToken) throw err;
          continue; // immediate retry with fresh token
        }
        if ((status === 429 || status === 403) && attempt < MAX_RETRIES) {
          // Spotify rate-limits return 429; some scope-related cases return 403
          // with retry-after. Use Retry-After header if present, else exponential
          // backoff from 1s with jitter, capped at 30s so we don't give up
          // before the token-propagation window finishes.
          const ra = parseInt(err.response?.headers?.['retry-after'] || '0', 10);
          const backoff = ra > 0 ? ra * 1000 : Math.min(1000 * (2 ** attempt), 30000);
          const jitter = Math.floor(Math.random() * 400);
          attempt++;
          console.warn(
            `[LikedSongs] Spotify ${status} on ${url} → backing off ${backoff + jitter}ms ` +
            `(attempt ${attempt}/${MAX_RETRIES})`
          );
          await sleep(backoff + jitter);
          continue;
        }
        throw err;
      }
    };

    const scheduleRetry = (reasonLabel) => {
      const attempt = (likedSongsRetryCountRef.current || 0) + 1;
      const MAX_ATTEMPTS = 5;
      if (attempt > MAX_ATTEMPTS) {
        console.error(
          `[LikedSongs] Retry budget exhausted (${MAX_ATTEMPTS}); giving up on ${reasonLabel}.`
        );
        return;
      }
      likedSongsRetryCountRef.current = attempt;
      const delayMs = Math.min(15_000 * attempt, 60_000);
      console.warn(
        `[LikedSongs] ${reasonLabel} → retrying in ${delayMs / 1000}s (attempt ${attempt}/${MAX_ATTEMPTS})`
      );
      setTimeout(() => {
        // Reset the once-per-mount guard so the snapshot useEffect re-fires
        initialDataFetchPerformed.current = false;
        fetchAllLikedSongs(localStorage.getItem('token'));
      }, delayMs);
    };

    try {
      // Show the "Preparing your DJ" loader IMMEDIATELY for first-time
      // users so they see "Loading your Spotify library…" / "Getting to
      // know your music taste…" instead of the generic "Finding the
      // perfect rhythm". RETURNING users (who already finished onboarding
      // at least once on this device) skip straight to the regular flow —
      // the localStorage flag below is set in the snapshot response
      // handler the first time a personalized playlistId arrives, so it
      // sticks across sessions.
      const onboardedBefore = localStorage.getItem('dj_first_onboarding_done') === '1';
      if (!onboardedBefore && !isPreparingDjRef.current) {
        isPreparingDjRef.current = true;
        setIsPreparingDj(true);
        setPreparingStage('fetching_likes');
      }

      // Spotify's /api edge nodes need a brief moment to recognize a token
      // that was just minted at /accounts. If we fire requests inside that
      // window we get a 403 cascade that the retry loop has to chew through.
      // Sleep just long enough to skip the window when the token is fresh.
      const POST_OAUTH_SETTLE_MS = 3000;
      const tokenAge = Date.now() - (tokenMintedAtRef.current || 0);
      if (tokenMintedAtRef.current && tokenAge < POST_OAUTH_SETTLE_MS) {
        const wait = POST_OAUTH_SETTLE_MS - tokenAge;
        console.log(`[LikedSongs] Waiting ${wait}ms for fresh OAuth token to propagate`);
        await new Promise(r => setTimeout(r, wait));
      }

      const userEmail = localStorage.getItem('user_email');
      if (!userEmail) {
        console.log('[LikedSongs] No email cached, fetching profile first');
        await fetchUserProfile(await resolveFreshToken(initialToken));
      }

      const limit = 50;
      const totalDesired = 500;
      const numberOfRequests = Math.ceil(totalDesired / limit);

      // Fan-out fetch with bounded concurrency. Firing all 10 batches in one
      // burst (Promise.all on 10 requests) reliably trips Spotify's per-token
      // rate limiter — every batch comes back 403/429. Limit to 3 concurrent
      // requests per endpoint and Spotify is happy. Wall-clock is still
      // ~5-10s for the full 500+500 vs. ~30s sequential.
      // Distinguish "Spotify call succeeded but returned empty" (legitimate
      // for brand-new users) from "Spotify call failed entirely". We mark
      // sync complete on the former, retry on the latter.
      const CONCURRENCY = 3;
      const fetchAll = async (label, url, extraParams) => {
        const results = new Array(numberOfRequests);
        let next = 0;
        const worker = async () => {
          while (true) {
            const i = next++;
            if (i >= numberOfRequests) return;
            try {
              const res = await spotifyGet(url, { limit, offset: i * limit, ...extraParams });
              results[i] = { ok: true, items: res.data.items || [] };
            } catch (err) {
              console.error(
                `[LikedSongs] ${label} batch ${i + 1} failed:`,
                err?.response?.status, err?.message
              );
              results[i] = { ok: false, items: [] };
            }
          }
        };
        await Promise.all(
          Array.from({ length: Math.min(CONCURRENCY, numberOfRequests) }, worker)
        );
        const items = results.flatMap(r => r.items);
        // Succeeded if at least one batch came back without error. Even a
        // brand-new user with 0 saved tracks gets ok=true with items=[].
        const succeeded = results.some(r => r.ok);
        return { items, succeeded };
      };

      console.log('[LikedSongs] Fetching top + liked tracks in parallel');
      const [topResult, likedResult] = await Promise.all([
        fetchAll('top tracks', 'https://api.spotify.com/v1/me/top/tracks', { time_range: 'medium_term' }),
        fetchAll('liked tracks', 'https://api.spotify.com/v1/me/tracks', {}),
      ]);
      const topTracks = topResult.items;
      const likedTracks = likedResult.items;
      const topTracksFetchSucceeded = topResult.succeeded;
      const likedTracksFetchSucceeded = likedResult.succeeded;

      // If BOTH endpoints failed entirely, the token / network is broken.
      // Don't mark sync complete; schedule a retry so god_mode never fires
      // against a row that nobody ever wrote.
      if (!topTracksFetchSucceeded && !likedTracksFetchSucceeded) {
        scheduleRetry('Spotify auth/network failure (no batches succeeded)');
        return;
      }

      const formattedTopTracks = topTracks.map(song => ({
        title: song.name,
        artist: song.artists.map(a => a.name).join(', '),
        spotifyTrackId: song.id,
      }));
      const formattedLikedTracks = likedTracks.map(song => ({
        title: song.track.name,
        artist: song.track.artists.map(a => a.name).join(', '),
        spotifyTrackId: song.track.id,
      }));

      const currentEmail = localStorage.getItem('user_email');
      if (!currentEmail) {
        console.warn('[LikedSongs] No email available for update-likes-songs');
        scheduleRetry('missing email after sync');
        return;
      }

      try {
        await axios.post(`${API_BASE_URL}/update-likes-songs`, {
          email: currentEmail,
          topTracks: formattedTopTracks,
          likedTracks: formattedLikedTracks,
        }, {
          headers: { 'Content-Type': 'application/json' },
        });
        likedSongsSyncedRef.current = true;
        likedSongsRetryCountRef.current = 0;
        console.log(
          `[LikedSongs] Sync complete — ${formattedTopTracks.length} top + ` +
          `${formattedLikedTracks.length} liked. Snapshots may now proceed.`
        );
      } catch (postError) {
        console.error('[LikedSongs] update-likes-songs POST failed:', postError);
        scheduleRetry('backend POST failed');
      }
    } catch (error) {
      console.error('[LikedSongs] Sync failed unexpectedly:', error);
      scheduleRetry('unexpected error');
    }
  }, [fetchUserProfile, refreshAccessToken, resolveFreshToken]);

  // Move exchangeCodeForToken before the useEffect that uses it
  const exchangeCodeForToken = useCallback(async (code) => {
    codeExchangeInProgress.current = true;
    try {
      const clientId = CLIENT_ID;
      const clientSecret = CLIENT_SECRET;
      const redirectUri = REDIRECT_URI;

      const authHeader = `Basic ${btoa(`${clientId}:${clientSecret}`)}`;

      const data = new URLSearchParams({
        grant_type: 'authorization_code',
        code: code,
        redirect_uri: redirectUri,
      }).toString();

      const response = await axios({
        method: 'post',
        url: TOKEN_ENDPOINT,
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        data: data,
      });

      const { access_token, refresh_token, expires_in } = response.data;

      // Store tokens and expiry as timestamp
      localStorage.setItem('token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      const expiryTime = Date.now() + (expires_in * 1000);
      localStorage.setItem('token_expiry', expiryTime.toString());

      tokenRef.current = access_token;
      tokenMintedAtRef.current = Date.now();
      setToken(access_token);

      // Fetch user profile and liked songs
      await fetchUserProfile(access_token);
      if (!initialDataFetchPerformed.current) {
        await fetchAllLikedSongs(access_token);
        initialDataFetchPerformed.current = true;
      }

    } catch (error) {
      console.error('Error in token exchange:', error);
      codeExchangeInProgress.current = false;
      redirectToSpotifyLogin();
    }
    codeExchangeInProgress.current = false;
  }, [CLIENT_ID, CLIENT_SECRET, REDIRECT_URI, fetchUserProfile, fetchAllLikedSongs, redirectToSpotifyLogin]);

  // Update the initial useEffect dependencies
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const storedToken = localStorage.getItem('token');

    if (code) {
      // Clear the URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
      exchangeCodeForToken(code);
    } else if (storedToken && credentialsResolved) {
      // Already authenticated and credentials resolved — proceed normally
    } else if (!credentialsResolved) {
      // No credentials resolved yet — email gate will show
      // If we have a stored email, auto-resolve
      const storedEmail = localStorage.getItem('user_email');
      if (storedEmail) {
        resolveSpotifyCredentials(storedEmail);
      }
    } else if (!storedToken && credentialsResolved) {
      // Credentials resolved but no token — redirect to Spotify auth
      redirectToSpotifyLogin();
    }
  }, [exchangeCodeForToken, redirectToSpotifyLogin, credentialsResolved, resolveSpotifyCredentials]);

  // Webcam logic
  // Pick the best camera from a list of video inputs.
  // Priority: external USB > front camera. NEVER back camera (the DJ should
  // see the room/user, not the wall behind the phone).
  const isBackLabel = (label) => /facing back|rear|back camera|camera 0(?!\d)/i.test(label || '');
  const isExternalLabel = (label) => /usb|external|webcam|hd usb|logitech|c920|c922|c930/i.test(label || '');
  const isFrontLabel = (label) => /front|selfie|facing front|camera 1(?!\d)|user[\s-]?facing/i.test(label || '');

  const pickPreferredCameraId = (videoInputs) => {
    if (!videoInputs || videoInputs.length === 0) return null;
    // 1. External (USB) wins.
    const external = videoInputs.find(c => isExternalLabel(c.label));
    if (external) return external.deviceId;
    // 2. Then explicit front camera.
    const front = videoInputs.find(c => isFrontLabel(c.label));
    if (front) return front.deviceId;
    // 3. Then any camera that's not a back camera (Mac FaceTime is unlabeled
    //    "front" but is a user-facing cam — falls through here).
    const nonBack = videoInputs.find(c => !isBackLabel(c.label));
    if (nonBack) return nonBack.deviceId;
    // 4. Refuse: only back-facing cameras available.
    return null;
  };

  const startWebcam = async (preferredDeviceIdArg) => {
    const baseVideo = {
      width: { ideal: 640 },
      height: { ideal: 480 },
      frameRate: { ideal: 24 },
      // Hint the OS to prefer the front (user-facing) camera. On Android this
      // is the canonical way to avoid the back camera; on Mac it's a no-op
      // because FaceTime is already user-facing.
      facingMode: { ideal: 'user' }
    };
    try {
      console.log('[Webcam] Requesting camera access');
      let stream = null;
      const savedId = preferredDeviceIdArg ?? localStorage.getItem('preferred_camera_id');

      // 1. Try saved/preferred deviceId first — but reject it if the active
      //    track turns out to be a back camera (e.g. stale id from earlier
      //    session before the never-back-camera rule).
      if (savedId) {
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { ...baseVideo, deviceId: { exact: savedId } }
          });
          const savedLabel = stream.getVideoTracks?.()[0]?.label || '';
          if (isBackLabel(savedLabel)) {
            console.warn('[Webcam] Saved camera is a back camera, rejecting:', savedLabel);
            stream.getTracks().forEach(t => t.stop());
            stream = null;
            localStorage.removeItem('preferred_camera_id');
          } else {
            console.log('[Webcam] Using saved camera:', savedId, savedLabel);
          }
        } catch (err) {
          console.warn('[Webcam] Saved camera unavailable, falling back to auto-pick:', err.name);
        }
      }

      // 2. No saved id, or saved id failed: bootstrap permission, enumerate, auto-pick
      if (!stream) {
        const bootstrap = await navigator.mediaDevices.getUserMedia({ video: baseVideo });
        const devices = await navigator.mediaDevices.enumerateDevices();
        const cameras = devices.filter(d => d.kind === 'videoinput');
        const chosenId = pickPreferredCameraId(cameras);
        const chosenLabel = cameras.find(c => c.deviceId === chosenId)?.label;
        console.log('[Webcam] Auto-picked camera:', chosenId, chosenLabel);

        if (!chosenId) {
          // pickPreferredCameraId refused: only back cameras available.
          console.error('[Webcam] No acceptable camera (only back cameras present). Refusing to open back camera.');
          bootstrap.getTracks().forEach(t => t.stop());
          setIsLoading(false);
          toast.warn('No usable camera found. Please connect a USB camera or use a device with a front camera.', { theme: 'colored', position: 'top-center' });
          recordSessionError({
            userEmail: localStorage.getItem('user_email'),
            sessionId: sessionIdRef.current,
            errorType: 'camera_unavailable',
            errorMessage: 'pickPreferredCameraId returned null (only back cameras present)',
          });
          return;
        }

        const bootstrapId = bootstrap.getVideoTracks()[0]?.getSettings?.().deviceId;
        if (chosenId !== bootstrapId) {
          bootstrap.getTracks().forEach(t => t.stop());
          stream = await navigator.mediaDevices.getUserMedia({
            video: { ...baseVideo, deviceId: { exact: chosenId } }
          });
        } else {
          stream = bootstrap;
        }
        // Final guard: even if bootstrap or the reopened stream somehow gave
        // us a back camera (Android `facingMode: 'user'` ignored), reject it.
        const activeLabel = stream.getVideoTracks?.()[0]?.label || '';
        if (isBackLabel(activeLabel)) {
          console.error('[Webcam] Active stream is a back camera, refusing:', activeLabel);
          stream.getTracks().forEach(t => t.stop());
          setIsLoading(false);
          toast.warn('Phone returned the back camera. Please connect a USB camera or use the front camera.', { theme: 'colored', position: 'top-center' });
          recordSessionError({
            userEmail: localStorage.getItem('user_email'),
            sessionId: sessionIdRef.current,
            errorType: 'camera_back_only',
            errorMessage: `active stream label: ${activeLabel}`,
            cameraLabel: activeLabel,
          });
          return;
        }
        localStorage.setItem('preferred_camera_id', chosenId);
      }

      console.log('[Webcam] Camera access granted');
      setVideoStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          console.log('[Webcam] Video metadata loaded');
          videoRef.current.play();
          // Add a longer delay after the camera starts to allow it to adjust
          setTimeout(() => {
            console.log('[Webcam] Camera warm-up complete');
            setIsLoading(false);
          }, 3000); // 3 second delay for better camera adjustment
        };
        // Set loading state while camera warms up
        setIsLoading(true);
      } else {
        console.log('[Webcam] No video ref available');
      }
    } catch (error) {
      console.error('[Webcam] Error in startWebcam:', error);
      const isPermissionDenied = error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError' || (error.name === 'DOMException' && error.message.includes('The request is not allowed'));
      if (isPermissionDenied) {
        toast.warn('Camera permission is disabled. Please enable it in your browser settings to use the video features.', { theme: 'colored', position: "top-center" });
      }
      recordSessionError({
        userEmail: localStorage.getItem('user_email'),
        sessionId: sessionIdRef.current,
        errorType: isPermissionDenied ? 'camera_permission_denied' : 'camera_unavailable',
        errorMessage: `${error.name || 'Error'}: ${error.message || ''}`,
      });
      setIsLoading(false);
    }
  };

  const stopWebcam = useCallback(() => {
    if (videoStream) {
      videoStream.getTracks().forEach((track) => track.stop());
      setVideoStream(null);
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    }
  }, [videoStream]);

  // Mirror videoStream into a ref so the master-toggle effect can tear down
  // the webcam without taking videoStream as a React dep — see DJ-(this fix):
  // including videoStream/stopWebcam in the deps caused the effect to re-fire
  // every time the stream attached/detached, and the enable branch
  // unconditionally calls rotateSessionId(), thrashing the session_id.
  const videoStreamRef = useRef(videoStream);
  useEffect(() => { videoStreamRef.current = videoStream; }, [videoStream]);

  // Track whether we've already sent direct_shutdown for this toggle-off
  const directShutdownSentRef = useRef(false);
  // Track initial mount to avoid showing feedback on page load
  const masterToggleInitialMount = useRef(true);

  // Handle master toggle state changes
  useEffect(() => {
    isMasterEnabledRef.current = isMasterEnabled;

    const handleMasterToggle = async () => {
      console.log('[Master Toggle] State changed to:', isMasterEnabled);

      if (!isMasterEnabled) {
        // Stop everything when disabled
        console.log('[Master Toggle] Disabling all features...');

        // Session feedback is triggered from the Stop DJ click handler (same render batch).
        // This guard handles edge cases where isMasterEnabled is set to false programmatically.
        // DJ-139: when the shutdown originated on dj-remote (`remote_button` /
        // `remote_voice`), the remote owns the popup — suppress the webapp's
        // modal so the user isn't asked twice on two surfaces.
        if (!masterToggleInitialMount.current && !showSessionFeedback) {
          feedbackSessionIdRef.current = sessionIdRef.current;
          feedbackListeningMinutesRef.current = Math.round((Date.now() - sessionStartTimeRef.current) / 60000);
          if (actionOriginRef.current === 'dj_app') {
            setShowSessionFeedback(true);
          }
        }

        // Track direct shutdown event (before disconnecting player) — only once per toggle-off
        if (!directShutdownSentRef.current) {
          directShutdownSentRef.current = true;
          try {
            let track = null;
            let position = null;
            let duration = null;
            if (playerInstance.current) {
              const state = await playerInstance.current.getCurrentState();
              track = state?.track_window?.current_track;
              position = state?.position || null;
              duration = state?.duration || null;
            }
            const userEmail = localStorage.getItem('user_email');
            if (userEmail) {
              console.log('[Playback Event] direct_shutdown:', {
                track: track ? `${track.name} - ${track.artists[0]?.name}` : 'none',
                position,
                duration
              });
              axios.post(`${API_BASE_URL}/track-playback-event`, {
                email: userEmail,
                songName: track?.name || null,
                artistName: track?.artists[0]?.name || null,
                spotifyTrackId: track ? (track.linked_from?.id || track.id) : null,
                action: 'direct_shutdown',
                position,
                duration,
                sessionId: sessionIdRef.current,
                playlistId: currentPlaylistIdRef.current,
                subVibe: currentSubVibeRef.current ?? queuedSubVibeRef.current ?? null,
                origin: actionOriginRef.current
              }).then(res => {
                console.log('[Playback Event] direct_shutdown response:', res.status, res.data);
              }).catch(err => {
                console.error('[Playback Event] Error sending direct_shutdown:', err.response?.status, err.response?.data || err.message);
              });
            } else {
              console.warn('[Playback Event] No user_email in localStorage, skipping direct_shutdown');
            }
          } catch (err) {
            console.warn('[Playback Event] Could not send direct_shutdown:', err.message);
          }
        }

        // Rotate session ID after direct_shutdown
        rotateSessionId();
        console.log('[Session] Rotated session ID after direct_shutdown:', sessionIdRef.current);

        // Clear No Music timer
        if (noneStateTimerRef.current) {
          clearTimeout(noneStateTimerRef.current);
          noneStateTimerRef.current = null;
        }

        // Terminate player instance
        if (playerInstance.current) {
          try {
            console.log('[Master Toggle] Disconnecting player instance.');
            const playerToDisconnect = playerInstance.current;
            playerInstance.current = null;
            setPlayer(null);
            setPlayerReady(false);
            setDeviceId(null);

            // Reset initialization lock
            initializationLock.current = false;

            // Mark that we need a fresh SDK on next toggle
            needsFreshSDK.current = true;

            // Disconnect after clearing references to prevent race conditions
            await playerToDisconnect.disconnect();
          } catch (error) {
            console.error('[Master Toggle] Error disconnecting player:', error);
          }
        }

        // Stop webcam — read from ref so we don't need videoStream/stopWebcam
        // in this effect's dep array (see videoStreamRef definition above).
        const currentStream = videoStreamRef.current;
        if (currentStream) {
          console.log('[Master Toggle] Stopping webcam');
          currentStream.getTracks().forEach((track) => track.stop());
          setVideoStream(null);
          if (videoRef.current) {
            videoRef.current.srcObject = null;
          }
        }

        // Clear progress interval
        if (progressInterval.current) {
          clearInterval(progressInterval.current);
          progressInterval.current = null;
        }

        // Clear any pending updates and current state
        console.log('[Master Toggle] Clearing song and scene state.');
        setPendingScene(null);
        setPendingSongs(null);
        queuedPlaylistIdRef.current = null;
        hasPendingUpdate.current = false;
        pendingUpdateApplied.current = false;
        setRecommendedSongs([]);
        setScene({
          scene: '',
          sceneMatches: false,
          action: '',
          confidence: 0,
          explanation: ''
        });
        setCurrentlyPlaying(null);
        setIsPlaying(false);
        setTrackProgress(0);
        setTrackDuration(0);
        lastValidState.current = null;

        // Reset device transfer / yielded state
        djYieldedRef.current = false;
        setDjYielded(false);
        yieldedPlaylistContext.current = null;

        // Clean up any pending playlist subscriptions
        if (playlistSubscriptionRef.current) {
          console.log('[Master Toggle] Unsubscribing from playlist updates');
          playlistSubscriptionRef.current.unsubscribe();
          setPlaylistSubscription(null);
          playlistSubscriptionRef.current = null;
        }

        // Clean up polling interval
        if (playlistPollingIntervalRef.current) {
          console.log('[Master Toggle] Stopping playlist polling');
          clearInterval(playlistPollingIntervalRef.current);
          playlistPollingIntervalRef.current = null;
          setPlaylistPollingInterval(null);
        }

        setPendingPlaylistId(null);
        setIsGeneratingPlaylist(false);
        // Also reset the refs
        isGeneratingPlaylistRef.current = false;
        pendingPlaylistIdRef.current = null;

      } else {
        // Resume when enabled — reset shutdown guard for next toggle-off
        directShutdownSentRef.current = false;
        console.log('[Master Toggle] Enabling all features...');
        console.log('[Master Toggle] Generating new session ID.');
        rotateSessionId();
        console.log('[Session] New session ID generated for master toggle:', sessionIdRef.current);

        // Reset session start time so feedback shows accurate listening duration
        sessionStartTimeRef.current = Date.now();

        // Small delay to ensure cleanup from toggle off is complete
        setTimeout(() => {
          console.log('[Master Toggle] Cleanup delay complete, player will initialize');
        }, 100);

        // The player initialization useEffect will handle re-initializing the player.
        // The auto-start webcam useEffect will handle restarting the webcam.
      }
    };

    handleMasterToggle();
    masterToggleInitialMount.current = false;
    // Only depend on isMasterEnabled — videoStream/stopWebcam are read via
    // videoStreamRef.current to avoid spurious re-fires that thrash session_id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMasterEnabled]);

  // Rotate session ID after 3 minutes of continuous "No Music" state
  useEffect(() => {
    if (scene.action === 'No Music') {
      noneStateTimerRef.current = setTimeout(() => {
        rotateSessionId();
        console.log('[Session] Rotated session ID after 3 min of No Music:', sessionIdRef.current);
      }, 3 * 60 * 1000);
    } else {
      if (noneStateTimerRef.current) {
        clearTimeout(noneStateTimerRef.current);
        noneStateTimerRef.current = null;
      }
    }
    return () => {
      if (noneStateTimerRef.current) {
        clearTimeout(noneStateTimerRef.current);
        noneStateTimerRef.current = null;
      }
    };
  }, [scene.action]);

  const takeSnapshot = async (darkRetryCount = 0) => {
    // Check if master toggle is disabled
    if (!isMasterEnabledRef.current) {
      console.log('[Snapshot] Master toggle is disabled, skipping snapshot');
      return;
    }

    // Gate: wait for liked-songs sync so god_mode always has the user's library
    if (!likedSongsSyncedRef.current) {
      console.log('[Snapshot] Waiting for liked songs sync before first snapshot');
      return;
    }

    // Don't take snapshot if camera is still warming up
    if (isLoading) {
      console.log('[Snapshot] Camera is still warming up, please wait');
      return;
    }

    // Don't take snapshot if playlist generation is already in progress
    if (isGeneratingPlaylist || pendingPlaylistId) {
      console.log('[Snapshot] Playlist generation already in progress, skipping snapshot');
      return;
    }

    console.log('[Snapshot] Starting snapshot process');
    const canvas = canvasRef.current;
    const video = videoRef.current;

    if (!canvas) {
      console.log('[Snapshot] Missing canvas ref');
      return;
    }

    // UVC fast-path: if a USB camera is attached + ready via the native bridge,
    // grab a JPEG frame directly from it and skip the phone-video readiness
    // checks. The bridge is only present inside the Android APK.
    const uvc = (typeof window !== 'undefined') ? window.AndroidUvcCamera : null;
    // Diagnostic: surface UVC bridge state on every snapshot so we can see
    // why the USB camera isn't engaging (logcat is device-local, not BetterStack).
    try {
      console.log('[Snapshot] UVC bridge state:', JSON.stringify({
        bridgeExists: !!uvc,
        isAttached: uvc?.isAttached?.() ?? 'n/a',
        isReady: uvc?.isReady?.() ?? 'n/a',
        deviceLabel: uvc?.getDeviceLabel?.() || null
      }));
    } catch (e) {
      console.warn('[Snapshot] UVC bridge state probe threw:', e?.message);
    }
    const useUvc = !!(uvc && uvc.isAttached && uvc.isAttached() && uvc.isReady && uvc.isReady());
    let uvcCameraLabel = null;
    let uvcDrawn = false;

    const ctx = canvas.getContext('2d', { alpha: false });

    if (useUvc) {
      try {
        const base64 = uvc.captureFrameBase64();
        if (base64) {
          const img = new Image();
          img.src = 'data:image/jpeg;base64,' + base64;
          await new Promise((resolve) => { img.onload = resolve; img.onerror = resolve; });
          if (img.complete && img.naturalWidth > 0) {
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            ctx.fillStyle = '#000';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0);
            uvcCameraLabel = (uvc.getDeviceLabel && uvc.getDeviceLabel()) || 'USB Camera';
            uvcDrawn = true;
            console.log('[Snapshot] Using USB camera frame:', uvcCameraLabel,
                        canvas.width + 'x' + canvas.height);
          } else {
            console.warn('[Snapshot] UVC frame failed to decode, falling back to phone video');
          }
        } else {
          console.warn('[Snapshot] UVC bridge returned empty frame, falling back');
        }
      } catch (e) {
        console.warn('[Snapshot] UVC capture threw, falling back to phone video:', e);
      }
    }

    if (!uvcDrawn) {
      if (!video) {
        console.log('[Snapshot] Missing video ref');
        return;
      }
      // Enhanced video readiness check
      if (
        video.readyState < 2 ||
        video.videoWidth === 0 ||
        video.videoHeight === 0 ||
        !video.srcObject ||
        !video.srcObject.active
      ) {
        console.log('[Snapshot] Video not ready:', {
          readyState: video.readyState,
          width: video.videoWidth,
          height: video.videoHeight,
          srcObject: !!video.srcObject,
          streamActive: video.srcObject?.active
        });
        return;
      }

      console.log('[Snapshot] Video dimensions:', {
        width: video.videoWidth,
        height: video.videoHeight
      });

      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Clear the canvas before drawing
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw the video frame
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    }

    // Reject visually-dark frames before they're shipped to GPT vision.
    // The previous "any non-zero pixel" check let JPEG-near-black frames slip
    // through and produced "image completely dark" -> consecutive None ->
    // "keep acting normal" UX. Bounded retry: 3 attempts then drop the
    // snapshot; the next 60s tick will try again with a fresh frame.
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    if (isFrameTooDark(imageData)) {
      const MAX_DARK_RETRIES = 3;
      if (darkRetryCount + 1 >= MAX_DARK_RETRIES) {
        console.log(`[Snapshot] Frame too dark after ${MAX_DARK_RETRIES} retries — skipping this snapshot`);
        return;
      }
      console.log(`[Snapshot] Frame too dark (retry ${darkRetryCount + 1}/${MAX_DARK_RETRIES}) — retrying in 500ms`);
      setTimeout(() => takeSnapshot(darkRetryCount + 1), 500);
      return;
    }

    canvas.toBlob(async (blob) => {
      // (P1 #5) — drop response if master-toggle is off; the cleanup path
      // already reset isGeneratingPlaylist/pendingPlaylistId. Without this
      // guard the response handler re-sets them, sticking the spinner forever.
      if (!isMasterEnabledRef.current) {
        console.log('[Snapshot] Master toggle off — dropping in-flight snapshot blob');
        return;
      }
      if (!blob) {
        console.log('[Snapshot] Failed to create blob');
        return;
      }
      console.log('[Snapshot] Created blob:', blob.size, 'bytes');

      const file = new File([blob], 'snapshot.jpg', { type: 'image/jpeg' });

      try {
        setIsLoading(true);
        console.log('[Snapshot] Getting upload URL');
        const presignedUrlResponse = await axios.post(
          `${API_BASE_URL}/get-upload-url`,
          {
            fileName: file.name,
            fileType: file.type,
          },
          {
            timeout: 30000,
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        const { uploadUrl, fileKey } = presignedUrlResponse.data;
        console.log('[Snapshot] Got upload URL for key:', fileKey);

        await axios.put(uploadUrl, file, {
          headers: {
            'Content-Type': file.type,
          },
          timeout: 30000
        });
        console.log('[Snapshot] Uploaded file to S3');

        const fileUrl = `https://dj-img-app.s3.amazonaws.com/${fileKey}`;
        const userEmail = localStorage.getItem('user_email');

        // Store for playlist skip feature
        setLastFileUrl(fileUrl);

        console.log('[Snapshot] Processing image, email:', userEmail);
        console.log('[Snapshot] Session ID:', sessionIdRef.current);

        const currentAction = scene.action === undefined ? 'None' : scene.action;
        console.log('[Snapshot] Sending action:', currentAction);
        
        // Get current playing state directly from player
        let currentIsPlaying = isPlaying; // fallback to React state
        if (player) {
          try {
            const playerState = await player.getCurrentState();
            if (playerState) {
              currentIsPlaying = !playerState.paused;
              console.log('[Snapshot] Got player state from SDK - paused:', playerState.paused, 'isPlaying:', currentIsPlaying);
            }
          } catch (error) {
            console.warn('[Snapshot] Could not get player state:', error);
          }
        }
        console.log('[Snapshot] Player status - isPlaying:', currentIsPlaying);

        try {
          let activeCameraLabel = uvcCameraLabel || null;
          // Read the LIVE stream off the video element, not React state —
          // setVideoStream is async and can be stale in this closure.
          // Without this, the entire fallback chain below was being skipped
          // because videoStream === null, and we'd send cameraLabel: null
          // even though video.srcObject was clearly active.
          const liveStream = videoRef.current?.srcObject || videoStream || null;
          if (!activeCameraLabel && liveStream && liveStream.getVideoTracks) {
            const track = liveStream.getVideoTracks()[0];
            const settings = track?.getSettings?.() || {};
            const constraints = track?.getConstraints?.() || {};
            const trackLabel = track?.label || '';
            console.log('[Snapshot] camera label diagnostics:', JSON.stringify({
              trackLabel,
              settingsDeviceId: settings.deviceId ? String(settings.deviceId).slice(0, 16) : null,
              settingsFacingMode: settings.facingMode || null,
              constraintsFacingMode: constraints.video?.facingMode ?? constraints.facingMode ?? null,
              hasVideoStreamState: !!videoStream,
              hasLiveStream: !!liveStream,
              ua: (navigator.userAgent || '').slice(0, 80)
            }));
            if (trackLabel) {
              activeCameraLabel = trackLabel;
            } else if (settings.deviceId) {
              try {
                const devs = await navigator.mediaDevices.enumerateDevices();
                const match = devs.find(d => d.kind === 'videoinput' && d.deviceId === settings.deviceId);
                if (match?.label) activeCameraLabel = match.label;
              } catch (e) {
                console.warn('[Snapshot] enumerateDevices failed during label lookup:', e);
              }
            }
            if (!activeCameraLabel) {
              if (settings.facingMode === 'user') activeCameraLabel = 'Phone camera (facing user)';
              else if (settings.facingMode === 'environment') activeCameraLabel = 'Phone camera (facing back)';
              else if (settings.deviceId) activeCameraLabel = `videoinput:${String(settings.deviceId).slice(0, 12)}`;
            }
          }
          // ULTIMATE fallback — outside the conditional so it always runs.
          // Guarantees cameraLabel is never null on a successful snapshot.
          if (!activeCameraLabel) {
            const isAndroidApk = /DJApp\//i.test(navigator.userAgent || '');
            activeCameraLabel = isAndroidApk
              ? 'Phone camera (front, label unavailable)'
              : 'Camera (label unavailable)';
          }
          console.log('[Snapshot] resolved cameraLabel:', activeCameraLabel);
          const notifyResponse = await axios.post(
            `${API_BASE_URL}/process-image-to-songs`,
            {
              fileUrl,
              email: userEmail,
              currentAction: currentAction,
              sessionId: sessionIdRef.current,
              isPlaying: currentIsPlaying,
              cameraLabel: activeCameraLabel,
            },
            {
              timeout: 90000, // Increased to 90 seconds
              headers: {
                'Content-Type': 'application/json'
              }
            }
          );

          console.log('[Snapshot] Got scene response:', notifyResponse.data);

          // (P1 #5) — second guard: master toggle may have been turned off
          // while the /process-image-to-songs request was in flight. The
          // cleanup path already cleared state; re-applying it here would
          // re-arm isGeneratingPlaylist and block future snapshots.
          if (!isMasterEnabledRef.current) {
            console.log('[Snapshot] Master toggle off after POST — dropping response');
            return;
          }

          // Check if we're waiting for consecutive threshold
          if (notifyResponse.data.waitingForConsecutive) {
            console.log(`[Snapshot] Waiting for consecutive threshold: ${notifyResponse.data.consecutiveCount}/${notifyResponse.data.threshold}`);
            console.log(`[Snapshot] Keeping previous action: ${scene.action}, detected action: ${notifyResponse.data.action}`);
          }
          
          // Check if the DJ is still building the user's profile
          if (notifyResponse.data.status === 'preparing') {
            console.log('[Preparing] DJ profile is being built, stage:', notifyResponse.data.stage);
            setIsPreparingDj(true);
            isPreparingDjRef.current = true;
            setPreparingStage(notifyResponse.data.stage || 'analyzing_taste');
            return;
          }

          // Profile is now ready — clear the preparing state if it was set
          // and persist the "onboarded" flag so we skip the first-time
          // preparing UX on subsequent sessions.
          if (isPreparingDjRef.current && notifyResponse.data.playlistId) {
            setIsPreparingDj(false);
            isPreparingDjRef.current = false;
            setPreparingStage(null);
            localStorage.setItem('dj_first_onboarding_done', '1');
          } else if (notifyResponse.data.playlistId) {
            // Backstop: even if preparing was never visible (e.g. fast
            // returning user), mark them as onboarded the first time a
            // playlist is returned so the localStorage flag is reliably set.
            localStorage.setItem('dj_first_onboarding_done', '1');
          }

          // Check if this is an async playlist response
          if (notifyResponse.data.playlistId) {
            // Async playlist generation
            console.log('[Snapshot] Received playlist ID:', notifyResponse.data.playlistId);
            setPendingPlaylistId(notifyResponse.data.playlistId);
            // currentPlaylistId is intentionally NOT updated here — it must
            // continue to reflect the playlist whose tracks are *actually
            // playing* until the new tracks begin (handled in
            // applyPendingUpdates / handlePlaylistReady IMMEDIATE branch).
            if (notifyResponse.data.playlistMode) {
              setCurrentPlaylistMode(notifyResponse.data.playlistMode);
              modesUsedInSessionRef.current.add(notifyResponse.data.playlistMode);
            }
            queuedSubVibeRef.current = notifyResponse.data.subVibe ?? null;
            setIsGeneratingPlaylist(true);
            subscribeToPlaylist(notifyResponse.data.playlistId);
            
            // Update scene immediately
            setScene(prevScene => ({
              scene: notifyResponse.data.scene || prevScene.scene,
              sceneMatches: false,
              action: notifyResponse.data.action || prevScene.action,
              confidence: prevScene.confidence,
              explanation: prevScene.explanation
            }));
            
                      // Playlist generation started
          } else if (notifyResponse.data.error) {
            // Error response
            console.error('[Snapshot] Error from backend:', notifyResponse.data.error);
            toast.error(`Error: ${notifyResponse.data.error}`, {
              theme: 'colored',
              position: "top-center"
            });
          } else if (notifyResponse.data.action === 'No Music') {
            // No Music: pause playback immediately and update scene
            console.log('[Snapshot] No Music response - pausing playback immediately');
            if (player) {
              try {
                await player.pause();
                console.log('[Snapshot] Playback paused');
              } catch (e) {
                console.warn('[Snapshot] Error pausing player:', e);
              }
            }
            setIsPlaying(false);
            setScene({
              scene: notifyResponse.data.scene,
              sceneMatches: false,
              action: 'No Music',
              confidence: 0,
              explanation: notifyResponse.data.explanation
            });
          } else if (notifyResponse.data.songs && notifyResponse.data.songs.length > 0) {
            // Synchronous response (backward compatibility - this path should not be reached anymore)
            const enrichedSongs = await searchSpotifyTracks(notifyResponse.data.songs);
            console.log('[Snapshot] Enriched songs with Spotify IDs:', enrichedSongs);

            // Check if there's a currently playing song
            const currentState = player ? await player.getCurrentState() : null;
            const sdkSaysPlaying = currentState && !currentState.paused && currentState.track_window?.current_track;
            const hasCurrentlyPlayingSong = sdkSaysPlaying
              || (currentState === null && (isPlaying || (lastValidState.current && !lastValidState.current.paused
                  && lastValidState.current.track_window?.current_track)));

            if (hasCurrentlyPlayingSong) {
              // Set pending updates to be applied 3 seconds before current song ends
              console.log('[Snapshot] Setting pending updates for when current song ends');
              setPendingSongs(enrichedSongs);
              setPendingScene(prevScene => ({
                scene: notifyResponse.data.waitingForConsecutive ? prevScene.scene : notifyResponse.data.scene,
                sceneMatches: notifyResponse.data.sceneMatches,
                action: (notifyResponse.data.sceneMatches && notifyResponse.data.action === 'None') || notifyResponse.data.waitingForConsecutive ? prevScene.action : notifyResponse.data.action,
                confidence: notifyResponse.data.confidence,
                explanation: notifyResponse.data.waitingForConsecutive ? prevScene.explanation : (!notifyResponse.data.sceneMatches ? notifyResponse.data.explanation : prevScene.explanation)
              }));
              hasPendingUpdate.current = true;
            } else {
              // No song playing, apply immediately
              console.log('[Snapshot] No song currently playing, applying updates immediately');
              

              
              setRecommendedSongs(enrichedSongs);
              setScene(prevScene => ({
                scene: notifyResponse.data.waitingForConsecutive ? prevScene.scene : notifyResponse.data.scene,
                sceneMatches: notifyResponse.data.sceneMatches,
                action: (notifyResponse.data.sceneMatches && notifyResponse.data.action === 'None') || notifyResponse.data.waitingForConsecutive ? prevScene.action : notifyResponse.data.action,
                confidence: notifyResponse.data.confidence,
                explanation: notifyResponse.data.waitingForConsecutive ? prevScene.explanation : (!notifyResponse.data.sceneMatches ? notifyResponse.data.explanation : prevScene.explanation)
              }));
              
              // Directly play the songs if player is ready
              if (playerReady && deviceId) {
                console.log('[Snapshot] Player ready, starting playback immediately');
                const uris = enrichedSongs
                  .map(song => song.spotifyTrackId)
                  .filter(Boolean)
                  .map(id => id.startsWith('spotify:track:') ? id : `spotify:track:${id}`);
                
                const currentToken = localStorage.getItem('token');
                
                axios.put(
                  `https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`,
                  { uris },
                  {
                    headers: {
                      Authorization: `Bearer ${currentToken}`,
                    },
                  }
                ).then(() => {
                  console.log('[Snapshot] Successfully started playing new songs');
                  setIsPlaying(true);
                }).catch(async (error) => {
                  console.error('[Snapshot] Error playing songs:', error?.response?.data || error.message || error);
                  
                  // Handle 401 Unauthorized errors
                  if (error?.response?.status === 401) {
                    console.log('[Snapshot] Got 401 error, attempting to refresh token and retry');
                    const newToken = await refreshAccessToken();
                    if (newToken) {
                      // Retry with fresh token
                      try {
                        await axios.put(
                          `https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`,
                          { uris },
                          {
                            headers: {
                              Authorization: `Bearer ${newToken}`,
                            },
                          }
                        );
                        console.log('[Snapshot] Successfully started playing new songs after token refresh');
                        setIsPlaying(true);
                      } catch (retryError) {
                        console.error('[Snapshot] Error playing songs after token refresh:', retryError?.response?.data || retryError.message || retryError);
                      }
                    }
                  }
                });
              }
            }
          } else {
            // No songs in response, just update scene
            console.log('[Snapshot] No songs in response. waitingForConsecutive:', notifyResponse.data.waitingForConsecutive);
            console.log('[Snapshot] Will update action?', !(notifyResponse.data.sceneMatches && notifyResponse.data.action === 'None') && !notifyResponse.data.waitingForConsecutive);
            console.log('[Snapshot] Will keep previous scene?', notifyResponse.data.waitingForConsecutive);
            console.log('[Snapshot] Current scene:', scene.scene, 'New scene:', notifyResponse.data.scene);
            setScene(prevScene => ({
              scene: notifyResponse.data.waitingForConsecutive ? prevScene.scene : notifyResponse.data.scene,
              sceneMatches: notifyResponse.data.sceneMatches,
              action: (notifyResponse.data.sceneMatches && notifyResponse.data.action === 'None') || notifyResponse.data.waitingForConsecutive ? prevScene.action : notifyResponse.data.action,
              confidence: notifyResponse.data.confidence,
              explanation: notifyResponse.data.waitingForConsecutive ? prevScene.explanation : (!notifyResponse.data.sceneMatches ? notifyResponse.data.explanation : prevScene.explanation)
            }));
          }
        } catch (error) {
          console.error('[Snapshot] Error processing image:', error);
          if (error.code === 'ECONNABORTED') {
            console.error('[Snapshot] Request timed out');
          } else if (error.response) {
            console.error('[Snapshot] Error response:', error.response.data);
          } else if (error.request) {
            console.error('[Snapshot] No response received:', error.request);
          } else {
            console.error('[Snapshot] Error:', error.message);
          }
          const { errorType, errorMessage } = classifyClientError(error);
          recordSessionError({
            userEmail: localStorage.getItem('user_email'),
            sessionId: sessionIdRef.current,
            errorType,
            errorMessage,
          });
        } finally {
          setIsLoading(false);
        }
      } catch (error) {
        console.error('[Snapshot] Error in snapshot process:', error);
        if (error.code === 'ECONNABORTED') {
          console.error('[Snapshot] Request timed out');
        } else if (error.response) {
          console.error('[Snapshot] Error response:', error.response.data);
        } else if (error.request) {
          console.error('[Snapshot] No response received:', error.request);
        } else {
          console.error('[Snapshot] Error:', error.message);
        }
      }
          }, 'image/jpeg');
    };

  useEffect(() => {
    // Check if recommendedSongs actually changed (not just re-rendered with same content)
    const songsChanged = JSON.stringify(recommendedSongs.map(s => s.spotifyTrackId)) !== 
                        JSON.stringify(lastPlayedSongsRef.current);
    
    if (songsChanged && recommendedSongs.length > 0) {
      console.log('[AutoPlay] Detected new songs in recommendedSongs');
      lastPlayedSongsRef.current = recommendedSongs.map(s => s.spotifyTrackId);
    }
    
    // Skip autoplay if we're handling playlist playback
    if (isHandlingPlaylistPlayback.current) {
      console.log('[AutoPlay] Skipping autoplay - playlist handler is managing playback');
      return;
    }
    
    // Only autoplay if songs actually changed and player has no current track
    if (recommendedSongs.length > 0 && player && playerReady && deviceId && songsChanged && isMasterEnabled) {
      player.getCurrentState().then(state => {
        // Only start playing if there's no current track at all (not just paused)
        if (!state || !state.track_window?.current_track) {
          console.log('[AutoPlay] No track loaded, starting playback of new songs');
          playAllSongs();
        } else {
          console.log('[AutoPlay] Track exists in player, not auto-playing', {
            trackName: state.track_window.current_track.name,
            paused: state.paused,
            songsChanged
          });
        }
      }).catch(error => {
        console.error('[AutoPlay] Error checking player state:', error);
      });
    }
  }, [recommendedSongs, playAllSongs, player, playerReady, deviceId, isMasterEnabled]);

  useEffect(() => {
    const errorParam = new URLSearchParams(window.location.search).get('error');
    if (errorParam) {
      console.error('[Auth] Authentication Error:', errorParam);
    }
  }, []);

  useEffect(() => {
    if (token && !initialDataFetchPerformed.current) {
      fetchAllLikedSongs(token);
      initialDataFetchPerformed.current = true;
    }
  }, [token, fetchAllLikedSongs]);

  useEffect(() => {
    return () => stopWebcam();
  }, [stopWebcam]);

  // Clean up Supabase subscription and polling on unmount
  useEffect(() => {
    return () => {
      if (playlistSubscriptionRef.current) {
        console.log('[Cleanup] Unsubscribing from playlist updates');
        playlistSubscriptionRef.current.unsubscribe();
        playlistSubscriptionRef.current = null;
      }
      if (playlistPollingIntervalRef.current) {
        console.log('[Cleanup] Stopping playlist polling');
        clearInterval(playlistPollingIntervalRef.current);
        playlistPollingIntervalRef.current = null;
      }
    };
  }, []); // Empty dependency array - only run cleanup on unmount

  const logout = () => {
    // Stop the player if it exists
    if (player) {
      player.disconnect();
    }
    
    // Clear all tokens and state
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expiry');
    localStorage.removeItem('user_email');
    localStorage.removeItem('spotify_auth_state');

    tokenRef.current = null;
    setToken(null);
    setDeviceId(null);
    setPlayer(null);
    setPlayerReady(false);
    clearSpotifyCredentials();

    // Log out of Spotify via popup window (clears their session cookies).
    // A popup is first-party context for Spotify's domain, so SameSite=Lax
    // cookies get properly cleared (unlike iframes which are third-party).
    const spotifyLogoutWindow = window.open(
      'https://accounts.spotify.com/logout',
      'SpotifyLogout',
      'width=700,height=500,top=100,left=100'
    );

    // Close popup and redirect to home after Spotify session is cleared
    setTimeout(() => {
      if (spotifyLogoutWindow) spotifyLogoutWindow.close();
      window.location.href = REDIRECT_URI;
    }, 2000);
  };

  // Add cleanup for progress interval
  useEffect(() => {
    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  }, []);

  // Format time helper function
  const formatTime = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Add this function after the fetchAllLikedSongs function
  const searchSpotifyTracks = useCallback(async (songs) => {
    const token = localStorage.getItem('token');
    if (!token) return [];

    const enrichedSongs = await Promise.all(songs.map(async (song) => {
      try {
        // Try multiple search strategies to find the track
        let spotifyTrackId = null;
        
        // Strategy 1: Exact search with track and artist
        const exactQuery = `track:${song.title} artist:${song.artist}`;
        const exactResponse = await axios.get('https://api.spotify.com/v1/search', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            q: exactQuery,
            type: 'track',
            limit: 5,
          },
        });

        if (exactResponse.data.tracks.items.length > 0) {
          // Verify artist match (case-insensitive)
          const matchedTrack = exactResponse.data.tracks.items.find(track => 
            track.artists.some(artist => 
              artist.name.toLowerCase().includes(song.artist.toLowerCase()) ||
              song.artist.toLowerCase().includes(artist.name.toLowerCase())
            )
          );
          if (matchedTrack) {
            spotifyTrackId = matchedTrack.id;
          }
        }
        
        // Strategy 2: If not found, try simpler search without prefixes
        if (!spotifyTrackId) {
          const simpleQuery = `${song.title} ${song.artist}`.trim();
          const simpleResponse = await axios.get('https://api.spotify.com/v1/search', {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            params: {
              q: simpleQuery,
              type: 'track',
              limit: 10,
            },
          });

          if (simpleResponse.data.tracks.items.length > 0) {
            // Find best match by checking both title and artist
            const matchedTrack = simpleResponse.data.tracks.items.find(track => {
              const titleMatch = track.name.toLowerCase().includes(song.title.toLowerCase()) ||
                                song.title.toLowerCase().includes(track.name.toLowerCase());
              const artistMatch = track.artists.some(artist => 
                artist.name.toLowerCase().includes(song.artist.toLowerCase()) ||
                song.artist.toLowerCase().includes(artist.name.toLowerCase())
              );
              return titleMatch && artistMatch;
            });
            
            if (matchedTrack) {
              spotifyTrackId = matchedTrack.id;
            } else {
              // Take first result if partial match
              spotifyTrackId = simpleResponse.data.tracks.items[0].id;
              console.warn(`[Spotify Search] Using best guess for "${song.title}" by ${song.artist}`);
            }
          }
        }
        
        // Strategy 3: If still not found, try removing special characters and parentheses
        if (!spotifyTrackId) {
          const cleanTitle = song.title.replace(/\(.*?\)/g, '').replace(/[^\w\s]/g, ' ').trim();
          const cleanQuery = `${cleanTitle} ${song.artist}`;
          const cleanResponse = await axios.get('https://api.spotify.com/v1/search', {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            params: {
              q: cleanQuery,
              type: 'track',
              limit: 5,
            },
          });

          if (cleanResponse.data.tracks.items.length > 0) {
            const matchedTrack = cleanResponse.data.tracks.items.find(track => 
              track.artists.some(artist => 
                artist.name.toLowerCase().includes(song.artist.toLowerCase()) ||
                song.artist.toLowerCase().includes(artist.name.toLowerCase())
              )
            );
            if (matchedTrack) {
              spotifyTrackId = matchedTrack.id;
            }
          }
        }

        if (spotifyTrackId) {
          return {
            ...song,
            spotifyTrackId: spotifyTrackId,
          };
        } else {
          console.warn(`[Spotify Search] Could not find track: "${song.title}" by ${song.artist}`);
          return song;
        }
      } catch (error) {
        console.error(`[Spotify Search] Error searching for track "${song.title}":`, error);
        return song;
      }
    }));

    return enrichedSongs;
  }, []);

  // Function to handle ready playlist
  const isSkipPlaylistRef = useRef(false);

  const handlePlaylistReady = useCallback(async (playlistData) => {
    console.log('[Playlist] ====== PLAYLIST READY HANDLER START ======');
    console.log('[Playlist] Playlist data:', {
      id: playlistData.id,
      scene: playlistData.scene,
      songsCount: playlistData.songs?.length,
      sceneMatches: playlistData.scene_matches,
      action: playlistData.action,
      timestamp: new Date().toISOString()
    });
    
    // Set flag to prevent AutoPlay interference
    isHandlingPlaylistPlayback.current = true;
    console.log('[Playlist] Set isHandlingPlaylistPlayback to true');
    
    // Playlist ready
    
    // Check if there's a currently playing song
    const currentState = player ? await player.getCurrentState() : null;
    console.log('[Playlist] Current player state:', {
      hasState: !!currentState,
      paused: currentState?.paused,
      hasCurrentTrack: !!currentState?.track_window?.current_track,
      currentTrackName: currentState?.track_window?.current_track?.name,
      position: currentState?.position,
      duration: currentState?.duration,
      timeRemaining: currentState ? (currentState.duration - currentState.position) : null
    });
    
    const sdkSaysPlaying = currentState && !currentState.paused && currentState.track_window?.current_track;
    // Fallback: when SDK returns null (transient disconnect/reconnection), use React state
    // and last known valid SDK state to avoid incorrectly taking the IMMEDIATE path
    const hasCurrentlyPlayingSong = sdkSaysPlaying
      || (currentState === null && (isPlaying || (lastValidState.current && !lastValidState.current.paused
          && lastValidState.current.track_window?.current_track)));
    const forceImmediate = isSkipPlaylistRef.current;
    if (forceImmediate) {
      console.log('[Playlist] SKIP MODE: Forcing immediate playlist switch');
      isSkipPlaylistRef.current = false;
    }

    if (hasCurrentlyPlayingSong && !forceImmediate) {
      // Set as pending to be applied when current song ends
      console.log('[Playlist] PENDING MODE: Song currently playing, will apply when it ends');
      console.log('[Playlist] Current pending state before update:', {
        hasPendingUpdate: hasPendingUpdate.current,
        hasPendingSongs: !!pendingSongs,
        hasPendingScene: !!pendingScene
      });
      
      // Search for Spotify track IDs
      const enrichedSongs = await searchSpotifyTracks(playlistData.songs);
      
      // Log missing Spotify IDs for pending playlist
      const songsWithoutIds = enrichedSongs.filter(song => !song.spotifyTrackId);
      if (songsWithoutIds.length > 0) {
        console.warn('[Playlist] Songs without Spotify IDs:', songsWithoutIds.length, songsWithoutIds.map(s => `${s.title} - ${s.artist}`));
      }
      
      setPendingSongs(enrichedSongs);
      setPendingScene({
        scene: playlistData.scene,
        sceneMatches: playlistData.scene_matches || false,
        action: playlistData.action,
        explanation: playlistData.explanation
      });
      // Remember which playlist these queued tracks belong to so
      // applyPendingUpdates can promote it to currentPlaylistId when the
      // new tracks actually start playing. The cleanup below clears
      // pendingPlaylistId, so we mirror it here in a ref.
      queuedPlaylistIdRef.current = playlistData.id;
      // queuedSubVibeRef.current was set upstream from the Lambda response
      // (App.js:3280, 4761, 4895). Don't overwrite from playlistData — the
      // playlists table has no sub_vibe column, so playlistData.sub_vibe is
      // always undefined and would clobber the queued value with null.
      hasPendingUpdate.current = true;
      console.log('[Playlist] PENDING MODE: Set pending state successfully:', {
        pendingSongsCount: enrichedSongs.length,
        pendingScene: playlistData.scene,
        hasPendingUpdate: hasPendingUpdate.current
      });
    } else {
      // No song playing, apply immediately
      console.log('[Playlist] IMMEDIATE MODE: No song playing, applying playlist now');
      console.log('[Playlist] Player state:', {
        playerReady,
        deviceId,
        hasPlayer: !!player
      });
      
      // Search for Spotify track IDs
      const enrichedSongs = await searchSpotifyTracks(playlistData.songs);
      
      // Log missing Spotify IDs
      const songsWithoutIds = enrichedSongs.filter(song => !song.spotifyTrackId);
      if (songsWithoutIds.length > 0) {
        console.warn('[Playlist] Songs without Spotify IDs:', songsWithoutIds.length, songsWithoutIds.map(s => `${s.title} - ${s.artist}`));
      }
      
      setScene({
        scene: playlistData.scene,
        sceneMatches: playlistData.scene_matches || false,
        action: playlistData.action,
        explanation: playlistData.explanation
      });
      
      setRecommendedSongs(enrichedSongs);
      
      // Directly play the songs if player is ready
      if (playerReady && deviceId) {
        console.log('[Playlist] IMMEDIATE MODE: Starting playback process');
        const uris = enrichedSongs
          .map(song => song.spotifyTrackId)
          .filter(Boolean)
          .map(id => id.startsWith('spotify:track:') ? id : `spotify:track:${id}`);
        
        console.log('[Playlist] IMMEDIATE MODE: Prepared URIs:', {
          totalSongs: enrichedSongs.length,
          validUris: uris.length,
          firstUri: uris[0],
          lastUri: uris[uris.length - 1]
        });
        
        const currentToken = localStorage.getItem('token');

        // Small delay to ensure state is set before playing
        console.log('[Playlist] IMMEDIATE MODE: Waiting 100ms before playback...');
        await new Promise(resolve => setTimeout(resolve, 100));

        // Transfer playback to our device first. After a long pause (e.g. our
        // No Music auto-pause + 3-min session rotation) the device can be
        // inactive on Spotify Connect side; the play call below then returns
        // 200 but no audio actually starts. The transfer (play:false) re-flags
        // our device as the active one before we send tracks to it. Same
        // pattern resumeDJControl uses on yield-back. Best-effort — if it
        // fails, the play call below still has a chance via its own 404
        // fallback path.
        try {
          await axios.put(
            'https://api.spotify.com/v1/me/player',
            { device_ids: [deviceId], play: false },
            { headers: { Authorization: `Bearer ${currentToken}` } }
          );
          console.log('[Playlist] IMMEDIATE MODE: Pre-play transfer to device succeeded');
        } catch (transferError) {
          console.warn('[Playlist] IMMEDIATE MODE: Pre-play transfer failed (continuing):',
            transferError?.response?.status, transferError?.message);
        }

        try {
          // Play directly on the device without separate transfer to avoid resuming old context
          console.log('[Playlist] IMMEDIATE MODE: Playing new playlist directly on device:', deviceId);
          await axios.put(
            `https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`,
            { 
              uris,
              position_ms: 0, // Start from beginning
              offset: { position: 0 } // Start from first song
            },
            {
              headers: {
                Authorization: `Bearer ${currentToken}`,
              },
            }
          );
          console.log('[Playlist] IMMEDIATE MODE: SUCCESS - Playback started');
          // The new playlist's first track is now starting on the SDK.
          // Promote it to currentPlaylistId so playback_events get stamped
          // with the correct playlist_id. Update the ref synchronously to
          // avoid the one-render lag on the useEffect ref-syncer.
          currentPlaylistIdRef.current = playlistData.id;
          setCurrentPlaylistId(playlistData.id);
          currentSubVibeRef.current = queuedSubVibeRef.current;
          setIsPlaying(true);
          // Wake-up resume — Connect API queued the new track but on a
          // long-paused SDK (e.g. after No Music + 3 min session rotation)
          // the local state machine can stay paused. Verify; if still paused
          // call player.resume() so the user doesn't have to press Play.
          ensurePlaybackStarted(playerInstance.current || player).then((wokeUp) => {
            if (wokeUp) {
              console.warn('[Playlist] IMMEDIATE MODE: SDK was still paused after play API — woke it via resume()');
            }
          });
        } catch (error) {
          console.error('[Playlist] IMMEDIATE MODE: ERROR during playback:', {
            status: error?.response?.status,
            data: error?.response?.data,
            message: error?.message,
            deviceId,
            urisCount: uris?.length
          });
          
          // Handle 401 Unauthorized errors
          if (error?.response?.status === 401) {
            console.log('[Playlist] Got 401 error, attempting to refresh token and retry');
            const newToken = await refreshAccessToken();
            if (newToken) {
              // Retry with fresh token
              try {
                await axios.put(
                  `https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`,
                  { uris },
                  {
                    headers: {
                      Authorization: `Bearer ${newToken}`,
                    },
                  }
                );
                console.log('[Playlist] Successfully started playing new songs after token refresh');
                currentPlaylistIdRef.current = playlistData.id;
                setCurrentPlaylistId(playlistData.id);
                currentSubVibeRef.current = queuedSubVibeRef.current;
                setIsPlaying(true);
                ensurePlaybackStarted(playerInstance.current || player);
              } catch (retryError) {
                console.error('[Playlist] Error playing songs after token refresh:', retryError?.response?.data || retryError.message || retryError);
              }
            }
          } else if (error?.response?.status === 404) {
            // Device not found, try playing on any active device
            console.log('[Playlist] Device not found, attempting to play on any active device');
            try {
              await axios.put(
                'https://api.spotify.com/v1/me/player/play',
                { uris },
                {
                  headers: {
                    Authorization: `Bearer ${currentToken}`,
                  },
                }
              );
              console.log('[Playlist] Successfully started playing on active device');
              currentPlaylistIdRef.current = playlistData.id;
              setCurrentPlaylistId(playlistData.id);
              currentSubVibeRef.current = queuedSubVibeRef.current;
              setIsPlaying(true);
              ensurePlaybackStarted(playerInstance.current || player);
            } catch (fallbackError) {
              console.error('[Playlist] Fallback play failed:', fallbackError?.response?.data || fallbackError.message || fallbackError);
            }
          }
        }
      }
    }
    
    console.log('[Playlist] Cleaning up playlist generation state');
    setPendingPlaylistId(null);
    setIsGeneratingPlaylist(false);
    
    // Clean up polling interval if it exists
    if (playlistPollingIntervalRef.current) {
      console.log('[Playlist] Stopping polling interval');
      clearInterval(playlistPollingIntervalRef.current);
      playlistPollingIntervalRef.current = null;
      setPlaylistPollingInterval(null);
    }
    
    // Clear the flag after a delay to ensure AutoPlay doesn't interfere
    console.log('[Playlist] Scheduling isHandlingPlaylistPlayback reset in 2 seconds');
    setTimeout(() => {
      console.log('[Playlist] Resetting isHandlingPlaylistPlayback to false');
      isHandlingPlaylistPlayback.current = false;
    }, 2000);
    
    console.log('[Playlist] ====== PLAYLIST READY HANDLER END ======');
  }, [player, searchSpotifyTracks, playerReady, deviceId, refreshAccessToken, setPendingSongs, setPendingScene, setScene, setRecommendedSongs, setIsPlaying, isPlaying]);

  // Function to poll playlist status as backup for subscription
  const pollPlaylistStatus = useCallback(async (playlistId) => {
    if (!supabase) {
      console.error('[Playlist Polling] Supabase not configured');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('playlists')
        .select('*')
        .eq('id', playlistId)
        .maybeSingle();
      
      if (error) {
        console.error('[Playlist Polling] Error checking playlist:', error);
        return;
      }
      
      if (data) {
        console.log('[Playlist Polling] Playlist status:', data.status);
        
        if (data.status === 'ready') {
          // Playlist is ready, process it
          console.log('[Playlist Polling] Playlist ready via polling');
          handlePlaylistReady(data);
          
          // Clean up polling interval
          if (playlistPollingIntervalRef.current) {
            clearInterval(playlistPollingIntervalRef.current);
            playlistPollingIntervalRef.current = null;
            setPlaylistPollingInterval(null);
          }
          
          // Clean up subscription if exists
          if (playlistSubscription) {
            playlistSubscription.unsubscribe();
            setPlaylistSubscription(null);
          playlistSubscriptionRef.current = null;
          }
        } else if (data.status === 'error') {
          console.error('[Playlist Polling] Generation failed:', data.error_message);
          
          // Clean up polling interval
          if (playlistPollingIntervalRef.current) {
            clearInterval(playlistPollingIntervalRef.current);
            playlistPollingIntervalRef.current = null;
            setPlaylistPollingInterval(null);
          }
          
          // Clean up subscription if exists
          if (playlistSubscription) {
            playlistSubscription.unsubscribe();
            setPlaylistSubscription(null);
          playlistSubscriptionRef.current = null;
          }
          
          setPendingPlaylistId(null);
          setIsGeneratingPlaylist(false);
          
          // Show user-friendly error message
          if (data.error_message?.includes('reasoning.effort')) {
            toast.error('Playlist generation temporarily unavailable. Please try again in a moment.', {
              theme: 'colored',
              position: "top-center"
            });
          } else {
            toast.error('Failed to generate playlist. Please try again.', {
              theme: 'colored',
              position: "top-center"
            });
          }
        }
      }
    } catch (error) {
      console.error('[Playlist Polling] Unexpected error:', error);
    }
  }, [handlePlaylistReady, playlistSubscription]);

  // Function to subscribe to playlist updates
  const subscribeToPlaylist = useCallback(async (playlistId) => {
    console.log('[Playlist Subscription] ====== SUBSCRIBE START ======');
    console.log('[Playlist Subscription] Playlist ID:', playlistId);
    
    if (!supabase) {
      console.error('[Playlist Subscription] ERROR: Supabase not configured');
      toast.error('Playlist generation service not configured. Please contact support.', {
        theme: 'colored',
        position: "top-center"
      });
      return;
    }

    // Clean up any existing subscription before creating a new one
    if (playlistSubscription) {
      console.log('[Playlist Subscription] WARNING: Existing subscription found, cleaning up');
      console.log('[Playlist Subscription] Previous subscription ref:', playlistSubscriptionRef.current);
      playlistSubscription.unsubscribe();
      setPlaylistSubscription(null);
    }

    console.log('[Playlist Subscription] Creating new subscription for playlist:', playlistId);
    
    // Test if we can access the playlists table at all
    try {
      const { data: testData, error: testError } = await supabase
        .from('playlists')
        .select('id')
        .limit(1);
      
      console.log('[Playlist] Table access test:', { testData, testError });
      
      if (testError) {
        console.error('[Playlist] Cannot access playlists table:', testError);
        if (testError.message?.includes('row-level security') || testError.code === 'PGRST301') {
          toast.error('Database access error: Row Level Security is blocking access. Please check Supabase RLS policies.', {
            autoClose: 10000
          });
        }
      } else if (testData && testData.length === 0) {
        console.warn('[Playlist] Table is accessible but no rows are visible - RLS policies may be filtering all rows');
        toast.warn('Database configuration issue: RLS policies are preventing playlist access. Please update Supabase policies.', {
          autoClose: false,
          toastId: 'rls-warning'
        });
      }
    } catch (e) {
      console.error('[Playlist] Test query failed:', e);
    }
    
    // First, check if playlist already exists
    const { data, error } = await supabase
      .from('playlists')
      .select('*')
      .eq('id', playlistId)
      .maybeSingle();
    
    console.log('[Playlist] Initial check result:', { data, error, playlistId });
    
    if (error) {
      console.error('[Playlist] Error checking playlist:', error);
      toast.error('Failed to check playlist status');
      return;
    }
    
    if (data) {
      console.log('[Playlist] Found initial playlist data:', {
        id: data.id,
        status: data.status,
        hasSongs: !!data.songs,
        songsCount: data.songs?.length,
        fullData: data
      });
    }
    
    if (data && data.status === 'ready') {
      // Playlist already ready, process immediately
      console.log('[Playlist Subscription] Playlist already READY, processing immediately without subscription');
      handlePlaylistReady(data);
      console.log('[Playlist Subscription] ====== SUBSCRIBE END - IMMEDIATE READY ======');
      return;
    }
    
    if (!data) {
      console.log('[Playlist Subscription] Playlist NOT FOUND yet, will subscribe to updates');
    } else {
      console.log('[Playlist Subscription] Playlist found with status:', data.status, '- subscribing for updates');
    }
    
    // Subscribe to changes
    const subscription = supabase
      .channel(`playlist-${playlistId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'playlists',
          filter: `id=eq.${playlistId}`
        },
        (payload) => {
          console.log('[Playlist Subscription] UPDATE RECEIVED:', {
            playlistId: payload.new.id,
            status: payload.new.status,
            previousStatus: payload.old?.status,
            hasError: !!payload.new.error_message,
            timestamp: new Date().toISOString()
          });
          
          if (payload.new.status === 'ready') {
            console.log('[Playlist Subscription] Status changed to READY, processing playlist');
            handlePlaylistReady(payload.new);
            // Cleanup subscription
            console.log('[Playlist Subscription] Cleaning up subscription after ready status');
            subscription.unsubscribe();
            setPlaylistSubscription(null);
            playlistSubscriptionRef.current = null;
          } else if (payload.new.status === 'error') {
            console.error('[Playlist Subscription] ERROR status received:', payload.new.error_message);
            setPendingPlaylistId(null);
            setIsGeneratingPlaylist(false);
            subscription.unsubscribe();
            setPlaylistSubscription(null);
            playlistSubscriptionRef.current = null;
            
            // Show user-friendly error message
            if (payload.new.error_message?.includes('reasoning.effort')) {
              toast.error('Playlist generation temporarily unavailable. Please try again in a moment.', {
                theme: 'colored',
                position: "top-center"
              });
            } else {
              toast.error('Failed to generate playlist. Please try again.', {
                theme: 'colored',
                position: "top-center"
              });
            }
          }
        }
      )
      .subscribe((status, err) => {
        console.log('[Playlist Subscription] STATUS UPDATE:', {
          status,
          error: err,
          playlistId,
          timestamp: new Date().toISOString()
        });
        
        if (status === 'SUBSCRIBED') {
          console.log('[Playlist Subscription] SUCCESSFULLY SUBSCRIBED to updates for:', playlistId);
        } else if (status === 'CHANNEL_ERROR') {
          console.error('[Playlist Subscription] CHANNEL ERROR:', {
            error: err,
            playlistId
          });
          toast.error('Failed to connect to playlist updates');
          // Reset playlist generation state on error
          setPendingPlaylistId(null);
          setIsGeneratingPlaylist(false);
        } else if (status === 'TIMED_OUT') {
          console.error('[Playlist Subscription] TIMED OUT:', playlistId);
          toast.error('Playlist subscription timed out');
          // Reset playlist generation state on timeout
          setPendingPlaylistId(null);
          setIsGeneratingPlaylist(false);
        } else if (status === 'CLOSED') {
          console.log('[Playlist Subscription] CLOSED:', playlistId);
        }
      });
    
    setPlaylistSubscription(subscription);
    playlistSubscriptionRef.current = subscription;
    
    // Start polling as backup (every 30 seconds)
    console.log('[Playlist Subscription] Starting BACKUP POLLING for playlist:', playlistId);
    
    // Clean up any existing polling interval before creating new one
    if (playlistPollingIntervalRef.current) {
      console.log('[Playlist Subscription] Cleaning up existing polling interval');
      clearInterval(playlistPollingIntervalRef.current);
      playlistPollingIntervalRef.current = null;
    }
    
    const pollingInterval = setInterval(() => {
      // Only poll if we still have a pending playlist and subscription hasn't completed
      if (pendingPlaylistIdRef.current === playlistId && isGeneratingPlaylistRef.current) {
        console.log('[Playlist Polling] EXECUTING backup poll for:', {
          playlistId,
          pendingPlaylistId: pendingPlaylistIdRef.current,
          isGenerating: isGeneratingPlaylistRef.current,
          timestamp: new Date().toISOString()
        });
        pollPlaylistStatus(playlistId);
      } else {
        // Clean up polling if no longer needed
        console.log('[Playlist Polling] STOPPING - Playlist no longer pending:', {
          playlistId,
          currentPendingId: pendingPlaylistIdRef.current,
          isGenerating: isGeneratingPlaylistRef.current
        });
        clearInterval(pollingInterval);
        playlistPollingIntervalRef.current = null;
        setPlaylistPollingInterval(null);
      }
    }, 30000); // Poll every 30 seconds
    
    playlistPollingIntervalRef.current = pollingInterval;
    setPlaylistPollingInterval(pollingInterval);
    
    console.log('[Playlist Subscription] ====== SUBSCRIBE END ======');
  }, [handlePlaylistReady, pollPlaylistStatus]);

  // DJ-135 — When the Lambda's feedback agent swaps the user's scene mapping,
  // it builds a fresh playlist server-side and returns its id. We immediately
  // subscribe and arm the IMMEDIATE-mode flag so the in-flight track is cut
  // the moment the new playlist row turns 'ready' in Supabase.
  const handleFeedbackRegen = useCallback((respData) => {
    const newId = respData?.regeneratedPlaylistId;
    if (!newId) return;
    console.log('[FeedbackRegen] Lambda regenerated playlist:', newId);
    isSkipPlaylistRef.current = true;
    queuedSubVibeRef.current = null;
    skipInitiatedByApp.current = true;
    setPendingPlaylistId(newId);
    // (P1 #7) — tear down the existing playlist subscription before creating
    // a new one. Without this, a continuation pre-fetch that is still in
    // flight can race with the regen subscription: both fire
    // handlePlaylistReady, and pendingUpdateApplied.current races between
    // the two, causing the wrong (old) playlist to apply.
    if (playlistSubscriptionRef.current) {
      try { playlistSubscriptionRef.current.unsubscribe(); } catch (_) {}
      playlistSubscriptionRef.current = null;
    }
    pendingUpdateApplied.current = false; // reset so the new playlist applies
    // Arm isGeneratingPlaylist so the 30s backup poller in subscribeToPlaylist
    // actually fires — Supabase Realtime UPDATE events sometimes don't
    // deliver, and without this guard the poller exits with
    // "Playlist no longer pending" before catching the ready flip.
    setIsGeneratingPlaylist(true);
    subscribeToPlaylist(newId);
    toast.info('Updating your music…', {
      theme: 'colored',
      position: 'top-center',
      autoClose: 2500,
    });
  }, [subscribeToPlaylist]);

  // Ref-bridge so callsites inside long-lived useEffects (e.g. the player
  // state listener) always reach the latest closure without re-subscribing.
  const handleFeedbackRegenRef = useRef(null);
  useEffect(() => { handleFeedbackRegenRef.current = handleFeedbackRegen; }, [handleFeedbackRegen]);

  // Handle file upload for testing
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    console.log('[File Upload] Processing uploaded file:', file.name);

    // Don't process upload if playlist generation is already in progress
    if (isGeneratingPlaylist || pendingPlaylistId) {
      console.log('[File Upload] Playlist generation already in progress, skipping upload');
      toast.warn('Please wait for current playlist generation to complete', {
        theme: 'colored',
        position: "top-center"
      });
      // Reset the input so the same file can be uploaded again later
      event.target.value = '';
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      console.error('[File Upload] Invalid file type:', file.type);
      alert('Please upload an image file');
      return;
    }

    try {
      setIsLoading(true);
      console.log('[File Upload] Getting upload URL');
      const presignedUrlResponse = await axios.post(
        `${API_BASE_URL}/get-upload-url`,
        {
          fileName: file.name,
          fileType: file.type,
        },
        {
          timeout: 30000,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      const { uploadUrl, fileKey } = presignedUrlResponse.data;
      console.log('[File Upload] Got upload URL for key:', fileKey);

      await axios.put(uploadUrl, file, {
        headers: {
          'Content-Type': file.type,
        },
        timeout: 30000
      });
      console.log('[File Upload] Uploaded file to S3');

      const fileUrl = `https://dj-img-app.s3.amazonaws.com/${fileKey}`;
      const userEmail = localStorage.getItem('user_email');

      console.log('[File Upload] Processing image, email:', userEmail);
      console.log('[File Upload] Session ID:', sessionIdRef.current);

      const currentAction = scene.action === undefined ? 'None' : scene.action;
      console.log('[File Upload] Sending action:', currentAction);
      
      // Get current playing state directly from player
      let currentIsPlaying = isPlaying;
      if (player) {
        try {
          const playerState = await player.getCurrentState();
          if (playerState) {
            currentIsPlaying = !playerState.paused;
            console.log('[File Upload] Got player state from SDK - paused:', playerState.paused, 'isPlaying:', currentIsPlaying);
          }
        } catch (error) {
          console.warn('[File Upload] Could not get player state:', error);
        }
      }
      console.log('[File Upload] Player status - isPlaying:', currentIsPlaying);

      try {
        const notifyResponse = await axios.post(
          `${API_BASE_URL}/process-image-to-songs`,
          {
            fileUrl,
            email: userEmail,
            currentAction: currentAction,
            sessionId: sessionIdRef.current,
            isPlaying: currentIsPlaying,
          },
          {
            timeout: 60000,
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        console.log('[File Upload] Got scene response:', notifyResponse.data);
        
        // Check if we're waiting for consecutive threshold
        if (notifyResponse.data.waitingForConsecutive) {
          console.log(`[File Upload] Waiting for consecutive threshold: ${notifyResponse.data.consecutiveCount}/${notifyResponse.data.threshold}`);
          console.log(`[File Upload] Keeping previous action: ${scene.action}, detected action: ${notifyResponse.data.action}`);
        }
        
        // Check if this is an async playlist response
        if (notifyResponse.data.playlistId) {
          // Async playlist generation
          console.log('[File Upload] Received playlist ID:', notifyResponse.data.playlistId);
          setPendingPlaylistId(notifyResponse.data.playlistId);
          setIsGeneratingPlaylist(true);
          subscribeToPlaylist(notifyResponse.data.playlistId);
          
          // Update scene immediately
          setScene(prevScene => ({
            scene: notifyResponse.data.scene || prevScene.scene,
            sceneMatches: false,
            action: notifyResponse.data.action || prevScene.action,
            confidence: prevScene.confidence,
            explanation: prevScene.explanation
          }));
          
          // Playlist generation started
                  } else if (notifyResponse.data.error) {
            // Error response
            console.error('[File Upload] Error from backend:', notifyResponse.data.error);
            toast.error(`Error: ${notifyResponse.data.error}`, {
              theme: 'colored',
              position: "top-center"
            });
          } else if (notifyResponse.data.action === 'No Music') {
            // No Music: pause playback immediately and update scene
            console.log('[File Upload] No Music response - pausing playback immediately');
            if (player) {
              try {
                await player.pause();
                console.log('[File Upload] Playback paused');
              } catch (e) {
                console.warn('[File Upload] Error pausing player:', e);
              }
            }
            setIsPlaying(false);
            setScene({
              scene: notifyResponse.data.scene,
              sceneMatches: false,
              action: 'No Music',
              confidence: 0,
              explanation: notifyResponse.data.explanation
            });
          } else if (notifyResponse.data.songs && notifyResponse.data.songs.length > 0) {
            // Synchronous response (backward compatibility - this path should not be reached anymore)
          const enrichedSongs = await searchSpotifyTracks(notifyResponse.data.songs);
          console.log('[File Upload] Enriched songs with Spotify IDs:', enrichedSongs);

          // Check if there's a currently playing song
          const currentState = player ? await player.getCurrentState() : null;
          const sdkSaysPlaying = currentState && !currentState.paused && currentState.track_window?.current_track;
          const hasCurrentlyPlayingSong = sdkSaysPlaying
            || (currentState === null && (isPlaying || (lastValidState.current && !lastValidState.current.paused
                && lastValidState.current.track_window?.current_track)));

          if (hasCurrentlyPlayingSong) {
            // Set pending updates to be applied 3 seconds before current song ends
            console.log('[File Upload] Setting pending updates for when current song ends');
            setPendingSongs(enrichedSongs);
            setPendingScene(prevScene => ({
              scene: notifyResponse.data.waitingForConsecutive ? prevScene.scene : notifyResponse.data.scene,
              sceneMatches: notifyResponse.data.sceneMatches,
              action: (notifyResponse.data.sceneMatches && notifyResponse.data.action === 'None') || notifyResponse.data.waitingForConsecutive ? prevScene.action : notifyResponse.data.action,
              confidence: notifyResponse.data.confidence,
              explanation: notifyResponse.data.waitingForConsecutive ? prevScene.explanation : (!notifyResponse.data.sceneMatches ? notifyResponse.data.explanation : prevScene.explanation)
            }));
            hasPendingUpdate.current = true;
          } else {
            // No song playing, apply immediately
            console.log('[File Upload] No song currently playing, applying updates immediately');
            
            setRecommendedSongs(enrichedSongs);
            setScene(prevScene => ({
              scene: notifyResponse.data.waitingForConsecutive ? prevScene.scene : notifyResponse.data.scene,
              sceneMatches: notifyResponse.data.sceneMatches,
              action: (notifyResponse.data.sceneMatches && notifyResponse.data.action === 'None') || notifyResponse.data.waitingForConsecutive ? prevScene.action : notifyResponse.data.action,
              confidence: notifyResponse.data.confidence,
              explanation: notifyResponse.data.waitingForConsecutive ? prevScene.explanation : (!notifyResponse.data.sceneMatches ? notifyResponse.data.explanation : prevScene.explanation)
            }));
            
            // Directly play the songs if player is ready
            if (playerReady && deviceId) {
              console.log('[File Upload] Player ready, starting playback immediately');
              const uris = enrichedSongs
                .map(song => song.spotifyTrackId)
                .filter(Boolean)
                .map(id => id.startsWith('spotify:track:') ? id : `spotify:track:${id}`);
              
              const currentToken = localStorage.getItem('token');
              
              axios.put(
                `https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`,
                { uris },
                {
                  headers: {
                    Authorization: `Bearer ${currentToken}`,
                  },
                }
              ).then(() => {
                console.log('[File Upload] Successfully started playing new songs');
                setIsPlaying(true);
              }).catch(async (error) => {
                console.error('[File Upload] Error playing songs:', error?.response?.data || error.message || error);
                
                // Handle 401 Unauthorized errors
                if (error?.response?.status === 401) {
                  console.log('[File Upload] Got 401 error, attempting to refresh token and retry');
                  const newToken = await refreshAccessToken();
                  if (newToken) {
                    // Retry with fresh token
                    try {
                      await axios.put(
                        `https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`,
                        { uris },
                        {
                          headers: {
                            Authorization: `Bearer ${newToken}`,
                          },
                        }
                      );
                      console.log('[File Upload] Successfully started playing new songs after token refresh');
                      setIsPlaying(true);
                    } catch (retryError) {
                      console.error('[File Upload] Error playing songs after token refresh:', retryError?.response?.data || retryError.message || retryError);
                    }
                  }
                }
              });
            }
          }
        } else {
          // No songs in response, just update scene
          console.log('[File Upload] No songs in response. waitingForConsecutive:', notifyResponse.data.waitingForConsecutive);
          console.log('[File Upload] Will update action?', !(notifyResponse.data.sceneMatches && notifyResponse.data.action === 'None') && !notifyResponse.data.waitingForConsecutive);
          console.log('[File Upload] Will keep previous scene?', notifyResponse.data.waitingForConsecutive);
          console.log('[File Upload] Current scene:', scene.scene, 'New scene:', notifyResponse.data.scene);
          setScene(prevScene => ({
            scene: notifyResponse.data.waitingForConsecutive ? prevScene.scene : notifyResponse.data.scene,
            sceneMatches: notifyResponse.data.sceneMatches,
            action: (notifyResponse.data.sceneMatches && notifyResponse.data.action === 'None') || notifyResponse.data.waitingForConsecutive ? prevScene.action : notifyResponse.data.action,
            confidence: notifyResponse.data.confidence,
            explanation: notifyResponse.data.waitingForConsecutive ? prevScene.explanation : (!notifyResponse.data.sceneMatches ? notifyResponse.data.explanation : prevScene.explanation)
          }));
        }
      } catch (error) {
        console.error('[File Upload] Error processing image:', error);
        if (error.code === 'ECONNABORTED') {
          console.error('[File Upload] Request timed out');
        } else if (error.response) {
          console.error('[File Upload] Error response:', error.response.data);
        } else if (error.request) {
          console.error('[File Upload] No response received:', error.request);
        } else {
          console.error('[File Upload] Error:', error.message);
        }
        const { errorType, errorMessage } = classifyClientError(error);
        recordSessionError({
          userEmail: localStorage.getItem('user_email'),
          sessionId: sessionIdRef.current,
          errorType,
          errorMessage,
        });
      }
    } catch (error) {
      console.error('[File Upload] Error in upload process:', error);
      if (error.code === 'ECONNABORTED') {
        console.error('[File Upload] Request timed out');
      } else if (error.response) {
        console.error('[File Upload] Error response:', error.response.data);
      } else if (error.request) {
        console.error('[File Upload] No response received:', error.request);
      } else {
        console.error('[File Upload] Error:', error.message);
      }
    } finally {
      setIsLoading(false);
      // Reset the input so the same file can be uploaded again
      event.target.value = '';
    }
  };

  // Handle test image upload
  const handleTestImageUpload = async (imageName) => {
    try {
      console.log('[Test Image] Loading test image:', imageName);
      
      // Fetch the image from public folder
      const response = await fetch(`/test-images/${imageName}`);
      const blob = await response.blob();
      
      // Convert blob to File object
      const file = new File([blob], imageName, { type: blob.type });
      
      // Create a mock event object similar to what handleFileUpload expects
      const mockEvent = {
        target: {
          files: [file],
          value: '' // Will be reset by handleFileUpload
        }
      };
      
      // Call the existing handleFileUpload function
      await handleFileUpload(mockEvent);
    } catch (error) {
      console.error('[Test Image] Error loading test image:', error);
      toast.error(`Failed to load test image: ${imageName}`, {
        theme: 'colored',
        position: "top-center"
      });
    }
  };

  const handlePlayPause = useCallback(async () => {
    if (!isPlayerReady(player, playerReady, deviceId)) return;
    try {
      // Determine action BEFORE toggling — isPlaying reflects current state
      const action = isPlaying ? 'pause' : 'resume';
      const state = await player.getCurrentState();
      const track = state?.track_window?.current_track;
      const position = state?.position;
      const duration = state?.duration;

      await player.togglePlay();

      const userEmail = localStorage.getItem('user_email');
      if (userEmail && track) {
        console.log(`[Playback Event] ${action}:`, {
          track: `${track.name} - ${track.artists[0]?.name}`,
          position,
          duration
        });
        axios.post(`${API_BASE_URL}/track-playback-event`, {
          email: userEmail,
          songName: track.name,
          artistName: track.artists[0]?.name,
          spotifyTrackId: track.linked_from?.id || track.id,
          action: action,
          position,
          duration,
          sessionId: sessionIdRef.current,
          playlistId: currentPlaylistIdRef.current,
          subVibe: currentSubVibeRef.current ?? queuedSubVibeRef.current ?? null,
          origin: actionOriginRef.current
        }).catch(err => {
          console.error(`[Playback Event] Error sending ${action} data:`, err.message);
        });
      }
    } catch (error) {
      console.error('[Player] Error in handlePlayPause:', error);
    }
  }, [player, playerReady, deviceId, isPlaying, API_BASE_URL]);

  const handlePlayPreviousSong = useCallback(async () => {
    if (!isPlayerReady(player, playerReady, deviceId)) return;

    try {
      // Log skip_backward for both branches
      const currentState = await player.getCurrentState();
      if (currentState && currentState.track_window && currentState.track_window.current_track) {
        const track = currentState.track_window.current_track;
        const userEmail = localStorage.getItem('user_email');
        if (userEmail && track.name && track.artists[0]?.name) {
          axios.post(`${API_BASE_URL}/track-skip`, {
            email: userEmail,
            songName: track.name,
            artistName: track.artists[0].name,
            spotifyTrackId: track.linked_from?.id || track.id,
            action: 'skip_backward',
            position: currentState.position,
            duration: currentState.duration,
            sessionId: sessionIdRef.current,
            playlistId: currentPlaylistIdRef.current,
            subVibe: currentSubVibeRef.current ?? queuedSubVibeRef.current ?? null,
            origin: actionOriginRef.current
          }).catch(err => {
            console.error('[Skip Tracking] Error sending backward skip data:', err.message);
          });
        }
      }

      // First check if we're more than 3 seconds into the song
      if (trackProgress > 3000) {
        // If so, just restart the current song (track doesn't change, no ref flag needed)
        await player.seek(0);
        setTrackProgress(0);
      } else {
        // Otherwise, go to previous song — set flag so state change handler doesn't double-log
        backwardSkipInitiatedByApp.current = true;
        await player.previousTrack();
      }
    } catch (error) {
      console.error('[Player] Error handling previous track:', error);
    }
  }, [player, playerReady, deviceId, trackProgress]);

  const handleDislikeSkip = useCallback(async () => {
    if (!isPlayerReady(player, playerReady, deviceId)) {
      console.warn('[DislikeSkip] Player not ready, skipping.');
      return;
    }
    try {
      console.log('[DislikeSkip] Skipping track after dislike — no skip event logged.');
      skipInitiatedByApp.current = true;
      await player.nextTrack();
    } catch (error) {
      console.error('[DislikeSkip] Error skipping track:', error);
    }
  }, [player, playerReady, deviceId]);

  const handlePlayNextSong = useCallback(async () => {
    console.log('[Skip Tracking] Attempting to play next song.');
    if (!isPlayerReady(player, playerReady, deviceId)) {
      console.warn('[Skip Tracking] Player not ready or no device ID.');
      return;
    }
    
    try {
      console.log('[Skip Tracking] Fetching current player state.');
      const currentState = await player.getCurrentState();
      console.log('[Skip Tracking] Current state from player:', currentState);

      if (currentState && currentState.track_window && currentState.track_window.current_track) {
        const skippedTrack = currentState.track_window.current_track;
        const userEmail = localStorage.getItem('user_email');
        
        console.log('[Skip Tracking] Details of track to be skipped:', {
          name: skippedTrack.name,
          artist: skippedTrack.artists[0]?.name,
          id: skippedTrack.id
        });
        console.log('[Skip Tracking] User email for skip:', userEmail);

        if (!userEmail) {
          console.warn('[Skip Tracking] No user email found in localStorage. Cannot send skip data.');
        } else if (!skippedTrack.name || !skippedTrack.artists[0]?.name) {
          console.warn('[Skip Tracking] Missing track name or artist name. Cannot send skip data.');
        } else {
          try {
            console.log('[Skip Tracking] Sending skip data to API for track:', skippedTrack.name);
            const skipResp = await axios.post(`${API_BASE_URL}/track-skip`, {
              email: userEmail,
              songName: skippedTrack.name,
              artistName: skippedTrack.artists[0].name,
              spotifyTrackId: skippedTrack.linked_from?.id || skippedTrack.id,
              action: 'skip_forward',
              sessionId: sessionIdRef.current,
              playlistId: currentPlaylistIdRef.current,
              subVibe: currentSubVibeRef.current ?? queuedSubVibeRef.current ?? null,
              origin: actionOriginRef.current
            });
            handleFeedbackRegenRef.current?.(skipResp?.data);
            console.log('[Skip Tracking] Successfully sent skip data for track:', skippedTrack.name);
          } catch (apiError) {
            console.error('[Skip Tracking] Error sending skip data to API:', apiError.response ? apiError.response.data : apiError.message, apiError);
          }
        }
      } else {
        console.warn('[Skip Tracking] Could not get current track from player state to record skip. State:', currentState);
      }
      
      console.log('[Skip Tracking] Calling player.nextTrack()');
      skipInitiatedByApp.current = true;
      await player.nextTrack();
      console.log('[Skip Tracking] player.nextTrack() completed.');
    } catch (error) {
      console.error('[Player] Error in handlePlayNextSong or during nextTrack():', error);
    }
  }, [player, playerReady, deviceId]);

  const requestContinuationPlaylist = useCallback(async () => {
    // Guards
    if (djYieldedRef.current) {
      console.log('[Continuation] DJ yielded to another device, skipping');
      return;
    }
    if (isContinuationInProgressRef.current) {
      console.log('[Continuation] Already in progress, skipping');
      return;
    }
    if (isGeneratingPlaylistRef.current) {
      console.log('[Continuation] Playlist generation already in progress, skipping');
      return;
    }
    if (pendingPlaylistIdRef.current) {
      console.log('[Continuation] Pending playlist already exists, skipping');
      return;
    }
    if (!scene.action || scene.action === 'No Music') {
      console.log('[Continuation] No valid scene action, skipping');
      return;
    }
    if (!isMasterEnabledRef.current) {
      console.log('[Continuation] Master toggle disabled, skipping');
      return;
    }

    isContinuationInProgressRef.current = true;
    setIsGeneratingPlaylist(true);

    try {
      const email = localStorage.getItem('user_email');

      // Get actual playing state from player
      let currentIsPlaying = isPlaying;
      if (player) {
        try {
          const playerState = await player.getCurrentState();
          if (playerState) {
            currentIsPlaying = !playerState.paused;
          }
        } catch (error) {
          console.warn('[Continuation] Could not get player state:', error);
        }
      }

      console.log(`[Continuation] Requesting continuation playlist for action: ${scene.action}`);
      const response = await axios.post(
        `${API_BASE_URL}/process-image-to-songs`,
        {
          email,
          currentAction: scene.action,
          sessionId: sessionIdRef.current,
          isPlaying: currentIsPlaying,
          skipImageAnalysis: true,
          sceneDescription: scene.scene,
          actionOverride: scene.action,
        },
        {
          timeout: 90000,
          headers: { 'Content-Type': 'application/json' }
        }
      );

      console.log('[Continuation] Got response:', response.data);

      if (response.data.playlistId) {
        console.log('[Continuation] New playlist ID:', response.data.playlistId);
        setPendingPlaylistId(response.data.playlistId);
        // currentPlaylistId stays on the playing playlist; flips when the new
        // tracks actually start (applyPendingUpdates / IMMEDIATE branch).
        if (response.data.playlistMode) {
          setCurrentPlaylistMode(response.data.playlistMode);
          modesUsedInSessionRef.current.add(response.data.playlistMode);
        }
        queuedSubVibeRef.current = response.data.subVibe ?? null;
        subscribeToPlaylist(response.data.playlistId);
      } else if (response.data.error) {
        console.error('[Continuation] Error from backend:', response.data.error);
        setIsGeneratingPlaylist(false);
      }
    } catch (error) {
      console.error('[Continuation] Error requesting continuation playlist:', error.message);
      const { errorType, errorMessage } = classifyClientError(error);
      recordSessionError({
        userEmail: localStorage.getItem('user_email'),
        sessionId: sessionIdRef.current,
        errorType,
        errorMessage,
      });
      setIsGeneratingPlaylist(false);
    } finally {
      isContinuationInProgressRef.current = false;
    }
  }, [scene.action, scene.scene, isPlaying, player, API_BASE_URL, subscribeToPlaylist]);

  // Keep ref in sync for use inside handlePlayerStateChanged
  requestContinuationPlaylistRef.current = requestContinuationPlaylist;

  // Full session shutdown — same behavior as clicking Shut Down DJ, also
  // invokable via remote voice ("shutdown_dj" tool). Opens the session
  // feedback modal and disables the master toggle; handleMasterToggle then
  // writes the direct_shutdown event + rotates the session ID.
  const handleShutdownDJ = useCallback(() => {
    feedbackSessionIdRef.current = sessionIdRef.current;
    feedbackListeningMinutesRef.current = Math.round((Date.now() - sessionStartTimeRef.current) / 60000);
    // DJ-139: when the shutdown was initiated on dj-remote, the remote
    // opens its own popup — the webapp must not also pop one.
    if (actionOriginRef.current === 'dj_app') {
      setShowSessionFeedback(true);
    }
    setIsMasterEnabled(false);
  }, []);

  // Power the DJ back on from the remote (DJ-131). Mirrors handleActivateDJ
  // without the AudioContext resume — there is no user gesture to attach to.
  // Speaker product activates audio once on first deploy; sessionStorage flag
  // keeps the activation overlay from flashing on subsequent starts. The
  // master-toggle effect handles webcam restart, player init, and session
  // rotation. Dismisses the session feedback modal if it's still open from a
  // shutdown that just happened.
  const handleStartDJ = useCallback(() => {
    sessionStorage.setItem('dj_audio_activated', 'true');
    setShowSessionFeedback(false);
    setIsMasterEnabled(true);
  }, []);

  const handleSkipPlaylist = useCallback(async () => {
    console.log('[New Playlist] User requested to pick a new playlist');

    if (!lastFileUrl) {
      console.warn('[Skip Playlist] No last file URL available');
      toast.warning('Cannot skip playlist - no scene context available', {
        theme: 'colored',
        position: "top-center"
      });
      return;
    }

    if (!currentPlaylistId) {
      console.warn('[Skip Playlist] No current playlist to skip');
      toast.warning('No active playlist to skip', {
        theme: 'colored',
        position: "top-center"
      });
      return;
    }

    console.log(`[Skip Playlist] Skipping playlist ID: ${currentPlaylistId}`);

    // (P1 #6) — capture session ID BEFORE rotating so every reference in
    // this handler (skip POST + process-image-to-songs POST + error path)
    // attributes to the correct (old) session. Declared OUTSIDE the try
    // so the catch block can also use it (const is block-scoped).
    const capturedSessionId = sessionIdRef.current;

    try {
      const userEmail = localStorage.getItem('user_email');

      // 1. Track the skip (fire-and-forget, don't block the skip flow)
      console.log('[Skip Playlist] Recording skip...');
      axios.post(`${API_BASE_URL}/playlist-skip`, {
        email: userEmail,
        playlistId: currentPlaylistId,
        playlistName: scene.action || 'Unknown', // Using action as playlist name proxy
        action: scene.action || 'unknown',
        sessionId: capturedSessionId,
        origin: actionOriginRef.current
      }).then((response) => {
        console.log('[Skip Playlist] Skip recorded successfully');
        handleFeedbackRegenRef.current?.(response?.data);
      }).catch((err) => {
        console.warn('[Skip Playlist] Failed to record skip (non-critical):', err.message);
      });

      // Rotate session ID — playlist skip ends the current session
      rotateSessionId();
      console.log('[Session] Rotated session ID after playlist skip:', sessionIdRef.current);

      // 2. Add to skipped list (keep last 5 only)
      const updatedSkipped = [...skippedPlaylistIds, currentPlaylistId].slice(-5);
      setSkippedPlaylistIds(updatedSkipped);
      console.log(`[Skip Playlist] Updated skipped list (${updatedSkipped.length} total):`, updatedSkipped);

      // 3. Show loading state
      toast.info('Getting a fresh playlist...', {
        theme: 'colored',
        position: "top-center",
        autoClose: 2000
      });
      setIsGeneratingPlaylist(true);

      // 4. Get current playing state
      let currentIsPlaying = isPlaying;
      if (player) {
        try {
          const playerState = await player.getCurrentState();
          if (playerState) {
            currentIsPlaying = !playerState.paused;
          }
        } catch (error) {
          console.warn('[Skip Playlist] Could not get player state:', error);
        }
      }

      // 5. Re-request playlist with excluded IDs (skip image analysis since the S3 image is already deleted)
      console.log('[Skip Playlist] Requesting new playlist with exclusions:', updatedSkipped);
      const response = await axios.post(
        `${API_BASE_URL}/process-image-to-songs`,
        {
          email: userEmail,
          currentAction: scene.action,
          // (P1 #6) — use capturedSessionId (pre-rotation) so the new-playlist
          // request is attributed to the session that triggered the skip, not
          // the freshly-rotated next session.
          sessionId: capturedSessionId,
          isPlaying: currentIsPlaying,
          excludePlaylistIds: updatedSkipped,
          skipImageAnalysis: true,
          sceneDescription: scene.scene,
          actionOverride: scene.action,
        },
        {
          timeout: 90000,
          headers: { 'Content-Type': 'application/json' }
        }
      );

      console.log('[Skip Playlist] Got response:', response.data);

      // 6. Handle the new playlist
      if (response.data.playlistId) {
        console.log('[Skip Playlist] New playlist ID:', response.data.playlistId);
        isSkipPlaylistRef.current = true;
        skipInitiatedByApp.current = true; // Prevent duplicate skip event when new playlist's first track triggers state change
        setPendingPlaylistId(response.data.playlistId);
        // currentPlaylistId stays on the playing playlist; flips when the
        // new tracks actually start (handlePlaylistReady IMMEDIATE branch
        // fires under forceImmediate for skip).
        if (response.data.playlistMode) {
          setCurrentPlaylistMode(response.data.playlistMode);
          modesUsedInSessionRef.current.add(response.data.playlistMode);
        }
        queuedSubVibeRef.current = response.data.subVibe ?? null;
        subscribeToPlaylist(response.data.playlistId);

        toast.success('Switched to fresh playlist!', {
          theme: 'colored',
          position: "top-center"
        });
      } else if (response.data.error) {
        console.error('[Skip Playlist] Error:', response.data.error);
        toast.error(`Error: ${response.data.error}`, {
          theme: 'colored',
          position: "top-center"
        });
        setIsGeneratingPlaylist(false);
      }

    } catch (error) {
      console.error('[Skip Playlist] Error skipping playlist:', error);
      toast.error('Failed to skip playlist', {
        theme: 'colored',
        position: "top-center"
      });
      const { errorType, errorMessage } = classifyClientError(error);
      recordSessionError({
        userEmail: localStorage.getItem('user_email'),
        // (P1 #6) — use capturedSessionId here too; if the error occurred
        // after rotateSessionId() fired, sessionIdRef.current is already the
        // new session and would mis-attribute the error.
        sessionId: capturedSessionId,
        errorType,
        errorMessage,
      });
      setIsGeneratingPlaylist(false);
    }
  }, [lastFileUrl, currentPlaylistId, skippedPlaylistIds, scene.action, scene.scene, isPlaying, player, API_BASE_URL, sessionIdRef]);

  const submitTrackFeedbackFromRemote = useCallback(async ({ feedbackType, reasons }) => {
    if (!currentlyPlaying) return;
    const songName = currentlyPlaying.title || currentlyPlaying.name;
    const artist = currentlyPlaying.artist || currentlyPlaying.artists?.[0]?.name;
    if (!songName || !userEmail) return;
    const resp = await axios.post(`${API_BASE_URL}/submit-track-feedback`, {
      email: userEmail,
      sessionId: sessionIdRef.current,
      feedbackType,
      songName,
      artist,
      playlistId: currentPlaylistId,
      subVibe: currentSubVibeRef.current ?? queuedSubVibeRef.current ?? null,
      reasons: Array.isArray(reasons) ? reasons : [reasons],
      position: trackProgress ?? null,
      duration: trackDuration ?? null,
      action: scene.action || '',
      spotifyTrackId: currentlyPlaying.spotifyTrackId || '',
      origin: actionOriginRef.current,
    });
    handleFeedbackRegen(resp?.data);
  }, [API_BASE_URL, currentlyPlaying, userEmail, currentPlaylistId, trackProgress, trackDuration, scene.action, handleFeedbackRegen]);

  const handleRemoteAction = useCallback(async (toolCall, heard, sessionInfo) => {
    console.log('[voice] heard:', heard, 'tool:', toolCall?.name, 'args:', toolCall?.args);
    setLastRemoteAction({
      tool: toolCall?.name,
      heard,
      reason: toolCall?.args?.reason || null,
      ts: Date.now(),
    });
    if (lastRemoteActionClearRef.current) clearTimeout(lastRemoteActionClearRef.current);
    lastRemoteActionClearRef.current = setTimeout(() => setLastRemoteAction(null), 6000);
    const handlers = {
      handlePlayPause,
      handlePlayNextSong,
      handlePlayPreviousSong,
      handleVolumeChange,
      handleMuteToggle,
      handleSkipPlaylist,
      handleDislikeSkip,
      handleShutdownDJ,
      handleStartDJ,
      submitTrackFeedback: submitTrackFeedbackFromRemote,
      showToast: (msg, type = 'info') => toast[type]?.(msg, { autoClose: 2500 }),
    };
    const state = {
      isPlaying,
      isMuted,
      volume,
      originalVolume: sessionInfo?.originalVolume ?? null,
    };
    return dispatchAction(toolCall, handlers, state);
  }, [handlePlayPause, handlePlayNextSong, handlePlayPreviousSong, handleVolumeChange, handleMuteToggle, handleSkipPlaylist, handleDislikeSkip, handleShutdownDJ, handleStartDJ, submitTrackFeedbackFromRemote, isPlaying, isMuted, volume]);

  // Ref-bridge so the remote-channel subscription can always reach the newest
  // handleRemoteAction closure without tearing down / re-subscribing on every
  // state change.
  const remoteActionHandlerRef = useRef(null);
  useEffect(() => { remoteActionHandlerRef.current = handleRemoteAction; }, [handleRemoteAction]);

  // Heartbeat so dj-remote's pairing query (which looks at the most recent
  // playback_events row in a freshness window) stays valid across long tracks.
  // Without this, a 6-minute track emits no events between 'playing' rows and
  // the remote silently falls back to "No DJ paired" mid-song.
  //
  // Writes are direct Supabase inserts from the client (no Lambda hop needed).
  // Action = 'heartbeat'; the remote treats it as "still playing".
  useEffect(() => {
    if (!isSupabaseConfigured() || !userEmail || !isPlaying) return undefined;

    const writeHeartbeat = async () => {
      try {
        const p = playerInstance.current;
        const state = p ? await p.getCurrentState().catch(() => null) : null;
        const track = state?.track_window?.current_track ?? null;
        // Pick the largest available album image from Spotify (sizes are
        // usually 640/300/64). First entry is the largest in the SDK.
        const albumArtUrl = track?.album?.images?.[0]?.url ?? null;
        const canonicalTrackId = track ? (track.linked_from?.id || track.id) : null;
        await supabase.from('playback_events').insert({
          user_email: userEmail,
          timestamp: new Date().toISOString(),
          action: 'heartbeat',
          playback_session_id: sessionIdRef.current,
          song_name: track?.name ?? null,
          artist: track?.artists?.[0]?.name ?? null,
          spotify_track_id: canonicalTrackId,
          album_art_url: albumArtUrl,
          playlist_id: currentPlaylistIdRef.current,
          sub_vibe: currentSubVibeRef.current,
          origin: 'dj_app',
        });
      } catch (err) {
        // Heartbeats are best-effort; log and keep running so a transient
        // network blip doesn't kill pairing freshness forever.
        console.warn('[heartbeat] failed:', err?.message || err);
      }
    };

    const id = setInterval(writeHeartbeat, 60_000);
    // Also fire one immediately so remote sees paired status on first render
    // after a brief lull (e.g. mid-song reload) without waiting 60 seconds.
    // Depending on currentlyPlaying?.spotifyTrackId means this also fires
    // on every track change — otherwise the Lambda's 'playing'/'skip_forward'
    // rows (which carry no album_art_url) would sit as the latest event for
    // up to 60s, blanking the remote's cover art.
    writeHeartbeat();
    return () => clearInterval(id);
  }, [isPlaying, userEmail, sessionEpoch, currentlyPlaying?.spotifyTrackId]);

  // Companion remote: join the Supabase Broadcast channel for this session
  // and forward incoming action payloads to the action dispatcher. The phone
  // handles all voice capture on its side (POSTs audio to /voice-command
  // itself); by the time a message lands here it already carries
  // {name, args, heard}.
  useEffect(() => {
    if (!isSupabaseConfigured() || !userEmail) return undefined;
    const sessionId = sessionIdRef.current;
    if (!sessionId) return undefined;

    // Treat this useEffect run as its own epoch so a stale reconnect loop
    // (from a prior session rotation) can't interfere with a newer one.
    let cancelled = false;
    let reconnectTimer = null;
    let activeChannel = null;

    // Each environment now uses its own Supabase project (separate Realtime
    // tenant), so the `dj:session:{id}` channel name is sufficient — the dev
    // remote and prod remote can't reach each other across project boundaries.
    const channelName = `dj:session:${sessionId}`;

    const subscribe = () => {
      if (cancelled) return;
      const channel = supabase.channel(channelName, {
        config: { broadcast: { self: false } },
      });
      activeChannel = channel;
      channel.on('broadcast', { event: 'action' }, async ({ payload }) => {
        if (!payload || !payload.name) return;
        const toolCall = { name: payload.name, args: payload.args || {} };
        const heard = payload.heard || '(remote)';
        // Stamp all writes triggered by this remote action with the remote's
        // origin. Revert after 1.5s — comfortably longer than the longest
        // synchronous write chain (dispatch → SDK state change → POST).
        const incomingOrigin = payload.origin === 'remote_voice' || payload.origin === 'remote_button'
          ? payload.origin
          : 'dj_app';
        actionOriginRef.current = incomingOrigin;
        try {
          const fn = remoteActionHandlerRef.current;
          if (fn) await fn(toolCall, heard, { originalVolume: volumeStateRef.current?.current ?? null });
        } catch (err) {
          console.warn('[remote] action dispatch failed:', err);
        } finally {
          setTimeout(() => { actionOriginRef.current = 'dj_app'; }, 1500);
        }
      });
      channel.subscribe((status) => {
        if (cancelled) return;
        if (status === 'SUBSCRIBED') {
          console.log('[remote] listening on ' + channelName);
          return;
        }
        // Anything that isn't SUBSCRIBED means the channel isn't reliably
        // delivering broadcasts. Log, tear down, and retry — otherwise a
        // silent CLOSED/TIMED_OUT state leaves the DJ deaf to the remote.
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          console.warn('[remote] channel status:', status, '→ reconnecting in 1.5s');
          try { supabase.removeChannel(channel); } catch { /* noop */ }
          if (activeChannel === channel) activeChannel = null;
          if (!reconnectTimer) {
            reconnectTimer = setTimeout(() => {
              reconnectTimer = null;
              subscribe();
            }, 1500);
          }
        }
      });
    };

    subscribe();

    return () => {
      cancelled = true;
      if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
      if (activeChannel) {
        try { supabase.removeChannel(activeChannel); } catch { /* noop */ }
        activeChannel = null;
      }
    };
    // Re-subscribe when session rotates (shutdown / 3-min no-music /
    // master toggle / playlist skip). Keyed on sessionEpoch which bumps on
    // every rotation via rotateSessionId().
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail, sessionEpoch]);

  // User-scoped broadcast channel (DJ-131). Survives session_id rotation so
  // the remote can send "wake" actions (start_dj) after a shutdown without
  // racing against the freshly-rotated sessionId. Only handles actions that
  // are valid regardless of session state (start_dj). Other actions still go
  // through the session channel above.
  useEffect(() => {
    if (!isSupabaseConfigured() || !userEmail) return undefined;

    let cancelled = false;
    let reconnectTimer = null;
    let activeChannel = null;
    const channelName = `dj:user:${userEmail}`;

    const subscribe = () => {
      if (cancelled) return;
      const channel = supabase.channel(channelName, {
        config: { broadcast: { self: false } },
      });
      activeChannel = channel;
      channel.on('broadcast', { event: 'action' }, async ({ payload }) => {
        if (!payload || !payload.name) return;
        const toolCall = { name: payload.name, args: payload.args || {} };
        const heard = payload.heard || '(remote)';
        const incomingOrigin = payload.origin === 'remote_voice' || payload.origin === 'remote_button'
          ? payload.origin
          : 'dj_app';
        actionOriginRef.current = incomingOrigin;
        try {
          const fn = remoteActionHandlerRef.current;
          if (fn) await fn(toolCall, heard, { originalVolume: volumeStateRef.current?.current ?? null });
        } catch (err) {
          console.warn('[remote-user] action dispatch failed:', err);
        } finally {
          setTimeout(() => { actionOriginRef.current = 'dj_app'; }, 1500);
        }
      });
      // DJ-139: the remote owns the post-session feedback popup, but the
      // DJ webapp owns the locally-tracked context (effectiveMode,
      // modesUsed, listening time) — so the remote broadcasts the user's
      // answers and we wrap them in the exact same POST our own modal
      // would have submitted. Sent on the user-scoped channel because the
      // session channel is gone by the time Submit fires (shutdown
      // rotates session_id).
      channel.on('broadcast', { event: 'session_feedback_submit' }, async ({ payload }) => {
        if (!payload || !payload.sessionId) return;
        try {
          const res = await axios.post(`${API_BASE_URL}/submit-session-feedback`, {
            email: userEmail,
            sessionId: payload.sessionId,
            wowMoment: payload.wowMoment,
            fightDj: payload.fightDj,
            vibeMatch: payload.vibeMatch,
            effectiveMode: currentPlaylistModeRef.current,
            additionalText: payload.additionalText ?? null,
            listeningTimeMinutes: feedbackListeningMinutesRef.current || 0,
            modesUsed: Array.from(modesUsedInSessionRef.current),
          });
          if (res?.data?.regeneratedPlaylistId) handleFeedbackRegenRef.current?.(res.data);
        } catch (err) {
          console.warn('[remote-user] session feedback submit failed:', err.response?.status, err.response?.data || err.message);
        }
      });
      channel.subscribe((status) => {
        if (cancelled) return;
        if (status === 'SUBSCRIBED') {
          console.log('[remote-user] listening on ' + channelName);
          return;
        }
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          console.warn('[remote-user] channel status:', status, '→ reconnecting in 1.5s');
          try { supabase.removeChannel(channel); } catch { /* noop */ }
          if (activeChannel === channel) activeChannel = null;
          if (!reconnectTimer) {
            reconnectTimer = setTimeout(() => {
              reconnectTimer = null;
              subscribe();
            }, 1500);
          }
        }
      });
    };

    subscribe();

    return () => {
      cancelled = true;
      if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
      if (activeChannel) {
        try { supabase.removeChannel(activeChannel); } catch { /* noop */ }
        activeChannel = null;
      }
    };
  }, [userEmail]);

  // Broadcast preparing state to dj-remote so it can show the same loader
  useEffect(() => {
    const sessionId = sessionIdRef.current;
    if (!sessionId || !userEmail) return;
    const channelName = `dj:session:${sessionId}`;
    const ch = supabase.channel(channelName, { config: { broadcast: { self: false } } });
    ch.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        ch.send({
          type: 'broadcast',
          event: 'dj_state',
          payload: { state: isPreparingDj ? 'preparing' : 'ready', stage: preparingStage },
        }).catch(() => {/* noop */});
      }
    });
    return () => {
      try { supabase.removeChannel(ch); } catch { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreparingDj, preparingStage]);

  const manualRefreshToken = async () => {
    console.log('[Manual Refresh] Starting manual token refresh simulation');
    
    const refreshTokenValue = localStorage.getItem('refresh_token');
    if (!refreshTokenValue) {
      console.error('[Manual Refresh] No refresh token available');
      toast.error('No refresh token available for simulation', { theme: 'colored', position: "top-center" });
      return;
    }

    try {
      console.log('[Manual Refresh] Making refresh token API call');
      const authHeader = `Basic ${btoa(`${CLIENT_ID}:${CLIENT_SECRET}`)}`;
      const data = new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: refreshTokenValue,
      }).toString();

      const response = await axios({
        method: 'post',
        url: TOKEN_ENDPOINT,
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        data: data,
      });

      const { access_token, expires_in } = response.data;
      console.log('[Manual Refresh] Successfully received new access token');
      
      // Store the new token and expiry
      localStorage.setItem('token', access_token);
      const expiryTime = Date.now() + (expires_in * 1000);
      localStorage.setItem('token_expiry', expiryTime.toString());
      
      // Update the token ref but NOT the state to avoid cascading effects
      tokenRef.current = access_token;
      
      console.log('[Manual Refresh] Token refresh simulation completed successfully');
      toast.success('Token refreshed successfully! Player should continue normally.', { 
        theme: 'colored', 
        position: "top-center",
        autoClose: 3000
      });
      
    } catch (error) {
      console.error('[Manual Refresh] Error during manual token refresh:', error);
      toast.error('Failed to refresh token: ' + (error.response?.data?.error_description || error.message), { 
        theme: 'colored', 
        position: "top-center" 
      });
      
      // If refresh fails, might need to re-authenticate
      if (error.response?.status === 400) {
        console.log('[Manual Refresh] Refresh token may be invalid, clearing tokens');
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('token_expiry');
        tokenRef.current = null;
        setToken(null);
      }
    }
  };

  // eslint-disable-next-line no-unused-vars
  const getRecommendedSongs = async () => {
    // Function logic...
  };

  useEffect(() => {
    const checkPermissions = async () => {
      // Check for EME support
      console.log('[Permissions] Checking for EME support...');
      if (navigator.requestMediaKeySystemAccess) {
        try {
          await navigator.requestMediaKeySystemAccess('com.widevine.alpha', [{
            videoCapabilities: [{ contentType: 'video/mp4; codecs="avc1.42E01E"' }],
          }]);
          console.log('[Permissions] EME support confirmed.');
        } catch (e) {
          console.error('EME check failed:', e);
          if (e.name === 'NotSupportedError' || e.message.includes('No supported keysystem')) {
             console.warn('[Permissions] EME is not supported or disabled.');
             toast.warn('Encrypted media is not supported or is disabled in your browser. Spotify playback may not work.', { autoClose: 10000, theme: 'colored', position: "top-center" });
          }
        }
      } else {
          console.warn('[Permissions] Encrypted Media Extensions (EME) are not supported in this browser.');
          toast.warn('Encrypted Media Extensions (EME) are not supported in this browser.', { autoClose: 10000, theme: 'colored', position: "top-center" });
      }
  
      // Check for autoplay
      console.log('[Permissions] Checking for autoplay support...');
      try {
        const video = document.createElement('video');
        video.muted = true;
        video.playsInline = true;
        video.src = "";
        await video.play();
        console.log('[Permissions] Autoplay support confirmed.');
      } catch (err) {
        if (err.name === 'NotAllowedError') {
          console.warn('[Permissions] Autoplay is disabled by the browser.');
          toast.warn('Autoplay seems to be disabled. You may need to interact with the page to start playback.', { autoClose: 10000, theme: 'colored', position: "top-center" });
        }
      }
    };
  
    checkPermissions();
  }, []);

  return (
    <div className={`App ${isMasterEnabled ? 'enabled' : 'disabled'}`}>
      <ToastContainer />
      <header className="App-header">
        {!credentialsResolved ? (
          <EmailGate
            userEmail={userEmail}
            onEmailChange={(val) => { setUserEmail(val); setUserNotRegistered(false); }}
            onSubmit={(email) => resolveSpotifyCredentials(email)}
            loading={credentialsLoading}
            notRegistered={userNotRegistered}
          />
        ) : !token ? (
          <a
            className="spotify-connect-button"
            href={getAuthUrl()}
          >
            <i className="fab fa-spotify"></i>
            Connect Spotify
          </a>
        ) : (
          <>
            {userEmail && (
              <div
                className="connected-account-badge"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  alignSelf: 'center',
                  padding: '4px 10px',
                  margin: '0 auto 8px',
                  borderRadius: 999,
                  background: 'rgba(255, 255, 255, 0.08)',
                  color: 'rgba(255, 255, 255, 0.75)',
                  fontSize: 12,
                  fontWeight: 500,
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: '#1db954',
                    display: 'inline-block',
                  }}
                />
                {userEmail}
              </div>
            )}
            {isMasterEnabled && lastRemoteAction?.tool && (
              <div
                className="remote-action-badge"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  alignSelf: 'center',
                  padding: '4px 10px',
                  margin: '0 auto 8px',
                  borderRadius: 999,
                  background: 'rgba(10, 175, 115, 0.18)',
                  color: '#34d399',
                  fontSize: 12,
                  fontWeight: 500,
                  border: '1px solid rgba(10, 175, 115, 0.35)',
                }}
              >
                <span style={{ fontSize: 10, opacity: 0.7 }}>REMOTE</span>
                <span>{labelForAction(lastRemoteAction.tool)}</span>
                {lastRemoteAction.reason && (
                  <span style={{ opacity: 0.85 }}>— {lastRemoteAction.reason}</span>
                )}
              </div>
            )}
            {isMasterEnabled && (
              <div className="music-player-card">

                {/* ===== SECTION 1: Status / Loading ===== */}
                <div className={`player-section section-status ${isGeneratingPlaylist || isPreparingDj || !scene.scene ? 'is-loading' : ''}`}>
                  {isGeneratingPlaylist || isPreparingDj || !scene.scene ? (
                    <>
                      <div className="status-equalizer">
                        <span></span><span></span><span></span><span></span><span></span>
                      </div>
                      <p className="status-text loading-text">
                        {isPreparingDj
                          ? (preparingStage === 'analyzing_taste'
                              ? "Getting to know your music taste…"
                              : preparingStage === 'matching_scenes'
                              ? "Picking the perfect vibes for you…"
                              : preparingStage === 'fetching_likes'
                              ? "Loading your Spotify library…"
                              : "Setting up your DJ…")
                          : isGeneratingPlaylist
                          ? "Generating your personalized playlist..."
                          : "Finding the perfect rhythm for your moment"}
                      </p>
                      <div className="status-dots">
                        <span className="dot"></span>
                        <span className="dot"></span>
                        <span className="dot"></span>
                      </div>
                    </>
                  ) : (
                    <p className="status-text">
                      {scene.explanation || "Listening to your vibe"}
                    </p>
                  )}
                </div>

                <div className="player-divider"></div>

                {/* ===== SECTION 2: Now Playing (art + track info) ===== */}
                <div className="player-section section-now-playing">
                  {currentlyPlaying && scene.action !== "No Music" ? (
                    <div className="now-playing-row">
                      <div className="album-art-container">
                        {currentlyPlaying.albumArt ? (
                          <img
                            className="album-art"
                            src={currentlyPlaying.albumArt}
                            alt={currentlyPlaying.albumName || currentlyPlaying.title}
                          />
                        ) : (
                          <div className="album-art-placeholder">
                            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5">
                              <path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="track-details">
                        <div className="track-field">
                          <span className="track-label">Song</span>
                          <span className="track-value track-value-name">{currentlyPlaying.title}</span>
                        </div>
                        <div className="track-field">
                          <span className="track-label">Artist</span>
                          <span className="track-value">{currentlyPlaying.artist}</span>
                        </div>
                        {currentlyPlaying.albumName && (
                          <div className="track-field">
                            <span className="track-label">Album</span>
                            <span className="track-value track-value-album">{currentlyPlaying.albumName}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="now-playing-row now-playing-empty">
                      <div className="album-art-container">
                        <div className={`album-art-placeholder ${isGeneratingPlaylist || isPreparingDj || !scene.scene ? 'generating' : ''}`}>
                          {isGeneratingPlaylist || isPreparingDj || !scene.scene ? (
                            <div className="generating-indicator">
                              <div className="generating-rings">
                                <div className="ring ring-1"></div>
                                <div className="ring ring-2"></div>
                                <div className="ring ring-3"></div>
                              </div>
                              <svg className="generating-icon" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" />
                              </svg>
                            </div>
                          ) : (
                            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5">
                              <path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" />
                            </svg>
                          )}
                        </div>
                      </div>
                      <div className="track-details">
                        <p className="track-name empty">{isPreparingDj ? "Preparing your DJ\u2026" : isGeneratingPlaylist ? "Picking tracks..." : "No track yet"}</p>
                        <p className="track-artist empty">{isPreparingDj ? "One moment" : isGeneratingPlaylist ? "Almost there" : "\u2014"}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* ===== SECTION 3: Controls ===== */}
                <div className="player-section section-controls">
                  <div className="player-controls">
                    <button
                      className="control-button"
                      onClick={handlePlayPreviousSong}
                      disabled={!playerReady || !isMasterEnabled || djYielded}
                      title="Previous"
                    >
                      <SkipBack size={20} />
                    </button>

                    <button
                      className="control-button play-pause"
                      onClick={handlePlayPause}
                      disabled={!playerReady || !isMasterEnabled || djYielded}
                      title={isPlaying ? "Pause" : "Play"}
                    >
                      {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                    </button>

                    <button
                      className="control-button"
                      onClick={handlePlayNextSong}
                      disabled={!playerReady || !isMasterEnabled || djYielded}
                      title="Next"
                    >
                      <SkipForward size={20} />
                    </button>
                  </div>

                  <div className="track-progress">
                    <span className="time">{formatTime(trackProgress)}</span>
                    <div className="progress-slider-container">
                      <div
                        className="progress-slider-fill"
                        style={{ width: `${(trackProgress / trackDuration) * 100}%` }}
                      ></div>
                      <input
                        type="range"
                        min={0}
                        max={trackDuration}
                        value={trackProgress}
                        onChange={(e) => handleSeek(Number(e.target.value))}
                        disabled={!playerReady || trackDuration === 0 || !isMasterEnabled}
                      />
                    </div>
                    <span className="time">{formatTime(trackDuration)}</span>
                  </div>

                  <div className="player-divider"></div>

                  <div className="track-feedback-section">
                    <TrackFeedback
                      currentlyPlaying={currentlyPlaying}
                      sessionId={sessionIdRef.current}
                      playlistId={currentPlaylistId}
                      subVibe={currentSubVibeRef.current}
                      isPlaying={isPlaying}
                      apiBaseUrl={API_BASE_URL}
                      trackProgress={trackProgress}
                      trackDuration={trackDuration}
                      onDislike={handleDislikeSkip}
                      onRegenerated={handleFeedbackRegen}
                      action={scene.action}
                    >
                      <button
                        className="skip-playlist-button"
                        onClick={handleSkipPlaylist}
                        disabled={!playerReady || !isMasterEnabled || isGeneratingPlaylist || !currentPlaylistId || !lastFileUrl}
                        title="Skip to a different playlist for this activity"
                      >
                        <Shuffle size={20} />
                        <span>New Playlist</span>
                      </button>
                    </TrackFeedback>
                  </div>

                  <div className={`volume-control${isMuted ? ' muted' : ''}`}>
                    <button className="volume-mute-btn" onClick={handleMuteToggle} title={isMuted ? "Unmute" : "Mute"}>
                      {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                    </button>
                    <div className="volume-slider-container">
                      <div
                        className="volume-slider-fill"
                        style={{ width: `${volume * 100}%` }}
                      ></div>
                      <input
                        type="range"
                        min={0}
                        max={1}
                        step={0.01}
                        value={volume}
                        onChange={handleVolumeChange}
                        disabled={!playerReady || !isMasterEnabled}
                      />
                    </div>
                    <span className="volume-percentage">{isMuted ? '0%' : `${Math.round(volume * 100)}%`}</span>
                  </div>
                </div>

                <button
                  className="stop-dj-button"
                  onClick={handleShutdownDJ}
                  title="Shut Down DJ"
                >
                  <Power size={14} /> Shut Down DJ
                </button>
              </div>
            )}

            {/* Activation Overlay — shown on web browsers before first activation */}
            {!isMasterEnabled && sessionStorage.getItem('dj_audio_activated') !== 'true' && (
              <div className="activation-overlay">
                <div className="activation-content">
                  <div className="activation-icon">
                    <Power size={48} />
                  </div>
                  <h2>Activate DJ</h2>
                  <p>Allow audio playback to start your DJ session</p>
                  <button className="activation-button" onClick={handleActivateDJ}>
                    Start
                  </button>
                </div>
              </div>
            )}

            {/* Master Toggle Button - show when disabled but already activated (user turned it off manually) */}
            {!isMasterEnabled && sessionStorage.getItem('dj_audio_activated') === 'true' && (
              <div className="master-toggle-container">
                <button
                  className="master-toggle-button"
                  onClick={() => setIsMasterEnabled(true)}
                  title="Enable DJ Mode"
                >
                  <Power size={24} />
                </button>
              </div>
            )}

            {/* DJ Yielded Overlay — shown when another device took over Spotify playback */}
            {djYielded && isMasterEnabled && (
              <div className="dj-yielded-overlay">
                <div className="dj-yielded-content">
                  <div className="dj-yielded-icon">
                    <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="5" y="2" width="14" height="20" rx="2" />
                      <line x1="12" y1="18" x2="12" y2="18.01" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </div>
                  <h2>Music is playing on another device</h2>
                  <p>The DJ has paused while you listen elsewhere</p>
                  <button className="dj-yielded-resume-button" onClick={resumeDJControl}>
                    Resume DJ
                  </button>
                </div>
              </div>
            )}

            <button className="sign-out-button" onClick={logout}>
              <LogOut size={14} style={{ marginRight: '0px' }} /> Sign out
            </button>

            <div className="video-container">
              <video ref={videoRef} autoPlay muted playsInline></video>
              <canvas ref={canvasRef}></canvas>
            </div>
            <div className="debug-buttons">
              {/* File upload for testing */}
              <input
                type="file"
                id="fileUpload"
                accept="image/*"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                disabled={isLoading || isGeneratingPlaylist}
              />
              <label
                htmlFor="fileUpload"
                className={`file-upload-label ${(isLoading || isGeneratingPlaylist) ? 'disabled' : ''}`}
              >
                <i className="fas fa-upload" style={{ marginRight: '8px' }}></i>
                {isGeneratingPlaylist ? 'Generating Playlist...' : (isLoading ? 'Processing...' : 'Upload Image')}
              </label>
              <button
                onClick={manualRefreshToken}
                className={`file-upload-label ${isLoading ? 'disabled' : ''}`}
                style={{ marginTop: '10px' }}
                disabled={isLoading}
              >
                <i className="fas fa-sync-alt" style={{ marginRight: '8px' }}></i>
                Simulate Refresh Token
              </button>
            </div>
            
            {/* Test Image Buttons */}
            <div className="test-image-buttons" style={{ marginTop: '20px' }}>
              <div style={{ fontSize: '14px', color: '#aaa', marginBottom: '10px', gridColumn: '1 / -1', textAlign: 'center' }}>Test Images:</div>
              <button
                onClick={() => handleTestImageUpload('work.jpeg')}
                className={`test-image-btn ${(isLoading || isGeneratingPlaylist) ? 'disabled' : ''}`}
                disabled={isLoading || isGeneratingPlaylist}
              >
                Work
              </button>
              <button
                onClick={() => handleTestImageUpload('social_event.jpg')}
                className={`test-image-btn ${(isLoading || isGeneratingPlaylist) ? 'disabled' : ''}`}
                disabled={isLoading || isGeneratingPlaylist}
              >
                Social Event
              </button>
              <button
                onClick={() => handleTestImageUpload('Hanging.jpg')}
                className={`test-image-btn ${(isLoading || isGeneratingPlaylist) ? 'disabled' : ''}`}
                disabled={isLoading || isGeneratingPlaylist}
              >
                Hanging
              </button>
              <button
                onClick={() => handleTestImageUpload('cooking.jpg')}
                className={`test-image-btn ${(isLoading || isGeneratingPlaylist) ? 'disabled' : ''}`}
                disabled={isLoading || isGeneratingPlaylist}
              >
                Cooking
              </button>
              <button
                onClick={() => handleTestImageUpload('chicken.jpeg')}
                className={`test-image-btn ${(isLoading || isGeneratingPlaylist) ? 'disabled' : ''}`}
                disabled={isLoading || isGeneratingPlaylist}
              >
                Chicken
              </button>
            </div>
          </>
        )}
      </header>

      {/* Session Feedback Survey */}
      {showSessionFeedback && (
        <SessionFeedback
          sessionId={feedbackSessionIdRef.current}
          apiBaseUrl={API_BASE_URL}
          listeningTimeMinutes={feedbackListeningMinutesRef.current}
          effectiveMode={currentPlaylistMode}
          modesUsed={Array.from(modesUsedInSessionRef.current)}
          onClose={() => setShowSessionFeedback(false)}
          onRegenerated={handleFeedbackRegen}
        />
      )}
    </div>
  );
}

export default App;

